import { useState, useRef, useEffect, useCallback, useMemo, Component } from "react";
import {
  supabase,
  signInWithEmail,
  signUpWithEmail,
  signInWithGoogle,
  signOut,
  getAuthToken,
} from "./supabaseClient";
import { QRCodeSVG } from "qrcode.react";
import ConsoomePreOnboardingMotion from "./consoome_pre_onboarding_motion";
import logoCream from "./assets/logo-cream.png";
import {
  REFINEMENT_POOL as TILE_POOL,
  REFINEMENT_SECTIONS,
  buildAdaptiveDeck,
  selectTilesForRefine,
  mergeEffects,
  getSectionConfidence,
  getSectionDepthInfo,
  loadAnsweredTileIds,
  markTilesAnswered,
  LEVEL2_SLIDERS,
} from "./refinementTiles";
// logoDark (./assets/logo-dark.png) available for light backgrounds

// ─────────────────────────────────────────────────────────────
// FONT + GLOBAL STYLE INJECTION
// ─────────────────────────────────────────────────────────────
const injectFont = () => {
  if (document.getElementById("consoome-font")) return;
  const l = document.createElement("link");
  l.id = "consoome-font";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,300;1,400&display=swap";
  document.head.appendChild(l);
  const s = document.createElement("style");
  s.id = "consoome-styles";
  s.textContent = `
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html, body { overflow-x: hidden; width: 100%; max-width: 100vw; -webkit-overflow-scrolling: touch; }
    body { background: #1a1a1a; font-family: 'Montserrat', sans-serif; -webkit-font-smoothing: antialiased; padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left); }
    input[type=range] { -webkit-appearance: none; appearance: none; width: 100%; height: 4px; border-radius: 2px; outline: none; cursor: pointer; }
    input[type=range]::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 24px; height: 24px; border-radius: 50%; background: #1A1A1A; cursor: pointer; border: 3px solid #C8A951; box-shadow: 0 2px 8px rgba(0,0,0,0.18); }
    input[type=range]::-moz-range-thumb { width: 24px; height: 24px; border-radius: 50%; background: #1A1A1A; cursor: pointer; border: 3px solid #C8A951; box-shadow: 0 2px 8px rgba(0,0,0,0.18); }
    @keyframes fadeUp { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
    @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
    @keyframes slideIn { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }
    @keyframes slideInLeft { from { opacity:0; transform:translateX(-20px); } to { opacity:1; transform:translateX(0); } }
    @keyframes scaleUp { from { opacity:0; transform:scale(0.96); } to { opacity:1; transform:scale(1); } }
    @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.6; } }
    @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
    @keyframes confetti { 0% { opacity:1; transform:translateY(0) rotate(0deg); } 100% { opacity:0; transform:translateY(-80px) rotate(360deg); } }
    @keyframes ringFill { from { stroke-dasharray: 0 999; } }
    @keyframes countUp { from { opacity:0; transform:scale(0.8); } to { opacity:1; transform:scale(1); } }
    @keyframes gentleBounce { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-3px); } }
    @keyframes scanPulse { 0%,100% { transform:scale(1); } 50% { transform:scale(1.05); } }
    @keyframes pulseDot { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:0.5; transform:scale(0.85); } }
    .ctx-card:active { transform: scale(0.97) !important; }
    .anim-fadeup { animation: fadeUp 0.4s cubic-bezier(0.22,1,0.36,1) both; }
    .anim-fadein { animation: fadeIn 0.3s ease both; }
    .anim-slidein { animation: slideIn 0.35s cubic-bezier(0.22,1,0.36,1) both; }
    .anim-slidein-left { animation: slideInLeft 0.35s cubic-bezier(0.22,1,0.36,1) both; }
    .anim-scaleup { animation: scaleUp 0.3s cubic-bezier(0.22,1,0.36,1) both; }
    ::-webkit-scrollbar { width: 3px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); border-radius: 2px; }
    .consoome-root { margin: 0 auto; }
    @media (min-width: 481px) {
      .consoome-root { max-width: 430px; box-shadow: 0 0 80px rgba(0,0,0,0.15); }
    }
    *:focus-visible { outline: 2px solid #C8A951; outline-offset: 2px; }
    .skip-link { position: absolute; left: -9999px; top: auto; width: 1px; height: 1px; overflow: hidden; z-index: 9999; }
    .skip-link:focus { position: fixed; top: 8px; left: 8px; width: auto; height: auto; padding: 12px 24px; background: #C8A951; color: #1A1A1A; font-weight: 700; font-size: 14px; border-radius: 8px; text-decoration: none; z-index: 9999; }
    .sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0; }
  `;
  document.head.appendChild(s);
};
injectFont();

// ─────────────────────────────────────────────────────────────
// DESIGN TOKENS — from UX Research Design Spec Section 2.2
// ─────────────────────────────────────────────────────────────
const T = {
  // Light mode
  bgPrimary:   "#FFF9EC",
  bgSecondary: "#F5F0E3",
  textPrimary: "#1A1A1A",
  textSecondary: "#6B6B6B",
  gold:        "#C8A951",
  goldDark:    "#A68A3E",
  goldSoft:    "#FBF3E0",
  safetyRed:   "#D94F4F",
  safetyAmber: "#E8A838",
  safetyGreen: "#4A9B6E",
  matchGold:   "#C8A951",
  matchNeutral:"#E8DFC8",
  matchGray:   "#B8B3A6",
  border:      "#E8E2D4",
  overlay:     "rgba(26,26,26,0.5)",
  // Dark mode tokens
  darkBg:      "#1A1A1A",
  darkCard:    "#2A2A26",
  darkBorder:  "#3A3A34",
  darkText:    "#FFF9EC",
  darkTextSec: "#A0998A",
  darkGold:    "#D4B968",
  // Semantic
  cream:       "#FFF9EC",
  ink:         "#1A1A1A",
  white:       "#FFFFFF",
  // Preserved from original for compatibility
  slate:       "#6B6259",
  mist:        "#B8AFA4",
  fog:         "#E8E1D8",
  red:         "#D94F4F",
  redSoft:     "#FDECEA",
  green:       "#4A9B6E",
  greenSoft:   "#E5F5EC",
  teal:        "#1A6B5A",
  tealSoft:    "#E5F3EF",
  sapphire:    "#1A4A7A",
  sapphSoft:   "#E5EEF7",
};

// Typography scale from spec
const TYPE = {
  display:  { fontSize: 28, fontWeight: 700, lineHeight: "34px", letterSpacing: "0.5px" },
  h1:       { fontSize: 22, fontWeight: 600, lineHeight: "28px", letterSpacing: "0.25px" },
  h2:       { fontSize: 18, fontWeight: 600, lineHeight: "24px", letterSpacing: "0.15px" },
  body:     { fontSize: 15, fontWeight: 400, lineHeight: "22px", letterSpacing: "0.1px" },
  bodySmall:{ fontSize: 13, fontWeight: 400, lineHeight: "18px", letterSpacing: "0.1px" },
  caption:  { fontSize: 11, fontWeight: 500, lineHeight: "16px", letterSpacing: "0.5px" },
  button:   { fontSize: 15, fontWeight: 600, lineHeight: "20px", letterSpacing: "0.75px" },
  overline: { fontSize: 10, fontWeight: 500, lineHeight: "14px", letterSpacing: "1.5px" },
  tabLabel: { fontSize: 10, fontWeight: 500, lineHeight: "12px", letterSpacing: "0.5px" },
};

// Spacing
const SP = { xs: 4, sm: 8, md: 16, lg: 24, xl: 32, xxl: 48 };

// Radii
const RAD = { sm: 8, md: 12, lg: 16, xl: 24, pill: 999 };

// ─────────────────────────────────────────────────────────────
// STATIC DATA
// ─────────────────────────────────────────────────────────────
const ALLERGENS = [
  { key: "none",         label: "None",      icon: "✓" },
  { key: "has_nuts",     label: "Tree Nuts",  icon: "🥜" },
  { key: "has_shellfish",label: "Shellfish",  icon: "🦐" },
  { key: "has_gluten",   label: "Gluten",     icon: "🌾" },
  { key: "has_dairy",    label: "Dairy",      icon: "🥛" },
  { key: "has_eggs",     label: "Eggs",       icon: "🥚" },
  { key: "has_soy",      label: "Soy",        icon: "🫘" },
  { key: "has_sesame",   label: "Sesame",     icon: "⬤" },
  { key: "has_fish",     label: "Fish",       icon: "🐟" },
  { key: "has_sulphites",label: "Sulphites",  icon: "🍾" },
];

const DIETARY = [
  { key: "is_halal",        label: "Halal",        icon: "☪️" },
  { key: "no_pork",         label: "No Pork",      icon: "🐷" },
  { key: "no_beef",         label: "No Beef",      icon: "🐄" },
  { key: "is_pescetarian",  label: "Pescetarian",  icon: "🐟" },
  { key: "is_vegan",        label: "Vegan",        icon: "🌱" },
  { key: "is_vegetarian",   label: "Vegetarian",   icon: "🥦" },
  { key: "is_kosher",       label: "Kosher",       icon: "✡️" },
  { key: "is_gluten_free",  label: "Gluten-Free",  icon: "🚫" },
  { key: "is_dairy_free",   label: "Dairy-Free",   icon: "🥗" },
];

const DISH_GRID = [
  { label: "Wagyu Steak",      icon: "🥩", cuisine: "Steakhouse", protein: "Beef",    richness: 5, spice: 0, health: 1, price: "prestige", umami: 5, img: "https://images.unsplash.com/photo-1558030006-450675393462?w=400&h=400&fit=crop" },
  { label: "Sushi Omakase",    icon: "🍣", cuisine: "Japanese",   protein: "Seafood", richness: 3, spice: 1, health: 4, price: "premium",  umami: 5, img: "https://images.unsplash.com/photo-1579871494447-9811cf80d66c?w=400&h=400&fit=crop" },
  { label: "Caesar Salad",     icon: "🥗", cuisine: "American",   protein: "Dairy",   richness: 2, spice: 0, health: 4, price: "mid",      umami: 2, img: "https://images.unsplash.com/photo-1550304943-4f24f54ddde9?w=400&h=400&fit=crop" },
  { label: "Lamb Curry",       icon: "🍛", cuisine: "Indian",     protein: "Lamb",    richness: 3, spice: 4, health: 2, price: "mid",      umami: 3, img: "https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=400&h=400&fit=crop" },
  { label: "Pasta Carbonara",  icon: "🍝", cuisine: "Italian",    protein: "Pork",    richness: 4, spice: 0, health: 2, price: "mid",      umami: 4, img: "https://images.unsplash.com/photo-1612874742237-6526221588e3?w=400&h=400&fit=crop" },
  { label: "Grilled Sea Bass", icon: "🐟", cuisine: "French",     protein: "Seafood", richness: 2, spice: 0, health: 5, price: "premium",  umami: 3, img: "https://images.unsplash.com/photo-1519708227418-c8fd9a32b7a2?w=400&h=400&fit=crop" },
  { label: "BBQ Ribs",         icon: "🍖", cuisine: "American",   protein: "Pork",    richness: 4, spice: 2, health: 1, price: "mid",      umami: 4, img: "https://images.unsplash.com/photo-1544025162-d76694265947?w=400&h=400&fit=crop" },
  { label: "Truffle Risotto",  icon: "🍄", cuisine: "Italian",    protein: "Plant",   richness: 4, spice: 0, health: 3, price: "premium",  umami: 5, img: "https://images.unsplash.com/photo-1476124369491-e7addf5db371?w=400&h=400&fit=crop" },
  { label: "Pad Thai",         icon: "🍜", cuisine: "Thai",       protein: "Seafood", richness: 3, spice: 3, health: 3, price: "budget",   umami: 3, img: "https://images.unsplash.com/photo-1559314809-0d155014e29e?w=400&h=400&fit=crop" },
  { label: "Smash Burger",     icon: "🍔", cuisine: "American",   protein: "Beef",    richness: 4, spice: 1, health: 2, price: "mid",      umami: 4, img: "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=400&h=400&fit=crop" },
  { label: "Mezze Platter",    icon: "🫙", cuisine: "Lebanese",   protein: "Plant",   richness: 2, spice: 1, health: 4, price: "mid",      umami: 2, img: "https://images.unsplash.com/photo-1541518763669-27fef04b14ea?w=400&h=400&fit=crop" },
  { label: "Cheese Board",     icon: "🧀", cuisine: "French",     protein: "Dairy",   richness: 4, spice: 0, health: 2, price: "premium",  umami: 3, img: "https://images.unsplash.com/photo-1452195100486-9cc805987862?w=400&h=400&fit=crop" },
];

const NEVER_EAT = ["Liver", "Tripe", "Raw Fish", "Cilantro", "Blue Cheese", "Truffle", "Oysters", "Foie Gras", "Offal", "Anchovies", "Durian", "Kidneys"];

// ─── BILINGUAL SAFETY CARD (EN/AR) ───────────────────────────
const AR_FONT = "'Noto Naskh Arabic', 'Montserrat', sans-serif";
const SAFETY_AR = {
  'Tree Nuts': 'مكسرات', 'Shellfish': 'محار', 'Gluten': 'غلوتين',
  'Dairy': 'ألبان', 'Eggs': 'بيض', 'Soy': 'صويا',
  'Sesame': 'سمسم', 'Fish': 'سمك', 'Sulphites': 'كبريتات',
  'Halal': 'حلال', 'Vegan': 'نباتي', 'Vegetarian': 'نباتي (مع بيض وألبان)',
  'Kosher': 'كوشر', 'Gluten-Free': 'خالي من الغلوتين', 'Dairy-Free': 'خالي من الألبان',
};

const DRINK_OPTIONS = [
  { value: "Wine",          icon: "🍷", label: "Wine" },
  { value: "Cocktails",     icon: "🍸", label: "Cocktails" },
  { value: "Beer",          icon: "🍺", label: "Beer" },
  { value: "Non-alcoholic", icon: "🧃", label: "Non-Alcoholic" },
  { value: "Water",         icon: "💧", label: "Water Only" },
  { value: "Mood",          icon: "🌊", label: "It Depends" },
];

const WINE_STYLES = ["Red", "White", "Rosé", "Sparkling", "No Preference"];

const OCCASIONS = [
  { key: "business",   label: "Business Lunch",   icon: "💼" },
  { key: "romantic",   label: "Romantic Dinner",   icon: "🌹" },
  { key: "family",     label: "Family Meal",       icon: "👨‍👩‍👧" },
  { key: "solo",       label: "Solo Dining",       icon: "🧘" },
  { key: "travelling", label: "Travelling",        icon: "✈️" },
  { key: "celebrating",label: "Celebrating",       icon: "🥂" },
];

const SPICE_LABELS = ["Mild", "Medium", "Spicy", "Very Hot", "Bring It On"];
const BODY_LABELS  = ["Very Light", "Light", "Medium-Light", "Medium", "Medium-Full", "Full-Bodied"];

// ─── Swipe cards for O7 (adaptive gap-filler) ────────────────
const SWIPE_CARDS = [
  { name: "Bone Marrow", img: "🦴", cat: "protein" },
  { name: "Fermented Foods", img: "🫙", cat: "taste" },
  { name: "Rare Steak", img: "🥩", cat: "cook" },
  { name: "Spicy Tuna", img: "🌶", cat: "spice" },
  { name: "Foie Gras", img: "🦆", cat: "luxury" },
  { name: "Matcha Dessert", img: "🍵", cat: "sweet" },
  { name: "Raw Oysters", img: "🦪", cat: "texture" },
  { name: "Smoked Meats", img: "🔥", cat: "cook" },
];

// ─── Confidence Engine (Food ID scoring) ─────────────────────
// dim = dimension group used for Step 6 diversity guarantee
const CONFIDENCE_ATTRS = {
  // Taste — spice asked directly; richness/umami weakly inferred from dish picks
  spice_tolerance:    { weight: 15, category: "taste",     dim: "spice" },
  richness_pref:      { weight: 14, category: "taste",     dim: "taste_depth" },
  sweetness_pref:     { weight: 11, category: "taste",     dim: "taste_depth" },
  umami_pref:         { weight: 14, category: "taste",     dim: "taste_depth" },
  acidity_pref:       { weight: 10, category: "taste",     dim: "taste_depth" },
  bitterness_pref:    { weight: 8,  category: "taste",     dim: "taste_depth" },
  // Texture — completely unasked in Steps 1–5; highest-priority gap
  preferred_textures: { weight: 15, category: "texture",   dim: "texture" },
  accepts_raw:        { weight: 13, category: "texture",   dim: "texture" },
  // Cuisine — partially inferred from dish picks
  loves_asian:        { weight: 12, category: "cuisine",   dim: "cuisine" },
  loves_european:     { weight: 12, category: "cuisine",   dim: "cuisine" },
  loves_american:     { weight: 10, category: "cuisine",   dim: "cuisine" },
  loves_indian:       { weight: 12, category: "cuisine",   dim: "cuisine" },
  // Protein — partially inferred from dish picks
  likes_beef:         { weight: 10, category: "protein",   dim: "protein" },
  likes_seafood:      { weight: 12, category: "protein",   dim: "protein" },
  likes_chicken:      { weight: 10, category: "protein",   dim: "protein" },
  likes_lamb:         { weight: 8,  category: "protein",   dim: "protein" },
  likes_plant:        { weight: 10, category: "protein",   dim: "protein" },
  // Cooking method — completely unasked in Steps 1–5
  preferred_cooking:  { weight: 12, category: "cooking",   dim: "cooking" },
  // Context — novelty/indulgence/portion never asked; occasions weakly covers adventurousness
  adventurousness:    { weight: 13, category: "context",   dim: "novelty" },
  novelty_pref:       { weight: 12, category: "context",   dim: "novelty" },
  indulgence_default: { weight: 14, category: "context",   dim: "indulgence" },
  health_conscious:   { weight: 10, category: "context",   dim: "indulgence" },
  portion_pref:       { weight: 8,  category: "context",   dim: "dining" },
  // Beverage — partially set in Step 4.5; deepened via refinement
  wine_style_pref:    { weight: 12, category: "beverage",  dim: "beverage" },
  cocktail_style_pref:{ weight: 10, category: "beverage",  dim: "beverage" },
  na_bev_pref:        { weight: 8,  category: "beverage",  dim: "beverage" },
};

function computeConfidence(formData) {
  const scores = {};
  Object.keys(CONFIDENCE_ATTRS).forEach(k => { scores[k] = 0; });

  // Safety layer → high confidence (handled separately, always 0.95)
  // Spice from slider
  if (formData.spice !== undefined) scores.spice_tolerance = 0.85;
  // Dishes → infer cuisine/protein
  if (formData.dishes && formData.dishes.length > 0) {
    formData.dishes.forEach(idx => {
      const d = DISH_GRID[idx];
      if (!d) return;
      const c = d.cuisine;
      if (["Japanese", "Thai"].includes(c)) scores.loves_asian = Math.max(scores.loves_asian, 0.7);
      if (["French", "Italian"].includes(c)) scores.loves_european = Math.max(scores.loves_european, 0.7);
      if (c === "American") scores.loves_american = Math.max(scores.loves_american, 0.7);
      if (c === "Indian") scores.loves_indian = Math.max(scores.loves_indian, 0.7);
      if (c === "Lebanese") scores.loves_asian = Math.max(scores.loves_asian, 0.5);
      const p = (d.protein || "").toLowerCase();
      if (p === "beef") scores.likes_beef = Math.max(scores.likes_beef, 0.7);
      if (p === "seafood") scores.likes_seafood = Math.max(scores.likes_seafood, 0.7);
      if (p === "lamb") scores.likes_lamb = Math.max(scores.likes_lamb, 0.7);
      if (p === "plant") scores.likes_plant = Math.max(scores.likes_plant, 0.7);
      // dairy no longer infers plant preference (removed: dairy→plant weak boost)
      if (p === "pork") scores.likes_beef = Math.max(scores.likes_beef, 0.5);
      // Infer taste from dishes
      scores.richness_pref = Math.max(scores.richness_pref, d.richness > 3 ? 0.65 : 0.4);
      scores.umami_pref = Math.max(scores.umami_pref, d.umami > 3 ? 0.65 : 0.4);
    });
  }
  if (formData.occasions?.includes("travelling")) scores.adventurousness = Math.max(scores.adventurousness, 0.6);
  if (formData.occasions?.includes("celebrating")) scores.adventurousness = Math.max(scores.adventurousness, 0.5);

  // Beverage — seed from drink choice (Step 4.5)
  const dc = formData.drinkChoice;
  if (dc === "Wine") { scores.wine_style_pref = Math.max(scores.wine_style_pref, 0.4); }
  if (dc === "Cocktails") { scores.cocktail_style_pref = Math.max(scores.cocktail_style_pref, 0.4); }
  if (dc === "Beer") { scores.na_bev_pref = Math.max(scores.na_bev_pref, 0.4); }
  if (dc === "Non-alcoholic" || dc === "Water") { scores.na_bev_pref = Math.max(scores.na_bev_pref, 0.5); }
  if (formData.wineStyles?.length > 0) { scores.wine_style_pref = Math.max(scores.wine_style_pref, 0.5); }
  if (formData.wineBody !== undefined) { scores.wine_style_pref = Math.max(scores.wine_style_pref, 0.55); }

  // Compute completeness
  const totalW = Object.values(CONFIDENCE_ATTRS).reduce((s, a) => s + a.weight, 0);
  const scoredW = Object.entries(scores).reduce((s, [k, v]) => s + (v > 0 ? CONFIDENCE_ATTRS[k].weight : 0), 0);
  // Safety + dietary always count as ~30% of total
  const safetyBonus = (Object.keys(formData.allergens || {}).length > 0 || Object.keys(formData.dietary || {}).length > 0) ? 30 : 0;
  const completeness = Math.min(100, Math.round((scoredW / totalW) * 70 + safetyBonus));
  return { scores, completeness };
}

function identifyGaps(scores) {
  return Object.entries(scores)
    .filter(([, v]) => v < 0.6)
    .map(([k, v]) => ({ key: k, value: v, weight: CONFIDENCE_ATTRS[k]?.weight || 10, category: CONFIDENCE_ATTRS[k]?.category }))
    .sort((a, b) => b.weight - a.weight);
}

// ─── Adaptive Refinement Card Pool ────────────────────────────
// Imported from refinementTiles.js — shared between Step 6 and Refine Profile.
// TILE_POOL (aliased as REFINEMENT_POOL) contains 96 tiles across 8 categories, 3 depth tiers.
const REFINEMENT_POOL = TILE_POOL;

// buildAdaptiveDeck, mergeEffects, REFINEMENT_SECTIONS, getSectionConfidence
// are now imported from refinementTiles.js

// ─── Match tiers — calibrated to actual scoring range ─────────
// Real venue scores range ~65-78% with full profile. Tiers create visual
// differentiation within this realistic range.
const getMatchTier = (score) => {
  if (score >= 85) return { label: "Best Match", color: T.gold, bg: `${T.gold}25`, glow: true };
  if (score >= 65) return { label: "Good Fit", color: T.cream, bg: `${T.cream}12` };
  return { label: "Try Something New", color: T.darkTextSec, bg: `${T.cream}08` };
};

// ─── Structural template used only by handleComplete to build the onboarding POST payload ──
const _ONBOARDING_TEMPLATE = {
  handle: "consoome",
  display_name: "Guest",
  avatar: null,
  completeness_score: 72,
  safety: {
    has_nuts: false, has_shellfish: true, has_gluten: false,
    has_dairy: false, has_eggs: false, has_soy: false,
    has_sesame: false, has_fish: false, has_sulphites: false,
    is_gluten_free: false, is_dairy_free: false,
    is_vegan: false, is_vegetarian: false, is_halal: false, is_kosher: false,
    no_pork: false, no_beef: false, is_pescetarian: false,
    never_eat_list: ["Tripe", "Liver"],
    medical_notes: "",
  },
  taste: {
    richness_pref: 4, spice_tolerance: 2, sweetness_pref: 2,
    acidity_pref: 3, umami_pref: 5, bitterness_pref: 2,
    preferred_textures: ["Crispy", "Tender", "Creamy"],
    avoided_textures: ["Gelatinous", "Mushy"],
    accepts_raw: true, portion_pref: "standard",
    indulgence_default: 4, health_default: 2,
    flavor_vector: [4, 2, 2, 3, 5, 2],
  },
  cuisine: {
    cuisine_affinities: { Japanese: 5, Italian: 4, Steakhouse: 5, French: 3, Thai: 2 },
    avoided_cuisines: [],
    protein_ranking: ["Beef", "Seafood", "Lamb", "Poultry"],
    avoided_proteins: [],
    loved_ingredients: ["Wagyu", "Truffle", "Burrata", "Aged Parmesan", "Black Truffle"],
    disliked_ingredients: ["Cilantro", "Blue Cheese"],
    novelty_pref: 3,
    carb_pref: "pasta",
    preferred_cooking: ["Grilled", "Smoked", "Braised"],
  },
  beverage: {
    drinks_alcohol: true,
    preferred_bev_category: ["Wine", "Cocktails"],
    wine_style_pref: ["Red", "Sparkling"],
    wine_body_pref: 4,
    wine_tannin_pref: 4,
    wine_acidity_pref: 3,
    wine_sweetness_pref: 1,
    wine_oak_pref: 3,
    wine_region_pref: ["Burgundy", "Napa Valley", "Stellenbosch"],
    wine_budget_tier: "premium",
    sulphite_sensitive: false,
    cocktail_style_pref: ["Classic", "Bitter"],
    spirit_pref: ["Whisky", "Gin"],
    na_bev_pref: [],
  },
  context: {
    default_occasions: ["romantic", "celebrating"],
    travel_mode: true,
    group_size_pref: "couple",
    budget_signal: "premium",
    preferred_meal_period: "dinner",
  },
};

// ─── Venue data — SAFETY GATE applied ─────────────────────────
// Per spec 1.4: unsafe dishes are REMOVED, not shown with warning
const VENUES = [
  {
    id: "seafire",
    name: "Seafire Steakhouse & Bar",
    hotel: "Atlantis The Palm",
    cuisine: "Contemporary Steakhouse",
    outletType: "Fine Dining",
    icon: "🥩",
    matchScore: 96,
    greatMatches: 4,
    reasons: ["Wagyu & aged beef cuts match your top proteins", "Full-bodied South African reds on the list"],
    description: "Live-fire grills, USDA Prime and Australian Wagyu, an award-winning wine cellar spanning 800 labels.",
    priceRange: "AED 250–900",
    dishes: [
      { name: "Australian Wagyu Tenderloin MB9+", desc: "300g dry-aged, truffle café de paris butter, bone marrow jus, hand-cut fries", price: "AED 680", match: 98, safe: true, whyLine: "Matches your love of Wagyu and umami flavors", whyTags: ["Top protein: Wagyu", "Loved ingredient: Truffle", "Rich & umami 5/5", "Pairs with your bold red"], icon: "🥩" },
      { name: "Kobe A5 Beef Striploin", desc: "200g certified Kobe, wasabi cream, pickled daikon, dashi soy", price: "AED 890", match: 95, safe: true, whyLine: "Japanese cuisine affinity + premium beef", whyTags: ["Japanese affinity 5/5", "Top protein: Beef", "Umami maximum", "Prestige tier match"], icon: "🐄" },
      { name: "USDA Prime Dry-Aged Ribeye", desc: "45-day dry-aged 400g, café de paris butter, crispy onion, truffle jus", price: "AED 420", match: 91, safe: true, whyLine: "Rich, umami-forward — your flavor profile", whyTags: ["Bold & rich", "Loved: Truffle", "Crispy texture match", "Premium budget"], icon: "🔥" },
      { name: "Burrata & Heirloom Tomato", desc: "Buffalo burrata, aged 25yr balsamic, wild basil oil, Maldon salt", price: "AED 145", match: 87, safe: true, whyLine: "Loved ingredient: Burrata + Italian affinity", whyTags: ["Loved ingredient: Burrata", "Italian affinity 4/5", "Creamy texture match"], icon: "🫧" },
      // Grilled Tiger Prawns REMOVED by safety gate (shellfish allergy)
    ],
    wine: { name: "Meerlust Rubicon 2018", region: "Stellenbosch, SA", match: 96 },
  },
  {
    id: "nobu",
    name: "Nobu Atlantis",
    hotel: "Atlantis The Palm",
    cuisine: "Japanese · Peruvian Fusion",
    outletType: "Fine Dining",
    icon: "🍣",
    matchScore: 89,
    greatMatches: 3,
    reasons: ["Japanese cuisine affinity 5/5", "Raw-friendly profile · Omakase available"],
    description: "Nobu Matsuhisa's iconic Nikkei cuisine. Fresh fish flown daily, signature black cod miso.",
    priceRange: "AED 180–650",
    dishes: [
      { name: "Black Cod Miso", desc: "Signature Nobu, sweet den miso, pickled ginger, microgreens", price: "AED 245", match: 94, safe: true, whyLine: "Matches your Japanese affinity and umami depth", whyTags: ["Japanese affinity 5/5", "Umami 5/5", "Tender texture match", "Iconic umami dish"], icon: "🐟" },
      { name: "Wagyu Gyoza", desc: "Pan-fried Wagyu beef, ponzu dipping sauce, crispy base", price: "AED 165", match: 92, safe: true, whyLine: "Wagyu + crispy texture — two of your favorites", whyTags: ["Loved: Wagyu", "Crispy texture match", "Japanese 5/5", "Umami bomb"], icon: "🥟" },
      { name: "Yellowtail Jalapeño", desc: "Sashimi, yuzu ponzu, pickled jalapeño, micro shiso", price: "AED 185", match: 87, safe: true, whyLine: "Raw-friendly + medium spice — right in your zone", whyTags: ["Accepts raw ✓", "Spice medium match", "Japanese 5/5", "Light & bright"], icon: "🍋" },
      { name: "Toro Tartare with Caviar", desc: "Bluefin toro, Oscietra caviar, quail egg, chive oil", price: "AED 380", match: 84, safe: true, whyLine: "Premium celebration dish — fits your occasion", whyTags: ["Accepts raw ✓", "Premium budget match", "Celebration occasion", "Prestige upsell"], icon: "⭐" },
    ],
    wine: { name: "Château Léoville Poyferré 2015", region: "Saint-Julien, Bordeaux", match: 91 },
  },
  {
    id: "ossiano",
    name: "Ossiano",
    hotel: "Atlantis The Palm",
    cuisine: "Contemporary Seafood · Tasting Menu",
    outletType: "Fine Dining",
    icon: "🐠",
    matchScore: 74,
    greatMatches: 1,
    reasons: ["7-course tasting fits celebration context", "Wine pairing available"],
    description: "Underwater dining beside the Ambassador Lagoon. Michelin-recommended, 7-course tasting journey.",
    priceRange: "AED 450–950",
    dishes: [
      { name: "7-Course Tasting Menu", desc: "Chef's seasonal seafood journey · Optional Krug Champagne pairing", price: "AED 895", match: 85, safe: true, whyLine: "Celebration occasion + premium budget match", whyTags: ["Celebration occasion match", "Premium budget ✓", "Novelty score 3/5", "Prestige upsell"], icon: "⭐" },
      { name: "Atlantic Turbot", desc: "Oscietra caviar beurre blanc, samphire, sea herbs, finger lime", price: "AED 345", match: 78, safe: true, whyLine: "Seafood protein #2 + rich sauce match", whyTags: ["Seafood protein #2", "Rich sauce match", "Acidity pairing", "Celebration context"], icon: "🐟" },
      // Blue Lobster Bisque REMOVED by safety gate (shellfish allergy)
    ],
    wine: { name: "Krug Grande Cuvée 171ème Édition", region: "Reims, Champagne", match: 88 },
  },
  {
    id: "bread-street",
    name: "Bread Street Kitchen",
    hotel: "Atlantis The Palm",
    cuisine: "British · Gordon Ramsay",
    outletType: "Casual",
    icon: "🍽️",
    matchScore: 61,
    greatMatches: 0,
    reasons: ["Casual British matches your comfort mode", "Good Wagyu burger option"],
    description: "Gordon Ramsay's Dubai outpost. Relaxed all-day dining, signature beef Wellington, craft cocktails.",
    priceRange: "AED 120–380",
    dishes: [
      { name: "Beef Wellington", desc: "28-day aged beef fillet, duxelles, prosciutto, puff pastry, Madeira jus", price: "AED 320", match: 79, safe: true, whyLine: "Top protein: Beef + rich braised preparation", whyTags: ["Top protein: Beef", "Rich 4/5 match", "Braised cooking style", "Classic cuisine"], icon: "🥩" },
      { name: "Wagyu Beef Burger", desc: "Wagyu patty, aged cheddar, truffle mayo, brioche bun, duck fat fries", price: "AED 185", match: 74, safe: true, whyLine: "Loved: Wagyu + Truffle combination", whyTags: ["Loved: Wagyu", "Loved: Truffle", "Indulgence 4/5", "Casual match"], icon: "🍔" },
    ],
    wine: { name: "Stags' Leap Cabernet Sauvignon 2019", region: "Napa Valley, California", match: 82 },
  },
];

// ─── Location type helpers ──────────────────────────────────
const LOCATION_TYPE_ICONS = { hotel: "\u{1F3E8}", mall: "\u{1F6CD}\uFE0F", airport: "\u2708\uFE0F", standalone: "\u{1F37D}\uFE0F" };
const LOCATION_TYPE_LABELS = { hotel: "Hotel", mall: "Mall", airport: "Airport", standalone: "Restaurant" };
const LOCATION_TYPE_FILTERS = [
  { key: "all", label: "All", icon: "" },
  { key: "hotel", label: "Hotels", icon: "\u{1F3E8}" },
  { key: "mall", label: "Malls", icon: "\u{1F6CD}\uFE0F" },
  { key: "airport", label: "Airports", icon: "\u2708\uFE0F" },
];
const getLocationIcon = (type) => LOCATION_TYPE_ICONS[type] || "\u{1F3E8}";
const getLocationLabel = (type) => LOCATION_TYPE_LABELS[type] || "Hotel";

// ─────────────────────────────────────────────────────────────
// UTILITY HELPERS
// ─────────────────────────────────────────────────────────────
const getAllergensList = (safety) =>
  ALLERGENS.filter(a => a.key !== "none" && safety[a.key]).map(a => a.label);

const getTopCuisines = (affinities) =>
  Object.entries(affinities || {}).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([k]) => k);

const generateSummary = (profile) => {
  const allergens = getAllergensList(profile.safety);
  const cuisines  = getTopCuisines(profile.cuisine?.cuisine_affinities);
  const spice     = SPICE_LABELS[profile.taste?.spice_tolerance] || "Medium";
  const wine      = (profile.beverage?.wine_style_pref || [])[0];
  const parts = [];
  if (allergens.length) parts.push(`No ${allergens.join(", ")}`);
  if (cuisines.length) parts.push(`Loves ${cuisines.slice(0, 2).join(" & ")}`);
  parts.push(`Spice: ${spice}`);
  if (profile.beverage?.drinks_alcohol && wine) parts.push(`Prefers ${wine} wine`);
  return parts.join(" · ");
};

const getGreeting = () => {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return "Good morning";
  if (h >= 12 && h < 17) return "Good afternoon";
  return "Good evening";
};

const getMealPeriod = () => {
  const h = new Date().getHours();
  if (h >= 7 && h < 10) return "breakfast";
  if (h >= 11 && h < 14) return "lunch";
  if (h >= 18 && h < 22) return "dinner";
  return null;
};

const getVenueSubtitle = () => {
  const h = new Date().getHours();
  const d = new Date().getDay();
  const isWeekend = d === 0 || d === 6;
  if (h >= 7 && h < 10) return "BREAKFAST SPOTS MATCHED TO YOUR TASTE";
  if (h >= 11 && h < 14) return "QUICK LUNCH OPTIONS NEARBY";
  if (h >= 18 && h < 22) return "TONIGHT'S TOP PICKS FOR YOU";
  if (isWeekend) return "WEEKEND DINING ADVENTURES";
  return "RECOMMENDED FOR YOUR FOOD ID";
};

const timeAgo = (ts) => {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "yesterday";
  return `${days}d ago`;
};

const ACTIVITY_KEY = "consoome_activity";
const addActivity = (event) => {
  try {
    const list = JSON.parse(localStorage.getItem(ACTIVITY_KEY) || "[]");
    list.unshift({ ...event, timestamp: Date.now() });
    if (list.length > 20) list.length = 20;
    localStorage.setItem(ACTIVITY_KEY, JSON.stringify(list));
  } catch (_) {}
};
const getActivities = () => {
  try { return JSON.parse(localStorage.getItem(ACTIVITY_KEY) || "[]"); } catch (_) { return []; }
};

// ─────────────────────────────────────────────────────────────
// REUSABLE UI PRIMITIVES — redesigned per spec
// ─────────────────────────────────────────────────────────────

// Chip/Pill — spec: 44dp height, radius-sm, gold border on select
const Chip = ({ label, icon, selected, onClick, variant = "default", disabled, ariaLabel }) => {
  const isNone = label === "None";
  const styles = {
    default: {
      sel: { bg: `${T.gold}20`, color: T.cream, border: T.gold },
      unsel: { bg: "rgba(255,255,255,0.05)", color: `${T.cream}CC`, border: `${T.gold}25` },
    },
    danger: {
      sel: { bg: `${T.safetyRed}30`, color: T.cream, border: T.safetyRed },
      unsel: { bg: "rgba(255,255,255,0.05)", color: `${T.cream}CC`, border: `${T.gold}25` },
    },
    gold: {
      sel: { bg: `${T.gold}30`, color: T.cream, border: T.gold },
      unsel: { bg: "rgba(255,255,255,0.05)", color: `${T.cream}CC`, border: `${T.gold}25` },
    },
  };
  const v = styles[variant][selected ? "sel" : "unsel"];
  return (
    <button onClick={onClick} disabled={disabled} role="checkbox" aria-checked={!!selected} aria-label={ariaLabel || label} style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "10px 16px", height: 44,
      borderRadius: RAD.pill, border: `1px solid ${selected ? v.border : v.border}`,
      background: v.bg, color: v.color,
      fontSize: 13, fontWeight: 600, fontFamily: "Montserrat",
      cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.4 : 1,
      transition: "all 0.2s ease",
      transform: selected ? "scale(1.03)" : "scale(1)",
      outline: isNone && !selected ? `1px dashed ${T.gold}30` : "none",
      backdropFilter: "blur(8px)",
    }}>
      {icon && <span style={{ fontSize: 15 }}>{icon}</span>}
      {label}
      {selected && <span style={{ fontSize: 11, marginLeft: 2, color: T.gold }}>✓</span>}
    </button>
  );
};

// Gold CTA Button — spec: full-width, gold bg, black text, 48dp, radius-pill
const GoldBtn = ({ children, onClick, disabled, small, variant = "gold", fullWidth = true, style: ext = {} }) => {
  const btnStyles = {
    gold:      { bg: T.gold, color: T.ink, border: T.gold, hoverBg: T.goldDark },
    dark:      { bg: T.ink, color: T.cream, border: T.ink },
    outline:   { bg: "rgba(255,255,255,0.05)", color: T.cream, border: `${T.gold}40` },
    ghost:     { bg: "transparent", color: T.darkTextSec, border: "transparent" },
    cream:     { bg: T.cream, color: T.ink, border: T.cream },
  };
  const s = btnStyles[variant] || btnStyles.gold;
  return (
    <button onClick={onClick} disabled={disabled} style={{
      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
      width: fullWidth ? "100%" : "auto",
      padding: small ? "10px 20px" : "14px 24px",
      height: small ? 40 : 48,
      borderRadius: RAD.pill, border: `1.5px solid ${s.border}`,
      background: disabled ? `${T.cream}10` : s.bg,
      color: disabled ? T.darkTextSec : s.color,
      ...TYPE.button,
      fontFamily: "Montserrat",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.5 : 1,
      transition: "all 0.15s ease-out",
      ...ext,
    }}>
      {children}
    </button>
  );
};

const SectionLabel = ({ children }) => (
  <div style={{ ...TYPE.overline, color: T.darkTextSec, marginBottom: SP.md, textTransform: "uppercase" }}>
    {children}
  </div>
);

const ScoreBar = ({ label, value, max = 5, color = T.gold }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ display: "flex", justifyContent: "space-between", ...TYPE.bodySmall, fontWeight: 600, color: T.darkTextSec, marginBottom: 5 }}>
      <span>{label}</span>
      <span style={{ color: T.darkTextSec }}>{value}/{max}</span>
    </div>
    <div style={{ height: 5, background: `${T.gold}20`, borderRadius: 3, overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${(value / max) * 100}%`, background: color, borderRadius: 3, transition: "width 0.7s cubic-bezier(0.22,1,0.36,1)" }} />
    </div>
  </div>
);

// Confirm Dialog — safety-critical actions (allergen removal, discard changes)
const ConfirmDialog = ({ title, message, onConfirm, onCancel, confirmLabel = "Yes, Remove", cancelLabel = "Keep", danger = false }) => (
  <div role="alertdialog" aria-modal="true" aria-label={title} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: SP.lg, backdropFilter: "blur(4px)" }}>
    <div style={{ background: "#2A2A2A", borderRadius: RAD.xl, padding: SP.xl, maxWidth: 340, width: "100%", border: `1px solid ${danger ? T.safetyRed : T.gold}40` }}>
      <div style={{ ...TYPE.h2, color: T.cream, marginBottom: SP.md }}>{title}</div>
      <p style={{ ...TYPE.bodySmall, color: T.darkTextSec, lineHeight: "22px", marginBottom: SP.lg, margin: 0, marginBottom: SP.lg }}>{message}</p>
      <div style={{ display: "flex", gap: SP.sm }}>
        <GoldBtn variant="ghost" onClick={onCancel} small>{cancelLabel}</GoldBtn>
        <GoldBtn variant={danger ? "gold" : "gold"} onClick={onConfirm} small style={danger ? { background: T.safetyRed, borderColor: T.safetyRed } : {}}>{confirmLabel}</GoldBtn>
      </div>
    </div>
  </div>
);

// Match Badge — qualitative tiers only (no percentages — they feel like grades)
// variant="bestMatch" for Consoome Recommends top picks: ★★★ Best Match in prominent gold
const MatchBadge = ({ score, size = "sm", variant }) => {
  const tier = getMatchTier(score);
  const isBest = variant === "bestMatch";
  const isTop = isBest || score >= 80;
  return (
    <span aria-label={`${score} percent match, ${isBest ? "Best Match" : tier.label}`} style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: isBest ? "6px 14px" : size === "lg" ? "8px 14px" : "5px 10px",
      borderRadius: RAD.pill,
      background: isBest ? `${T.gold}30` : tier.bg,
      color: isBest ? T.gold : tier.color,
      fontSize: isBest ? 13 : size === "lg" ? 14 : 12, fontWeight: 700,
      border: isBest ? `1.5px solid ${T.gold}` : isTop ? `1px solid ${T.gold}` : score >= 73 ? `1px solid ${T.gold}60` : "none",
      boxShadow: isBest ? `0 0 12px ${T.gold}40` : isTop ? `0 0 8px ${T.gold}30` : "none",
    }}>
      {isBest ? "★★★ Best Match" : isTop ? `★★ ${tier.label}` : score >= 73 ? `★ ${tier.label}` : tier.label}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────
// PROGRESS BAR — spec: segmented, gold fill animation
// ─────────────────────────────────────────────────────────────
const StepBar = ({ current, total }) => (
  <div role="progressbar" aria-valuenow={current} aria-valuemin={1} aria-valuemax={total} aria-label={`Step ${current} of ${total}`} style={{ padding: `0 ${SP.lg}px`, marginBottom: SP.sm }}>
    <div style={{ display: "flex", gap: 4, marginBottom: SP.sm }}>
      {Array.from({ length: total }, (_, i) => (
        <div key={i} style={{
          flex: 1, height: 3, borderRadius: 2,
          background: i < current ? T.gold : `${T.gold}20`,
          transition: "background 0.4s ease-out",
        }} />
      ))}
    </div>
    <div style={{ display: "flex", justifyContent: "space-between", ...TYPE.overline, color: T.darkTextSec }}>
      <span>STEP {current} OF {total}</span>
      <span>{Math.round((current / total) * 100)}%</span>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// TAB BAR SVG ICONS — stroke-based, Feather-style
// ─────────────────────────────────────────────────────────────
const TabIcon = ({ name, color }) => {
  const p = { width: 22, height: 22, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (name) {
    case "compass": return (<svg {...p}><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>);
    case "camera": return (<svg {...p}><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>);
    case "user": return (<svg {...p}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>);
    case "share": return (<svg {...p}><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>);
    case "menu": return (<svg {...p}><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>);
    default: return null;
  }
};

// Inline SVG icons for UI elements (non-tab contexts)
const UiIcon = ({ name, size = 22, color = T.cream }) => {
  const p = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round", style: { flexShrink: 0 } };
  switch (name) {
    case "compass": return (<svg {...p}><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>);
    case "camera": return (<svg {...p}><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>);
    case "camera-off": return (<svg {...p}><line x1="1" y1="1" x2="23" y2="23"/><path d="M21 21H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3l2-3h6l2 3h4a2 2 0 0 1 2 2v9"/><path d="M15.28 15.28A4 4 0 1 1 8.72 8.72"/></svg>);
    case "user": return (<svg {...p}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>);
    case "share": return (<svg {...p}><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>);
    case "edit": return (<svg {...p}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>);
    case "bar-chart": return (<svg {...p}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>);
    case "sliders": return (<svg {...p}><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>);
    case "search": return (<svg {...p}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>);
    case "activity": return (<svg {...p}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>);
    default: return null;
  }
};

// ─────────────────────────────────────────────────────────────
// BOTTOM TAB BAR — spec 2.1: 5 tabs, scan elevated + gold
// ─────────────────────────────────────────────────────────────
const TabBar = ({ active, onNavigate, completeness = 100 }) => {
  const tabs = [
    { key: "home", label: "Discover", iconName: "compass" },
    { key: "scan", label: "Scan", iconName: "camera", elevated: true },
    { key: "profile", label: "My Food ID", iconName: "user" },
    { key: "share", label: "Share", iconName: "share" },
    { key: "more", label: "More", iconName: "menu" },
  ];
  return (
    <nav role="navigation" aria-label="Main navigation" style={{
      display: "flex", alignItems: "flex-end", justifyContent: "space-around",
      background: "rgba(26,26,26,0.92)", borderTop: `1px solid ${T.gold}15`,
      padding: "6px 0 calc(18px + env(safe-area-inset-bottom, 0px))", position: "sticky", bottom: 0, zIndex: 100,
      backdropFilter: "blur(20px)", boxShadow: "0 -2px 8px rgba(26,26,26,0.06)",
    }}>
      {tabs.map(t => {
        const isActive = active === t.key;
        const showProfileRing = t.key === "profile" && completeness < 100;
        const iconColor = t.elevated ? "#FFFFFF" : isActive ? T.ink : "#9E9E9E";
        return (
          <button key={t.key} onClick={() => onNavigate(t.key)} aria-label={t.label} aria-current={isActive ? "page" : undefined} style={{
            display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
            background: "none", border: "none", cursor: "pointer", fontFamily: "Montserrat",
            padding: t.elevated ? 0 : "4px 0",
            position: "relative",
          }}>
            {t.elevated ? (
              <div style={{
                width: 56, height: 56, borderRadius: "50%",
                background: isActive ? `linear-gradient(135deg, ${T.gold}, ${T.goldDark})` : T.darkCard,
                display: "flex", alignItems: "center", justifyContent: "center",
                marginTop: -12,
                boxShadow: isActive ? `0 4px 20px ${T.gold}50` : `0 2px 12px rgba(0,0,0,0.4)`,
                border: `1px solid ${isActive ? T.gold : T.gold + "25"}`,
                transition: "all 0.2s",
                animation: !isActive ? "scanPulse 3s ease-in-out infinite" : "none",
              }}>
                <TabIcon name={t.iconName} color="#FFFFFF" />
              </div>
            ) : showProfileRing ? (
              <div style={{ position: "relative", width: 24, height: 24 }}>
                <svg width="24" height="24" viewBox="0 0 24 24" style={{ position: "absolute", top: 0, left: 0 }}>
                  <circle cx="12" cy="12" r="10" fill="none" stroke={T.darkBorder} strokeWidth="2" />
                  <circle cx="12" cy="12" r="10" fill="none" stroke={T.gold} strokeWidth="2"
                    strokeDasharray={`${2 * Math.PI * 10 * completeness / 100} ${2 * Math.PI * 10}`}
                    strokeLinecap="round" transform="rotate(-90 12 12)" />
                </svg>
                <span style={{ position: "absolute", top: 1, left: 1, width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", opacity: isActive ? 1 : 0.45, transition: "opacity 0.2s" }}>
                  <TabIcon name={t.iconName} color={isActive ? T.cream : "#9E9E9E"} />
                </span>
              </div>
            ) : (
              <span style={{ display: "flex", alignItems: "center", justifyContent: "center", opacity: isActive ? 1 : 0.45, transition: "opacity 0.2s" }}>
                <TabIcon name={t.iconName} color={isActive ? T.cream : "#9E9E9E"} />
              </span>
            )}
            <span style={{
              ...TYPE.tabLabel, color: isActive ? (t.elevated ? T.gold : T.cream) : T.darkTextSec,
              fontWeight: isActive ? 600 : 500,
            }}>
              {t.label}
            </span>
            {isActive && !t.elevated && (
              <div style={{ position: "absolute", top: -7, width: 20, height: 2, borderRadius: 1, background: T.gold }} />
            )}
          </button>
        );
      })}
    </nav>
  );
};

// ─────────────────────────────────────────────────────────────
// SHARED: Dark-mode background layers (motion aesthetic)
// ─────────────────────────────────────────────────────────────
const DarkBgLayers = () => (
  <>
    <div style={{ position: "absolute", inset: 0, background: `radial-gradient(circle at 50% 24%, ${T.gold}2E, transparent 20%), radial-gradient(circle at 18% 18%, rgba(255,255,255,0.04), transparent 18%), radial-gradient(circle at 82% 78%, ${T.gold}18, transparent 16%), linear-gradient(180deg, ${T.darkBg} 0%, #222018 45%, ${T.darkBg} 100%)`, pointerEvents: "none" }} />
    <div style={{ position: "absolute", left: "50%", top: "30%", width: 320, height: 320, transform: "translate(-50%, -50%)", borderRadius: "50%", background: `radial-gradient(circle, ${T.gold}20, ${T.gold}08 42%, transparent 70%)`, filter: "blur(40px)", pointerEvents: "none" }} />
    <div style={{ position: "absolute", inset: 0, opacity: 0.03, backgroundImage: "radial-gradient(rgba(255,255,255,0.9) 0.7px, transparent 0.7px)", backgroundSize: "12px 12px", mixBlendMode: "soft-light", pointerEvents: "none" }} />
  </>
);

// Dark-mode form input style
const darkInputStyle = {
  width: "100%", padding: "14px 16px", borderRadius: RAD.lg,
  border: `1px solid ${T.gold}25`, background: "rgba(255,255,255,0.06)",
  ...TYPE.body, fontFamily: "Montserrat", color: T.cream,
  outline: "none", backdropFilter: "blur(8px)",
};

// Dark-mode divider
const DarkDivider = () => (
  <div style={{ display: "flex", alignItems: "center", gap: 16, margin: `${SP.lg}px 0` }}>
    <div style={{ flex: 1, height: 1, background: `${T.gold}20` }} />
    <span style={{ ...TYPE.caption, color: T.darkTextSec }}>OR</span>
    <div style={{ flex: 1, height: 1, background: `${T.gold}20` }} />
  </div>
);

// Google SVG icon (white-friendly)
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 010-9.18l-7.98-6.19a24.04 24.04 0 000 21.56l7.98-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
);

// ─────────────────────────────────────────────────────────────
// SCREEN: ENTRY / CHOICE — 3 action boxes
// ─────────────────────────────────────────────────────────────
const EntryChoiceScreen = ({ onBuildFoodID, onSignIn, onWhyConsoome, onPrivacy, onTerms }) => (
  <div style={{
    minHeight: "100vh", background: T.darkBg, display: "flex", flexDirection: "column",
    fontFamily: "'Montserrat', sans-serif", position: "relative", overflow: "hidden",
  }}>
    <DarkBgLayers />

    {/* Logo */}
    <div className="anim-fadein" style={{ position: "relative", zIndex: 2, padding: "60px 24px 0", textAlign: "center" }}>
      <img src={logoCream} alt="Consoome logo" style={{ height: 32, objectFit: "contain", marginBottom: 32 }} />
      <h1 className="anim-fadeup" style={{ fontSize: 26, fontWeight: 700, color: T.cream, letterSpacing: "-0.02em", marginBottom: 8 }}>
        Preferences checked in.
      </h1>
      <p className="anim-fadeup" style={{ ...TYPE.body, color: T.darkTextSec, marginBottom: 40, animationDelay: "0.05s" }}>
        Your personal Food ID, everywhere you dine.
      </p>
    </div>

    {/* Three choice boxes */}
    <div className="anim-fadeup" style={{ flex: 1, padding: "0 24px", position: "relative", zIndex: 2, animationDelay: "0.12s", display: "flex", flexDirection: "column", gap: 12 }}>

      {/* Box A: Build my Food ID */}
      <button onClick={onBuildFoodID} style={{
        display: "flex", alignItems: "center", gap: 16,
        padding: "20px 20px", borderRadius: RAD.lg,
        border: `1.5px solid ${T.gold}`,
        background: `${T.gold}15`,
        cursor: "pointer", fontFamily: "Montserrat", textAlign: "left",
        transition: "all 0.2s ease", backdropFilter: "blur(8px)",
        width: "100%",
      }}
      onMouseEnter={e => { e.currentTarget.style.background = `${T.gold}25`; }}
      onMouseLeave={e => { e.currentTarget.style.background = `${T.gold}15`; }}
      >
        <div style={{ width: 48, height: 48, borderRadius: RAD.md, background: T.gold, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>✦</div>
        <div>
          <div style={{ ...TYPE.h2, color: T.cream, marginBottom: 2 }}>Build my Food ID</div>
          <div style={{ ...TYPE.bodySmall, color: T.darkTextSec }}>Create account → personalised onboarding</div>
        </div>
        <span style={{ marginLeft: "auto", color: T.gold, fontSize: 18 }}>›</span>
      </button>

      {/* Box B: Sign in */}
      <button onClick={onSignIn} style={{
        display: "flex", alignItems: "center", gap: 16,
        padding: "20px 20px", borderRadius: RAD.lg,
        border: `1.5px solid ${T.gold}30`,
        background: "rgba(255,255,255,0.06)",
        cursor: "pointer", fontFamily: "Montserrat", textAlign: "left",
        transition: "all 0.2s ease", backdropFilter: "blur(8px)",
        width: "100%",
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = `${T.gold}70`; e.currentTarget.style.background = "rgba(255,255,255,0.10)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = `${T.gold}30`; e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
      >
        <div style={{ width: 48, height: 48, borderRadius: RAD.md, background: `${T.cream}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>↩</div>
        <div>
          <div style={{ ...TYPE.h2, color: T.cream, marginBottom: 2 }}>Sign in</div>
          <div style={{ ...TYPE.bodySmall, color: T.darkTextSec }}>Access your Food ID</div>
        </div>
        <span style={{ marginLeft: "auto", color: T.darkTextSec, fontSize: 18 }}>›</span>
      </button>

      {/* Box C: Why Consoome */}
      <button onClick={onWhyConsoome} style={{
        display: "flex", alignItems: "center", gap: 16,
        padding: "20px 20px", borderRadius: RAD.lg,
        border: `1.5px solid ${T.gold}20`,
        background: "rgba(255,255,255,0.03)",
        cursor: "pointer", fontFamily: "Montserrat", textAlign: "left",
        transition: "all 0.2s ease", backdropFilter: "blur(8px)",
        width: "100%",
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = `${T.gold}40`; e.currentTarget.style.background = "rgba(255,255,255,0.07)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = `${T.gold}20`; e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
      >
        <div style={{ width: 48, height: 48, borderRadius: RAD.md, background: `${T.cream}10`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>ℹ</div>
        <div>
          <div style={{ ...TYPE.h2, color: T.cream, marginBottom: 2 }}>Why Consoome</div>
          <div style={{ ...TYPE.bodySmall, color: T.darkTextSec }}>Learn how it works</div>
        </div>
        <span style={{ marginLeft: "auto", color: T.darkTextSec, fontSize: 18 }}>›</span>
      </button>
    </div>

    {/* Footer legal */}
    <div style={{ padding: "24px 24px 36px", textAlign: "center", position: "relative", zIndex: 2 }}>
      <p style={{ ...TYPE.caption, color: T.darkTextSec }}>
        <button onClick={onPrivacy} style={{ color: T.darkTextSec, background: "none", border: "none", cursor: "pointer", fontFamily: "Montserrat", fontSize: "inherit", padding: 0 }}>Privacy Policy</button>
        {" · "}
        <button onClick={onTerms} style={{ color: T.darkTextSec, background: "none", border: "none", cursor: "pointer", fontFamily: "Montserrat", fontSize: "inherit", padding: 0 }}>Terms of Use</button>
        {" · "}
        privacy@consoome.com
      </p>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// SCREEN: WHY CONSOOME — full info page
// ─────────────────────────────────────────────────────────────
const WhyConsoomeScreen = ({ onBack, onPrivacy, onTerms }) => (
  <div style={{
    minHeight: "100vh", background: T.darkBg, display: "flex", flexDirection: "column",
    fontFamily: "'Montserrat', sans-serif", position: "relative", overflow: "hidden",
  }}>
    <DarkBgLayers />

    {/* Header */}
    <div style={{ position: "relative", zIndex: 2, padding: "52px 24px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <img src={logoCream} alt="Consoome logo" style={{ height: 22, objectFit: "contain" }} />
      <button onClick={onBack} style={{ ...TYPE.bodySmall, fontWeight: 600, color: T.darkTextSec, background: "none", border: "none", cursor: "pointer", fontFamily: "Montserrat" }}>
        ← Back
      </button>
    </div>

    <div style={{ flex: 1, padding: "24px 24px 60px", position: "relative", zIndex: 2, overflowY: "auto" }}>
      {/* Hero */}
      <div className="anim-fadeup" style={{ marginBottom: 32, textAlign: "center" }}>
        <div style={{ ...TYPE.overline, color: T.gold, marginBottom: 8 }}>WHY CONSOOME</div>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: T.cream, letterSpacing: "-0.02em", marginBottom: 8 }}>Be expected everywhere.</h1>
        <p style={{ ...TYPE.body, color: T.darkTextSec, lineHeight: "24px" }}>
          Create your personal profile once.<br />
          Every luxury property knows exactly how to welcome you.
        </p>
      </div>

      {/* Without vs With */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 32 }}>
        {/* Without */}
        <div style={{ background: `${T.safetyRed}10`, border: `1px solid ${T.safetyRed}30`, borderRadius: RAD.lg, padding: 16 }}>
          <div style={{ ...TYPE.caption, fontWeight: 700, color: T.safetyRed, marginBottom: 12 }}>WITHOUT CONSOOME</div>
          {[
            "Explain your nut allergy at every restaurant",
            "Request almond milk for the third time",
            "Minibar stocked with things you won't touch",
            "Server suggests shellfish… you're allergic",
            "Hotels forget you were here last month",
          ].map((t, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "flex-start" }}>
              <span style={{ color: T.safetyRed, fontSize: 12, marginTop: 2, flexShrink: 0 }}>✕</span>
              <span style={{ ...TYPE.caption, color: T.darkTextSec, lineHeight: "18px" }}>{t}</span>
            </div>
          ))}
        </div>
        {/* With */}
        <div style={{ background: `${T.safetyGreen}10`, border: `1px solid ${T.safetyGreen}30`, borderRadius: RAD.lg, padding: 16 }}>
          <div style={{ ...TYPE.caption, fontWeight: 700, color: T.safetyGreen, marginBottom: 12 }}>WITH CONSOOME</div>
          {[
            "Allergen awareness built in — 0% match on unsafe dishes",
            "Share once, they remember you",
            "Know what suits you instantly on any menu",
            "Hotels personalise your minibar and dining before you arrive",
            "First visit or fifth — they know you",
          ].map((t, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "flex-start" }}>
              <span style={{ color: T.safetyGreen, fontSize: 12, marginTop: 2, flexShrink: 0 }}>✓</span>
              <span style={{ ...TYPE.caption, color: T.darkTextSec, lineHeight: "18px" }}>{t}</span>
            </div>
          ))}
        </div>
      </div>

      {/* How it works */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ ...TYPE.overline, color: T.darkTextSec, marginBottom: 16 }}>HOW IT WORKS</div>
        {[
          { step: "01", icon: "✦", title: "Build your profile once", desc: "Tell us your preferences, allergies, and what you love. Example: Nut allergy, oat milk preferred, love spicy food." },
          { step: "02", icon: "🎯", title: "Get instant recommendations", desc: "Browse 800+ dishes and see your perfect matches. 95% match dishes appear at the top of any menu." },
          { step: "03", icon: "↗", title: "Share with hotels", desc: "Send your profile to any hotel you're visiting. Hotels can prepare what you'll love before you arrive." },
          { step: "04", icon: "🏨", title: "Be expected, not anonymous", desc: "Hotels know you before you arrive. First visit or fifth — they remember your preferences." },
        ].map((s, i) => (
          <div key={i} style={{ display: "flex", gap: 16, marginBottom: 20, alignItems: "flex-start" }}>
            <div style={{ width: 44, height: 44, borderRadius: RAD.md, background: `${T.gold}15`, border: `1px solid ${T.gold}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
              {s.icon}
            </div>
            <div>
              <div style={{ ...TYPE.overline, color: T.gold, marginBottom: 2 }}>STEP {s.step}</div>
              <div style={{ ...TYPE.bodySmall, fontWeight: 700, color: T.cream, marginBottom: 4 }}>{s.title}</div>
              <div style={{ ...TYPE.caption, color: T.darkTextSec, lineHeight: "18px" }}>{s.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Scenario box */}
      <div style={{ background: "rgba(255,255,255,0.05)", border: `1px solid ${T.gold}25`, borderRadius: RAD.lg, padding: 20, marginBottom: 32, backdropFilter: "blur(8px)" }}>
        <div style={{ ...TYPE.overline, color: T.gold, marginBottom: 12 }}>EXAMPLE · DUBAI 5-STAR HOTEL</div>
        <div style={{ ...TYPE.caption, color: T.darkTextSec, lineHeight: "20px", marginBottom: 12 }}>
          You've shared your profile: Shellfish allergy · Love bold reds · Prefer oat milk · Enjoy dark chocolate
        </div>
        <div style={{ ...TYPE.caption, color: T.cream, fontWeight: 600, lineHeight: "22px" }}>
          <div>✓ Wagyu Tenderloin — 98% match</div>
          <div style={{ color: T.safetyRed }}>✕ Shellfish dishes show 0% match — allergen detected</div>
        </div>
        <div style={{ ...TYPE.caption, color: T.darkTextSec, marginTop: 12, lineHeight: "18px", fontStyle: "italic" }}>
          You build it once. You share it. Hotels can surprise you.
          Your data, your control. Share only when and where you choose.
        </div>
      </div>

      {/* Always verify disclaimer */}
      <div style={{ background: `${T.safetyRed}12`, border: `1px solid ${T.safetyRed}25`, borderRadius: RAD.md, padding: "10px 14px", marginBottom: 32 }}>
        <p style={{ ...TYPE.caption, color: T.safetyRed, lineHeight: "16px" }}>
          Consoome helps identify potential allergens based on available data. Always verify with restaurant staff before ordering.
        </p>
      </div>

      {/* Back button */}
      <GoldBtn onClick={onBack}>← Back to Start</GoldBtn>

      {/* Footer */}
      <div style={{ textAlign: "center", marginTop: 20 }}>
        <p style={{ ...TYPE.caption, color: T.darkTextSec }}>
          <button onClick={onPrivacy} style={{ color: T.darkTextSec, background: "none", border: "none", cursor: "pointer", fontFamily: "Montserrat", fontSize: "inherit", padding: 0 }}>Privacy Policy</button>
          {" · "}
          <button onClick={onTerms} style={{ color: T.darkTextSec, background: "none", border: "none", cursor: "pointer", fontFamily: "Montserrat", fontSize: "inherit", padding: 0 }}>Terms of Use</button>
        </p>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// SCREEN: PRIVACY POLICY
// ─────────────────────────────────────────────────────────────
const PrivacyPolicyScreen = ({ onBack }) => (
  <div style={{
    minHeight: "100vh", background: T.darkBg, display: "flex", flexDirection: "column",
    fontFamily: "'Montserrat', sans-serif", position: "relative",
  }}>
    <div style={{ padding: "52px 24px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <img src={logoCream} alt="Consoome logo" style={{ height: 22, objectFit: "contain" }} />
      <button onClick={onBack} style={{ ...TYPE.bodySmall, fontWeight: 600, color: T.darkTextSec, background: "none", border: "none", cursor: "pointer", fontFamily: "Montserrat" }}>
        ← Back
      </button>
    </div>
    <div style={{ flex: 1, padding: "24px 24px 60px", overflowY: "auto" }}>
      <h1 style={{ ...TYPE.display, color: T.cream, marginBottom: 8 }}>Privacy Policy</h1>
      <p style={{ ...TYPE.caption, color: T.darkTextSec, marginBottom: 24 }}>Effective Date: March 2026</p>

      <p style={{ ...TYPE.bodySmall, color: T.darkTextSec, lineHeight: "22px", marginBottom: 24 }}>
        Consoome is currently operated as a pilot / testing project in Dubai, United Arab Emirates. At the date of this Privacy Policy, no company has yet been incorporated for the pilot. References to "Consoome," "we," "us," or "our" mean the Consoome pilot team / project operators in Dubai, UAE.
      </p>
      <p style={{ ...TYPE.bodySmall, color: T.darkTextSec, lineHeight: "22px", marginBottom: 24 }}>
        This Privacy Policy explains how we collect, use, store, and protect personal data when you create and use a Consoome account and related pilot services.
      </p>

      {[
        { title: "1. Who We Are", body: "Pilot Operator / Data Controller: Consoome pilot team / project operators, Dubai, United Arab Emirates. No company has yet been incorporated for the pilot.\n\nContact: privacy@consoome.com" },
        { title: "2. Scope", body: "This Privacy Policy applies to personal data processed through: account registration and login; user profile creation; Food ID setup and refinement; recommendation and matching features; pilot testing and product evaluation; support communications; security, fraud prevention, and platform operations; menu scan features, where available during the pilot.\n\nThis Privacy Policy does not apply to third-party websites, venues, or services that you access independently." },
        { title: "3. Eligibility", body: "Consoome is intended for adults only. You must be at least 18 years old to create an account or use the Services.\n\nWe do not knowingly collect personal data from children. If we learn that an account was created by someone under 18, we may suspend or delete the account and remove associated data." },
        { title: "4. Data We Collect", body: "A. Account Data\nEmail address, password or authentication credentials, account status, login metadata.\n\nB. Food ID / Preference Data\nDietary preferences, cuisine preferences, taste preferences, beverage preferences, stated dislikes, profile refinement inputs, and any dietary or sensitivity-related information you choose to provide.\n\nC. Usage Data\nApp interaction data, feature usage logs, timestamps, device and technical log information necessary for service security and performance, scan usage counts.\n\nD. Scan and Uploaded Content Data\nWhere menu scan functionality is available: menu photos taken by you, uploaded image or PDF files, extracted menu text, parsing results, scan-related metadata such as page counts and timestamps. Each user may be allowed up to 5 menu scans, each containing up to 5 pages.\n\nE. Support and Communications Data\nMessages you send to us, replies and support history." },
        { title: "5. Higher-Risk Data", body: "Some preference data may reveal more sensitive information depending on context, including allergy-related preferences, health-related food restrictions, religion-linked dietary choices, or lifestyle-linked dietary exclusions.\n\nIf you provide such information, you do so voluntarily for personalization and recommendation purposes. We process it only as described in this Privacy Policy." },
        { title: "6. How We Collect Data", body: "We collect data directly from you when you register, log in, complete your Food ID, update preferences, use platform features, contact support, or take a menu photo or upload content for scanning. We also collect technical logs generated by your use of the Services." },
        { title: "7. Why We Process Your Data", body: "A. To create and manage your account — including authentication, security, and profile administration.\n\nB. To operate the pilot and test service performance — to run, evaluate, troubleshoot, secure, and improve the pilot.\n\nC. To provide personalization and recommendations — using your Food ID and stated preferences.\n\nD. To enable food matching — matching your preferences with available offerings.\n\nE. To enable user-initiated sharing — any sharing of profile-based results with a venue is initiated by you, not automatically by us.\n\nF. To process menu scans — extracting menu information, generating recommendation outputs, and improving scan performance.\n\nG. To operate, maintain, and secure the platform — ensuring stability, preventing misuse, detecting suspicious activity.\n\nH. To provide support and comply with legal obligations." },
        { title: "8. Basis for Processing", body: "Where consent is required, we rely on your consent. In other cases, we may process personal data where necessary to provide the Services you request, perform the service relationship, maintain platform security, comply with legal obligations, or pursue legitimate operational interests.\n\nWhere we rely on consent, you may withdraw it by contacting us, although doing so may affect our ability to provide parts of the Services." },
        { title: "9. No Marketing at Launch", body: "Consoome does not use your personal data for direct marketing during the pilot. We do not send marketing emails, promotional campaigns, or advertising communications." },
        { title: "10. Sharing of Personal Data", body: "Consoome does not automatically share your personal data with restaurants, hotels, or venues.\n\nAny sharing of your Food ID or related outputs with a venue is done only if you choose to initiate or authorize it.\n\nWe may share data with service providers acting on our behalf and under appropriate controls where necessary to operate the service." },
        { title: "11. Service Providers", body: "At launch, Consoome may use third-party providers such as AWS Middle East (UAE) for hosting and infrastructure, and Anthropic for certain AI-related processing functions. These providers process data only to the extent necessary to support the Services." },
        { title: "12. Data Storage Location", body: "Your personal data is intended to be stored in the United Arab Emirates only.\n\nIf a future feature requires cross-border processing or access, we may update this Privacy Policy and implement any additional safeguards required by law." },
        { title: "13. Data Retention", body: "We retain personal data only for as long as reasonably necessary for the pilot purposes described in this Privacy Policy.\n\nThis is a pilot deployment. Personal data collected for the pilot may be deleted at the end of the pilot. We may also delete inactive pilot accounts earlier where reasonably necessary.\n\nRetention may be extended where required by law, security, fraud prevention, dispute handling, or to complete deletion cycles in backups.\n\nIf you request deletion earlier, we will review and process the request in accordance with applicable law." },
        { title: "14. User Rights", body: "Depending on applicable law, you may have the right to request: access to your personal data; correction of inaccurate data; deletion of your account or personal data; withdrawal of consent; restriction or objection to certain processing; a copy of certain personal data.\n\nAt launch, Consoome handles such requests by written request only.\n\nContact: privacy@consoome.com\n\nWe may request reasonable verification before acting on your request." },
        { title: "15. Security", body: "We use reasonable technical and organizational measures designed to protect personal data against unauthorized access, misuse, alteration, disclosure, or loss.\n\nHowever, no system can be guaranteed to be completely secure, and you acknowledge that electronic transmission and storage involve inherent risks." },
        { title: "16. Account Credentials", body: "You are responsible for maintaining the confidentiality of your login credentials and for activities occurring under your account.\n\nPlease notify us immediately if you believe your account has been compromised." },
        { title: "17. Recommendation and Automated Processing Notice", body: "Consoome may use rule-based or AI-supported systems to provide personalization, ranking, recommendation relevance, and matching outputs.\n\nThese outputs are suggestions only. They are not medical advice, allergen clearance, ingredient certification, or a guarantee of safety, suitability, availability, or compliance with your dietary requirements.\n\nConsoome does not use your data during the pilot to make decisions that produce legal effects or similarly significant effects on you." },
        { title: "18. Changes to This Privacy Policy", body: "We may update this Privacy Policy from time to time. If we make material changes, we will update the effective date and, where appropriate, provide additional notice." },
        { title: "19. Contact Us", body: "For privacy-related questions or requests:\nprivacy@consoome.com" },
      ].map((s, i) => (
        <div key={i} style={{ marginBottom: 24 }}>
          <div style={{ ...TYPE.bodySmall, fontWeight: 700, color: T.cream, marginBottom: 6 }}>{s.title}</div>
          <div style={{ ...TYPE.bodySmall, color: T.darkTextSec, lineHeight: "22px", whiteSpace: "pre-line" }}>{s.body}</div>
        </div>
      ))}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// SCREEN: TERMS OF USE
// ─────────────────────────────────────────────────────────────
const TermsOfUseScreen = ({ onBack }) => (
  <div style={{
    minHeight: "100vh", background: T.darkBg, display: "flex", flexDirection: "column",
    fontFamily: "'Montserrat', sans-serif", position: "relative",
  }}>
    <div style={{ padding: "52px 24px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <img src={logoCream} alt="Consoome logo" style={{ height: 22, objectFit: "contain" }} />
      <button onClick={onBack} style={{ ...TYPE.bodySmall, fontWeight: 600, color: T.darkTextSec, background: "none", border: "none", cursor: "pointer", fontFamily: "Montserrat" }}>
        ← Back
      </button>
    </div>
    <div style={{ flex: 1, padding: "24px 24px 60px", overflowY: "auto" }}>
      <h1 style={{ ...TYPE.display, color: T.cream, marginBottom: 8 }}>Terms of Use</h1>
      <p style={{ ...TYPE.caption, color: T.darkTextSec, marginBottom: 24 }}>Effective Date: March 2026</p>

      <p style={{ ...TYPE.bodySmall, color: T.darkTextSec, lineHeight: "22px", marginBottom: 24 }}>
        These Terms of Use ("Terms") govern your access to and use of the Consoome platform, website, applications, and related services (collectively, the "Services"). By creating an account, clicking to accept, or using the Services, you agree to be bound by these Terms. If you do not agree, do not use the Services.
      </p>

      {[
        { title: "1. Pilot Status and Operator", body: "The Services are being provided during a pilot / testing phase by the Consoome pilot team / project operators in Dubai, United Arab Emirates.\n\nNo company has yet been incorporated for the pilot at the date of these Terms. Any future incorporated operating entity may replace this operator description once the commercial service is formally launched." },
        { title: "2. Eligibility", body: "You must be at least 18 years old to use the Services. By using the Services, you represent and warrant that you are legally capable of entering into these Terms." },
        { title: "3. Account Registration", body: "To use certain features, you must create an account and provide accurate, current, and complete information. You are responsible for maintaining the confidentiality of your account credentials, all activities under your account, and promptly updating your information if it changes.\n\nWe may suspend or terminate accounts that contain false, misleading, or incomplete information." },
        { title: "4. Nature of the Service", body: "Consoome provides a user-centered food preference and recommendation service designed to help users create a Food ID and receive more relevant recommendations or matching outputs.\n\nThe Services are currently offered on a pilot / beta / testing basis only. Features may change, be incomplete, contain errors, or be withdrawn without notice.\n\nConsoome is not a medical service, nutrition clinic, allergy-testing provider, emergency advisory system, healthcare provider, or certified menu-verification service." },
        { title: "5. No Medical, Allergy, Ingredient, or Safety Guarantee", body: "Food recommendations, preference matching, compatibility outputs, rankings, menu scan outputs, and suggested items are provided for general informational and convenience purposes only.\n\nConsoome is only suggesting possible options based on available data and user-entered preferences. Consoome does not verify final ingredients, hidden ingredients, substitutions, cross-contamination risks, kitchen practices, recipe changes, allergens, traces, staff handling, preparation methods, or item availability in real time.\n\nYou remain solely and fully responsible for verifying all relevant details directly with the venue, restaurant, hotel, chef, or serving staff before ordering or consuming any item.\n\nIf you have allergies, intolerances, medical dietary restrictions, religious restrictions, or any other food sensitivity, you must not rely solely on the Services. You must independently confirm suitability with venue staff.\n\nTo the fullest extent permitted by law, Consoome disclaims responsibility and liability for allergic reactions, dietary issues, ingredient inaccuracies, omitted warnings, venue substitutions, menu changes, extraction errors, inferred inaccuracies, or any adverse outcome resulting from reliance on recommendation outputs, scan outputs, matching outputs, or inferred compatibility." },
        { title: "6. User Content, Scan Inputs, and Uploads", body: "You retain responsibility for the information you provide through your account, including Food ID inputs, preference data, and any menu images or PDF files you submit for scanning.\n\nYou agree not to provide content or inputs that are false, misleading, or unlawful; infringe third-party rights; contain malicious code or harmful material; or are used to abuse, test, scrape, disrupt, or reverse-engineer the Services without authorization.\n\nWhere menu scan functionality is available during the pilot, you may scan menus by taking a photo or uploading an image or PDF. During the pilot, usage may be limited to 5 scans per user, with up to 5 pages per menu." },
        { title: "7. Limited License", body: "Subject to these Terms, Consoome grants you a limited, non-exclusive, non-transferable, revocable license to access and use the Services for your personal, lawful, non-commercial use during the pilot." },
        { title: "8. Prohibited Conduct", body: "You may not: use the Services unlawfully or fraudulently; interfere with system integrity or security; attempt unauthorized access to accounts, systems, or data; copy, scrape, crawl, reverse-engineer, or exploit the Services except as expressly permitted by law; use the Services to harm others or violate their rights." },
        { title: "9. Intellectual Property", body: "All rights, title, and interest in the Services, including software, branding, pilot flows, recommendation logic, scan workflows, interfaces, layouts, content arrangement, and platform functionality, belong to Consoome or its licensors, except for user-provided content and any third-party materials." },
        { title: "10. Privacy", body: "Your use of the Services is also governed by the Consoome Privacy Policy, which forms an integral part of these Terms." },
        { title: "11. Pilot Participation", body: "You acknowledge that the Services are made available as a pilot, beta, or testing environment. Features, recommendation logic, scan workflows, page limits, account access rules, and available functionality may change, reset, be restricted, or disappear at any time.\n\nPilot participation does not create any right to continued access, feature continuity, data persistence, or future commercial availability." },
        { title: "12. No Reliance on Recommendations", body: "Recommendation outputs, matching outputs, menu scan outputs, and compatibility signals are assistive only. They do not constitute definitive menu clearance, ingredient certification, allergen verification, or suitability confirmation.\n\nYou must not rely on the Services as the sole basis for ordering or consuming any food or beverage item." },
        { title: "13. Venue Data Accuracy", body: "Consoome may rely on venue-provided, user-submitted, extracted, parsed, inferred, or otherwise available menu information. Such information may be incomplete, outdated, inaccurate, changed without notice, inconsistently formatted, or context-dependent.\n\nConsoome does not guarantee that menu data, extracted text, structured dish data, ingredient assumptions, or recommendation outputs are complete or current." },
        { title: "14. Feedback", body: "If you provide feedback, suggestions, comments, issue reports, test notes, or improvement ideas, you grant Consoome a worldwide, non-exclusive, royalty-free right to use, evaluate, adapt, and incorporate that feedback for product improvement, testing, development, training, and service enhancement purposes without compensation." },
        { title: "15. Reservation of Rights", body: "Consoome reserves the right, at any time and in its sole discretion, to invite, restrict, suspend, revoke, limit, reset, or terminate access to the pilot or any part of it, with or without notice, for pilot management, legal, commercial, technical, operational, or security reasons." },
        { title: "16. Future Entity Transition", body: "You acknowledge that a newly incorporated Consoome entity may, upon notice, assume operation of the Services, related data processing activities, contractual position, and user relationships for the continuation of the project or launch of a commercial service." },
        { title: "17. Pilot Availability and Service Changes", body: "We may modify, suspend, restrict, test, reset, or discontinue any part of the Services at any time, with or without notice. We do not guarantee continuous availability, error-free operation, data persistence beyond the pilot, or continued access to any pilot feature." },
        { title: "18. Support During Pilot", body: "Support during the pilot is provided on a reasonable-efforts basis only. Consoome does not provide any service-level agreement, guaranteed response time, or continuous support obligation during the pilot." },
        { title: "19. Suspension and Termination", body: "We may suspend, restrict, or terminate your access if you breach these Terms, your use creates legal, technical, or security risk, we are required to do so by law, the account remains inactive, or the pilot is changed, limited, paused, or closed.\n\nYou may stop using the Services at any time and may request account deletion by writing to privacy@consoome.com." },
        { title: "20. Disclaimers", body: "The Services are provided on an \"as is,\" \"as available,\" and \"pilot testing\" basis to the fullest extent permitted by law.\n\nConsoome does not warrant that the Services will be uninterrupted, error-free, complete, fully accurate, current, safe for any dietary condition, or suitable for every dietary, cultural, medical, or personal context.\n\nRecommendation outputs are generated from limited available data and user inputs and may be incomplete, outdated, inaccurate, or unsuitable." },
        { title: "21. Limitation of Liability", body: "To the fullest extent permitted by law, Consoome and its founders, project operators, team members, contractors, advisors, and affiliates will not be liable for indirect, incidental, special, consequential, punitive, or exemplary damages.\n\nConsoome will not be responsible for any loss, damage, illness, reaction, or claim arising from ingredient inaccuracies, undeclared allergens, cross-contamination, recipe changes, venue substitutions, staff miscommunication, out-of-date menus, upload or extraction errors, parsing errors, or user reliance on suggestions without independent verification.\n\nIf liability is nevertheless established, Consoome's aggregate liability shall not exceed the amount you paid in the 12 months preceding the claim, or if you paid nothing, AED 350." },
        { title: "22. Indemnity", body: "You agree to indemnify and hold harmless Consoome from claims, losses, liabilities, and expenses arising from your misuse of the Services, breach of these Terms, violation of applicable law, or violation of third-party rights." },
        { title: "23. Governing Law and Jurisdiction", body: "These Terms are governed by the laws of the United Arab Emirates. Any dispute shall be subject to the exclusive jurisdiction of the courts of Dubai, unless mandatory law requires otherwise." },
        { title: "24. Changes to These Terms", body: "We may update these Terms from time to time. Continued use of the Services after an updated version takes effect constitutes acceptance of the revised Terms." },
        { title: "25. Contact", body: "Questions regarding these Terms may be sent to:\nprivacy@consoome.com" },
      ].map((s, i) => (
        <div key={i} style={{ marginBottom: 24 }}>
          <div style={{ ...TYPE.bodySmall, fontWeight: 700, color: T.cream, marginBottom: 6 }}>{s.title}</div>
          <div style={{ ...TYPE.bodySmall, color: T.darkTextSec, lineHeight: "22px", whiteSpace: "pre-line" }}>{s.body}</div>
        </div>
      ))}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// SCREEN: MORE MENU
// ─────────────────────────────────────────────────────────────
const MoreScreen = ({ profile, onPrivacy, onTerms, onBack, onLogout, onResetFoodId }) => {
  const [scansRemaining, setScansRemaining] = useState(null);

  useEffect(() => {
    try {
      const token = localStorage.getItem("sb_token");
      if (!token) return;
      const payload = JSON.parse(atob(token.split(".")[1]));
      const uid = payload.sub;
      if (!uid) return;
      fetch(`${API_BASE}/scan/quota/${uid}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.ok ? r.json() : null).then(data => {
        if (data) setScansRemaining(data.scans_remaining);
      }).catch(() => {});
    } catch (_) {}
  }, []);

  const MoreRow = ({ icon, label, detail, action, destructive }) => (
    <button onClick={action} style={{
      display: "flex", alignItems: "center", gap: SP.md, width: "100%",
      padding: `${SP.md}px 0`, background: "none", border: "none",
      borderBottom: `1px solid ${T.gold}15`, cursor: action ? "pointer" : "default",
      fontFamily: "Montserrat", textAlign: "left",
    }}>
      <div style={{ width: 40, height: 40, borderRadius: RAD.md, background: destructive ? `${T.safetyRed}15` : `${T.gold}10`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ ...TYPE.body, color: destructive ? T.safetyRed : T.cream, fontWeight: 500 }}>{label}</span>
        {detail && <div style={{ ...TYPE.caption, color: T.darkTextSec, marginTop: 2 }}>{detail}</div>}
      </div>
      {action && <span style={{ color: T.darkTextSec, fontSize: 16, flexShrink: 0 }}>›</span>}
    </button>
  );

  return (
    <div style={{
      minHeight: "100vh", background: T.darkBg, display: "flex", flexDirection: "column",
      fontFamily: "'Montserrat', sans-serif", position: "relative", overflow: "hidden",
    }}>
      <DarkBgLayers />
      <div style={{ padding: "52px 24px 0", position: "relative", zIndex: 2 }}>
        <img src={logoCream} alt="Consoome logo" style={{ height: 22, objectFit: "contain", marginBottom: 24 }} />
        <h1 style={{ ...TYPE.display, color: T.cream, marginBottom: 4 }}>More</h1>
      </div>
      <div style={{ flex: 1, padding: `${SP.md}px ${SP.lg}px 120px`, position: "relative", zIndex: 2, overflowY: "auto" }}>

        {/* Account Settings */}
        <SectionLabel>ACCOUNT</SectionLabel>
        <MoreRow icon="👤" label="Display Name" detail={profile?.display_name || "Not set"} />
        <MoreRow icon="✉️" label="Email" detail={profile?.email || "Not available"} />

        {/* Credits & Usage */}
        <div style={{ marginTop: SP.lg }}>
          <SectionLabel>CREDITS & USAGE</SectionLabel>
          <MoreRow icon="📸" label="Scan Credits" detail={scansRemaining != null ? `${scansRemaining} scan${scansRemaining !== 1 ? "s" : ""} remaining` : "Loading..."} />
        </div>

        {/* About */}
        <div style={{ marginTop: SP.lg }}>
          <SectionLabel>ABOUT</SectionLabel>
          <MoreRow icon="ℹ️" label="Version" detail="1.0.0 (Pilot)" />
          <MoreRow icon="⚠️" label="Allergen Disclaimer" detail="Consoome suggests options — always verify with venue staff" />
          <MoreRow icon="🔒" label="Privacy Policy" action={onPrivacy} />
          <MoreRow icon="📄" label="Terms of Use" action={onTerms} />
          <MoreRow icon="✉️" label="Contact" detail="privacy@consoome.com" action={() => window.location.href = "mailto:privacy@consoome.com"} />
        </div>

        {/* Account */}
        <div style={{ marginTop: SP.lg }}>
          <SectionLabel>ACCOUNT ACTIONS</SectionLabel>
          <MoreRow icon="🚪" label="Log Out" action={onLogout} />
        </div>

        {/* Danger Zone */}
        <div style={{ marginTop: SP.lg }}>
          <SectionLabel>DANGER ZONE</SectionLabel>
          <MoreRow icon="🔄" label="Reset Food ID" detail="Erase your profile and start onboarding from scratch" destructive action={onResetFoodId} />
          <MoreRow icon="🗑️" label="Delete Account" detail="Email privacy@consoome.com to request deletion" destructive action={() => window.location.href = "mailto:privacy@consoome.com?subject=Account%20Deletion%20Request"} />
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// SCREEN: LOGIN — sign in / Google / register
// ─────────────────────────────────────────────────────────────
const LoginScreen = ({ onSignIn, onRegister, onPrivacy, onTerms }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleSignIn = async () => {
    setError(""); setLoading(true);
    try {
      const data = await signInWithEmail(email, password);
      const token = data.session?.access_token;
      if (token) localStorage.setItem("sb_token", token);
      onSignIn({ token, user: data.user });
    } catch (e) {
      setError(friendlyError(e));
    } finally { setLoading(false); }
  };

  const handleGoogle = async () => {
    setError("");
    try { await signInWithGoogle(); }
    catch (e) { setError(friendlyError(e)); }
  };

  const handleForgotPassword = async () => {
    if (!email) { setError("Enter your email address first."); return; }
    setError(""); setLoading(true);
    try {
      const { resetPassword } = await import("./supabaseClient");
      await resetPassword(email);
      setResetSent(true);
    } catch (e) { setError(friendlyError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div style={{
      minHeight: "100vh", background: T.darkBg, display: "flex", flexDirection: "column",
      fontFamily: "'Montserrat', sans-serif", position: "relative", overflow: "hidden",
    }}>
      <DarkBgLayers />

      {/* Logo — large, prominent, centered */}
      <div className="anim-fadein" style={{ position: "relative", zIndex: 2, padding: "60px 24px 0", textAlign: "center" }}>
        <img src={logoCream} alt="Consoome logo" style={{ height: 48, objectFit: "contain", marginBottom: SP.xl }} />
        <h1 className="anim-fadeup" style={{ fontSize: 26, fontWeight: 700, color: T.cream, letterSpacing: "-0.02em", marginBottom: SP.sm }}>
          Welcome back.
        </h1>
        <p className="anim-fadeup" style={{ ...TYPE.body, color: T.darkTextSec, marginBottom: SP.xxl, animationDelay: "0.05s" }}>
          Sign in to access your Food ID.
        </p>
      </div>

      {/* Form */}
      <div className="anim-fadeup" style={{ flex: 1, padding: `0 ${SP.lg}px`, position: "relative", zIndex: 2, animationDelay: "0.1s" }}>
        {/* Email */}
        <label style={{ ...TYPE.caption, color: T.darkTextSec, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
          style={{ ...darkInputStyle, marginBottom: SP.lg }} />

        {/* Password */}
        <label style={{ ...TYPE.caption, color: T.darkTextSec, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Password</label>
        <div style={{ position: "relative", marginBottom: SP.sm }}>
          <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
            style={{ ...darkInputStyle, paddingRight: 48 }} />
          <button onClick={() => setShowPw(!showPw)} style={{
            position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)",
            background: "none", border: "none", cursor: "pointer",
            ...TYPE.bodySmall, color: T.darkTextSec, fontFamily: "Montserrat",
          }}>{showPw ? "Hide" : "Show"}</button>
        </div>
        <button onClick={handleForgotPassword} style={{
          background: "none", border: "none", cursor: "pointer",
          ...TYPE.bodySmall, color: T.darkGold, fontWeight: 600, fontFamily: "Montserrat",
          padding: 0, marginBottom: SP.xl, display: "block", textAlign: "right", width: "100%",
        }}>{resetSent ? "✓ Reset email sent!" : "Forgot password?"}</button>

        {error && <p style={{ ...TYPE.caption, color: "#FF6B6B", marginBottom: SP.md, textAlign: "center" }}>{error}</p>}

        {/* Sign In button */}
        <GoldBtn onClick={handleSignIn} disabled={!email || !password || loading}>
          {loading ? "Signing in…" : "Sign In"}
        </GoldBtn>

        <DarkDivider />

        {/* Google Sign In */}
        <button onClick={handleGoogle} style={{
          width: "100%", padding: "14px 24px", borderRadius: RAD.pill,
          border: `1px solid ${T.gold}30`, background: "rgba(255,255,255,0.06)",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          cursor: "pointer", ...TYPE.button, fontFamily: "Montserrat", color: T.cream,
          backdropFilter: "blur(8px)",
        }}>
          <GoogleIcon />
          Continue with Google
        </button>
      </div>

      {/* Bottom register link + footer */}
      <div className="anim-fadeup" style={{ padding: `${SP.xl}px ${SP.lg}px 36px`, textAlign: "center", position: "relative", zIndex: 2, animationDelay: "0.2s" }}>
        <p style={{ ...TYPE.body, color: T.darkTextSec, marginBottom: 16 }}>
          Don't have an account?{" "}
          <button onClick={onRegister} style={{
            background: "none", border: "none", cursor: "pointer",
            ...TYPE.body, color: T.darkGold, fontWeight: 700, fontFamily: "Montserrat",
            padding: 0,
          }}>Create one</button>
        </p>
        <p style={{ ...TYPE.caption, color: `${T.darkTextSec}80` }}>
          <button onClick={onPrivacy} style={{ color: T.darkTextSec, background: "none", border: "none", cursor: "pointer", fontFamily: "Montserrat", fontSize: "inherit", padding: 0 }}>Privacy Policy</button>
          {" · "}
          <button onClick={onTerms} style={{ color: T.darkTextSec, background: "none", border: "none", cursor: "pointer", fontFamily: "Montserrat", fontSize: "inherit", padding: 0 }}>Terms of Use</button>
        </p>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// SCREEN: REGISTER — name, email, password
// ─────────────────────────────────────────────────────────────
const RegisterScreen = ({ onRegister, onSignIn, onPrivacy, onTerms }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const pwMatch = password === confirmPw;
  const canSubmit = name.trim() && email.trim() && password.length >= 6 && pwMatch && !loading;

  const handleRegister = async () => {
    setError(""); setLoading(true);
    try {
      const data = await signUpWithEmail(email, password, name.trim());
      const token = data.session?.access_token;
      if (token) localStorage.setItem("sb_token", token);
      onRegister({ token, user: data.user, name: name.trim(), email });
    } catch (e) {
      setError(friendlyError(e));
    } finally { setLoading(false); }
  };

  const handleGoogle = async () => {
    setError("");
    try { await signInWithGoogle(); }
    catch (e) { setError(friendlyError(e)); }
  };

  return (
    <div style={{
      minHeight: "100vh", background: T.darkBg, display: "flex", flexDirection: "column",
      fontFamily: "'Montserrat', sans-serif", position: "relative", overflow: "hidden",
    }}>
      <DarkBgLayers />

      {/* Logo — prominent, centered */}
      <div className="anim-fadein" style={{ position: "relative", zIndex: 2, padding: "52px 24px 0", textAlign: "center" }}>
        <img src={logoCream} alt="Consoome logo" style={{ height: 30, objectFit: "contain", marginBottom: SP.lg }} />
        <h1 className="anim-fadeup" style={{ fontSize: 24, fontWeight: 700, color: T.cream, letterSpacing: "-0.02em", marginBottom: SP.sm }}>
          Create your account.
        </h1>
        <p className="anim-fadeup" style={{ ...TYPE.body, color: T.darkTextSec, marginBottom: SP.lg, animationDelay: "0.05s" }}>
          Then we'll build your personal Food ID.
        </p>
      </div>

      {/* Form */}
      <div className="anim-fadeup" style={{ flex: 1, padding: `0 ${SP.lg}px`, position: "relative", zIndex: 2, animationDelay: "0.1s" }}>
        {/* Continue with Google */}
        <button onClick={handleGoogle} style={{
          width: "100%", padding: "14px 24px", borderRadius: RAD.pill,
          border: `1px solid ${T.gold}30`, background: "rgba(255,255,255,0.06)",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          cursor: "pointer", ...TYPE.button, fontFamily: "Montserrat", color: T.cream,
          backdropFilter: "blur(8px)", marginBottom: SP.md,
        }}>
          <GoogleIcon />
          Continue with Google
        </button>

        <DarkDivider />

        {/* Name */}
        <label style={{ ...TYPE.caption, color: T.darkTextSec, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Full Name</label>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
          style={{ ...darkInputStyle, marginBottom: SP.md }} />

        {/* Email */}
        <label style={{ ...TYPE.caption, color: T.darkTextSec, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
          style={{ ...darkInputStyle, marginBottom: SP.md }} />

        {/* Password */}
        <label style={{ ...TYPE.caption, color: T.darkTextSec, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Password</label>
        <div style={{ position: "relative", marginBottom: SP.md }}>
          <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min. 6 characters"
            style={{ ...darkInputStyle, paddingRight: 48 }} />
          <button onClick={() => setShowPw(!showPw)} style={{
            position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)",
            background: "none", border: "none", cursor: "pointer",
            ...TYPE.bodySmall, color: T.darkTextSec, fontFamily: "Montserrat",
          }}>{showPw ? "Hide" : "Show"}</button>
        </div>

        {/* Confirm Password */}
        <label style={{ ...TYPE.caption, color: T.darkTextSec, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Confirm Password</label>
        <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} placeholder="Repeat password"
          style={{ ...darkInputStyle, marginBottom: SP.xs, borderColor: confirmPw && !pwMatch ? T.safetyRed : undefined }} />
        {confirmPw && !pwMatch && (
          <p style={{ ...TYPE.caption, color: T.safetyRed, marginBottom: SP.sm }}>Passwords do not match</p>
        )}
        {(!confirmPw || pwMatch) && (
          <p style={{ ...TYPE.caption, color: T.darkTextSec, marginBottom: SP.md }}>Must be at least 6 characters</p>
        )}

        {error && <p style={{ ...TYPE.caption, color: "#FF6B6B", marginBottom: SP.md, textAlign: "center" }}>{error}</p>}

        {/* Consent notice */}
        <p style={{ ...TYPE.caption, color: T.darkTextSec, lineHeight: "18px", textAlign: "center", marginBottom: SP.md }}>
          By creating an account, you agree to the{" "}
          <button onClick={onTerms} style={{ color: T.gold, background: "none", border: "none", cursor: "pointer", fontFamily: "Montserrat", fontSize: "inherit", padding: 0, textDecoration: "underline", textUnderlineOffset: 2 }}>Terms of Use</button>
          {" "}and{" "}
          <button onClick={onPrivacy} style={{ color: T.gold, background: "none", border: "none", cursor: "pointer", fontFamily: "Montserrat", fontSize: "inherit", padding: 0, textDecoration: "underline", textUnderlineOffset: 2 }}>Privacy Policy</button>
          , and consent to the processing of your account data, Food ID preferences, and any dietary or sensitivity-related information you provide for personalisation, recommendations, and matching features. Consoome only suggests possible options — it does not guarantee ingredient accuracy or allergen restrictions. Always verify with venue staff.
        </p>

        {/* Register button */}
        <GoldBtn onClick={handleRegister} disabled={!canSubmit}>
          {loading ? "Creating account…" : "Create Account"}
        </GoldBtn>
      </div>

      {/* Bottom sign-in link + footer */}
      <div className="anim-fadeup" style={{ padding: `${SP.lg}px ${SP.lg}px 36px`, textAlign: "center", position: "relative", zIndex: 2, animationDelay: "0.2s" }}>
        <p style={{ ...TYPE.body, color: T.darkTextSec, marginBottom: 12 }}>
          Already have an account?{" "}
          <button onClick={onSignIn} style={{
            background: "none", border: "none", cursor: "pointer",
            ...TYPE.body, color: T.darkGold, fontWeight: 700, fontFamily: "Montserrat",
            padding: 0,
          }}>Sign In</button>
        </p>
        <p style={{ ...TYPE.caption, color: `${T.darkTextSec}80` }}>
          <button onClick={onPrivacy} style={{ color: T.darkTextSec, background: "none", border: "none", cursor: "pointer", fontFamily: "Montserrat", fontSize: "inherit", padding: 0 }}>Privacy Policy</button>
          {" · "}
          <button onClick={onTerms} style={{ color: T.darkTextSec, background: "none", border: "none", cursor: "pointer", fontFamily: "Montserrat", fontSize: "inherit", padding: 0 }}>Terms of Use</button>
        </p>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// SCREEN: WELCOME (O1) — spec: value prop before effort
// ─────────────────────────────────────────────────────────────
const WelcomeScreen = ({ onCreateID, onSignIn }) => (
  <div style={{ minHeight: "100vh", background: T.bgPrimary, display: "flex", flexDirection: "column", position: "relative", overflow: "hidden" }}>
    {/* Hero food photo area */}
    <div style={{
      height: "42%", minHeight: 280, background: T.darkBg, position: "relative",
      overflow: "hidden",
    }}>
      {/* Warm gradient overlay */}
      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(180deg, rgba(26,26,26,0.1) 0%, rgba(26,26,26,0.6) 70%, rgba(26,26,26,0.95) 100%)",
        zIndex: 1,
      }} />
      {/* Decorative gold accent */}
      <div style={{ position: "absolute", top: -80, right: -60, width: 260, height: 260, borderRadius: "50%", background: `radial-gradient(circle, ${T.gold}18 0%, transparent 70%)`, zIndex: 0 }} />

      {/* Top nav */}
      <div className="anim-fadein" style={{ position: "relative", zIndex: 2, padding: "52px 24px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <img src={logoCream} alt="Consoome logo" style={{ height: 18, objectFit: "contain" }} />
        </div>
        <button onClick={onSignIn} style={{ ...TYPE.caption, fontWeight: 600, color: T.darkTextSec, background: "none", border: "none", cursor: "pointer", fontFamily: "Montserrat" }}>
          Sign In
        </button>
      </div>

      {/* Hero text overlay */}
      <div className="anim-fadeup" style={{ position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 2, padding: "0 24px 24px" }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: T.gold, letterSpacing: "0.12em", marginBottom: 8 }}>YOUR DINING PASSPORT</div>
      </div>
    </div>

    {/* Content area */}
    <div style={{ flex: 1, padding: `${SP.xl}px ${SP.lg}px 0`, display: "flex", flexDirection: "column" }}>
      <div className="anim-fadeup" style={{ animationDelay: "0.08s" }}>
        <h1 style={{ ...TYPE.display, color: T.textPrimary, marginBottom: SP.md }}>
          Know exactly what to order. Everywhere.
        </h1>
        <p style={{ ...TYPE.body, color: T.textSecondary, maxWidth: 320, marginBottom: SP.xl }}>
          Build your Food ID in 60 seconds. Personalized dining recommendations, dietary restrictions, and a taste profile that travels with you.
        </p>
      </div>

      {/* Feature highlights */}
      <div className="anim-fadeup" style={{ animationDelay: "0.15s", display: "flex", flexDirection: "column", gap: 10, marginBottom: SP.xl }}>
        {[
          { icon: "🛡️", text: "Dietary restrictions — always protected" },
          { icon: "🎯", text: "Dish-level matching at any restaurant" },
          { icon: "🍷", text: "Sommelier-grade wine pairing" },
          { icon: "📲", text: "Share via QR in seconds" },
        ].map((f, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 16, width: 24, textAlign: "center" }}>{f.icon}</span>
            <span style={{ ...TYPE.bodySmall, color: T.textSecondary, fontWeight: 500 }}>{f.text}</span>
          </div>
        ))}
      </div>
    </div>

    {/* CTA — spec: gold button, full-width, 48dp, radius-pill */}
    <div className="anim-fadeup" style={{ animationDelay: "0.3s", padding: `${SP.lg}px ${SP.lg}px 40px` }}>
      <GoldBtn onClick={onCreateID}>
        Get Started
      </GoldBtn>
      <div style={{ marginTop: SP.md, textAlign: "center" }}>
        <span style={{ ...TYPE.bodySmall, color: T.textSecondary }}>Already have an account? </span>
        <button onClick={onSignIn} style={{
          ...TYPE.bodySmall, color: T.gold, fontWeight: 700, fontFamily: "Montserrat",
          background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline",
        }}>
          Sign In
        </button>
      </div>
      <p style={{ ...TYPE.caption, color: T.textSecondary, textAlign: "center", marginTop: SP.sm }}>
        By continuing, you agree to our Terms of Service and Privacy Policy
      </p>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// SCREEN: ONBOARDING (O2–O8)
// ─────────────────────────────────────────────────────────────
const OnboardingScreen = ({ onComplete, displayName }) => {
  const [step, setStep] = useState(1);
  const [dir, setDir] = useState(1);
  const [formData, setFormData] = useState({
    allergens: {}, dietary: {},
    dishes: [], spice: 1, neverEat: [],
    richness: 3, adventure: 3,
    drinkChoice: null, wineStyles: [],
    occasions: [],
    milkIntolerant: false,
    milkPreference: null,
    milkAlternatives: [],
  });
  const [showCelebration, setShowCelebration] = useState(false);

  // 5 steps: Safety, Taste, Sliders, Drink, (Wine), Context, Celebration
  const hasWineStep = formData.drinkChoice === "Wine";
  const totalSteps = hasWineStep ? 6 : 5;

  const displayStep = () => {
    if (step <= 4) return step;
    if (step === 4.5) return 5;
    if (step === 5) return hasWineStep ? 6 : 5;
    return step;
  };

  const toggleAllergen = (key) => {
    if (key === "none") {
      setFormData(d => ({ ...d, allergens: d.allergens.none ? {} : { none: true } }));
      return;
    }
    setFormData(d => {
      const next = { ...d.allergens };
      delete next.none;
      next[key] = !next[key];
      if (!next[key]) delete next[key];
      return { ...d, allergens: next };
    });
  };
  const toggleDietary  = (key) => setFormData(d => ({ ...d, dietary: { ...d.dietary, [key]: !d.dietary[key] } }));
  const toggleDish     = (i) => {
    setFormData(d => {
      if (d.dishes.includes(i)) return { ...d, dishes: d.dishes.filter(x => x !== i) };
      if (d.dishes.length >= 3) {
        // Replace earliest selection (spec: gentle shake on 4th)
        return { ...d, dishes: [...d.dishes.slice(1), i] };
      }
      return { ...d, dishes: [...d.dishes, i] };
    });
  };
  const toggleNeverEat = (v) => setFormData(d => ({ ...d, neverEat: d.neverEat.includes(v) ? d.neverEat.filter(x => x !== v) : [...d.neverEat, v] }));
  const toggleOccasion = (v) => setFormData(d => ({ ...d, occasions: d.occasions.includes(v) ? d.occasions.filter(x => x !== v) : [...d.occasions, v] }));

  const goNext = () => {
    setDir(1);
    if (step === 4 && formData.drinkChoice === "Wine") { setStep(4.5); return; }
    if (step === 4.5) { setStep(5); return; }
    if (step === 4 && formData.drinkChoice !== "Wine") { setStep(5); return; }
    if (step === 5) { setShowCelebration(true); return; }
    setStep(s => s + 1);
  };

  const goBack = () => {
    setDir(-1);
    if (step === 5 && formData.drinkChoice === "Wine") { setStep(4.5); return; }
    if (step === 5 && formData.drinkChoice !== "Wine") { setStep(4); return; }
    if (step === 4.5) { setStep(4); return; }
    setStep(s => Math.max(1, s - 1));
  };

  const canContinue = () => {
    if (step === 1) return Object.keys(formData.allergens).length > 0 || Object.keys(formData.dietary).length > 0;
    if (step === 2) return formData.dishes.length === 3;
    if (step === 4) return !!formData.drinkChoice;
    return true;
  };

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const handleComplete = async () => {
    setSaving(true);
    setSaveError("");

    const profile = JSON.parse(JSON.stringify(_ONBOARDING_TEMPLATE));
    // Use actual user's name — override template default if we have one
    if (displayName && displayName !== "Guest" && displayName !== "consoome") {
      profile.display_name = displayName;
    }
    ALLERGENS.forEach(a => { if (a.key !== "none") profile.safety[a.key] = !!formData.allergens[a.key]; });
    DIETARY.forEach(d => { profile.safety[d.key] = !!formData.dietary[d.key]; });
    profile.safety.never_eat_list = formData.neverEat;
    profile.safety.milk_intolerant   = !!formData.milkIntolerant;
    profile.safety.milk_preference   = formData.milkPreference || null;
    profile.safety.milk_alternatives = formData.milkAlternatives || [];
    if (formData.dishes.length === 3) {
      const selected = formData.dishes.map(i => DISH_GRID[i]);
      profile.taste.richness_pref = Math.round(selected.reduce((s, d) => s + d.richness, 0) / 3);
      profile.taste.umami_pref = Math.round(selected.reduce((s, d) => s + d.umami, 0) / 3);
      profile.taste.spice_tolerance = formData.spice;
      const cuisines = {};
      selected.forEach(d => { cuisines[d.cuisine] = (cuisines[d.cuisine] || 0) + 2; });
      profile.cuisine.cuisine_affinities = { ...profile.cuisine.cuisine_affinities, ...cuisines };
      // Rank proteins by selection frequency (most-picked first)
      const proteins = selected.map(d => d.protein);
      const plantCount = proteins.filter(p => p === "Plant").length;
      const counts = {};
      proteins.forEach(p => { counts[p] = (counts[p] || 0) + 1; });
      if (plantCount < 2) delete counts["Plant"];
      profile.cuisine.protein_ranking = Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([p]) => p);
    }
    if (formData.drinkChoice === "Non-alcoholic") profile.beverage.drinks_alcohol = false;
    if (formData.drinkChoice === "Wine") {
      profile.beverage.wine_style_pref = formData.wineStyles.length ? formData.wineStyles : ["Red"];
    }
    if (formData.occasions.length) profile.context.default_occasions = formData.occasions;

    // Phase 1: Step 3 sliders — richness + adventure
    if (typeof formData.richness === "number") profile.taste.richness_pref = formData.richness;
    if (typeof formData.adventure === "number") profile.cuisine.novelty_pref = formData.adventure;

    // ── Confidence engine: compute real scores ──
    const { scores: confScores, completeness } = computeConfidence(formData);
    profile.completeness_score = completeness;
    profile._confidenceScores = confScores;
    profile._formData = formData; // Preserve for further refinement

    // ── Save to API (AWAITED — must succeed before navigating) ──
    try {
      const res = await fetch(`${API_BASE}/food-id/onboarding`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("sb_token"),
        },
        body: JSON.stringify({ answers: formData, scores: confScores, completeness: profile.completeness_score }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || data.message || `Save failed (${res.status})`);
      }
      if (data.food_id) profile._foodId = data.food_id;
      if (data.food_id_handle) profile.handle = data.food_id_handle;
      if (data.completeness != null) profile.completeness_score = data.completeness;
      // Always take display_name from backend — it's authoritative (uses email fallback if no name set)
      if (data.display_name) profile.display_name = data.display_name;
      onComplete(profile);
    } catch (e) {
      console.error("Onboarding save failed:", e);
      setSaveError("Couldn't save your Food ID. Check your connection and try again.");
      setSaving(false);
    }
  };

  // O8: Celebration screen
  if (showCelebration) {
    return (
      <div style={{ minHeight: "100vh", background: T.darkBg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: SP.lg, position: "relative", overflow: "hidden" }}>
        <DarkBgLayers />
        {/* Gold particle effect */}
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} style={{
            position: "absolute",
            left: `${10 + Math.random() * 80}%`,
            top: `${20 + Math.random() * 40}%`,
            width: 6, height: 6, borderRadius: "50%",
            background: T.gold,
            opacity: 0.3 + Math.random() * 0.4,
            animation: `confetti ${2 + Math.random() * 2}s ease-out ${Math.random() * 1}s infinite`,
          }} />
        ))}

        <div className="anim-scaleup" style={{ textAlign: "center", zIndex: 2 }}>
          <div style={{ fontSize: 48, marginBottom: SP.lg, color: T.gold }}>✦</div>
          <h1 style={{ ...TYPE.display, color: T.cream, marginBottom: SP.md }}>
            Your Food ID is ready.
          </h1>

          {/* Summary card — glassmorphic */}
          <div style={{
            background: `linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))`,
            border: `1px solid ${T.gold}25`, borderRadius: RAD.xl, padding: SP.lg,
            boxShadow: "0 12px 40px rgba(0,0,0,0.3)", marginTop: SP.lg, marginBottom: SP.xl,
            textAlign: "left", maxWidth: 320, width: "100%", backdropFilter: "blur(12px)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: SP.md }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: `linear-gradient(135deg, ${T.gold}, ${T.goldDark})`, display: "flex", alignItems: "center", justifyContent: "center", color: T.ink, fontSize: 20, fontWeight: 800 }}>
                {(displayName || "G").charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={{ ...TYPE.h2, color: T.cream }}>@{(displayName || "consoome").toLowerCase().replace(/\s+/g, "")}</div>
                <div style={{ ...TYPE.caption, color: T.darkTextSec }}>FOOD ID · CONSOOME</div>
              </div>
            </div>
            <div style={{ ...TYPE.bodySmall, color: T.darkTextSec, lineHeight: "22px" }}>
              {formData.dishes.length === 3 && (
                <div style={{ marginBottom: 4 }}>
                  Cuisines: {[...new Set(formData.dishes.map(i => DISH_GRID[i].cuisine))].join(" · ")}
                </div>
              )}
              {Object.keys(formData.allergens).filter(k => k !== "none" && formData.allergens[k]).length > 0 && (
                <div style={{ color: T.safetyRed, fontWeight: 600, marginBottom: 4 }}>
                  Allergies: {Object.keys(formData.allergens).filter(k => k !== "none" && formData.allergens[k]).map(k => ALLERGENS.find(a => a.key === k)?.label).join(", ")}
                </div>
              )}
              <div>Spice: {SPICE_LABELS[formData.spice]}</div>
            </div>
            <div style={{ marginTop: SP.md, display: "flex", alignItems: "center", gap: 8 }}>
              <CompletionRing pct={computeConfidence(formData).completeness} />
              <span style={{ ...TYPE.caption, color: T.darkTextSec }}>{computeConfidence(formData).completeness}% complete — it'll get smarter as you dine</span>
            </div>
          </div>

          {saveError && (
            <p style={{ ...TYPE.caption, color: "#FF6B6B", marginBottom: SP.md, textAlign: "center", maxWidth: 320 }}>
              {saveError}
            </p>
          )}
          <GoldBtn onClick={handleComplete} disabled={saving} style={{ maxWidth: 320, opacity: saving ? 0.6 : 1 }}>
            {saving ? "Saving your Food ID…" : "Start Exploring"}
          </GoldBtn>
          <button onClick={handleComplete} disabled={saving} style={{
            ...TYPE.bodySmall, fontWeight: 600, color: T.darkGold,
            background: "none", border: "none", cursor: saving ? "default" : "pointer", fontFamily: "Montserrat",
            marginTop: SP.md, opacity: saving ? 0.4 : 1,
          }}>
            Share My Food ID
          </button>
        </div>
      </div>
    );
  }

  const animClass = dir === 1 ? "anim-slidein" : "anim-slidein-left";

  return (
    <div style={{ minHeight: "100vh", background: T.darkBg, display: "flex", flexDirection: "column", position: "relative", overflow: "hidden" }}>
      <DarkBgLayers />
      {/* Header */}
      <div style={{ padding: `52px ${SP.lg}px 20px`, position: "relative", zIndex: 2 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: SP.md }}>
          <img src={logoCream} alt="Consoome logo" style={{ height: 22, objectFit: "contain" }} />
          {step > 1 && (
            <button onClick={goBack} style={{ ...TYPE.bodySmall, fontWeight: 600, color: T.darkTextSec, background: "none", border: "none", cursor: "pointer", fontFamily: "Montserrat" }}>
              ← Back
            </button>
          )}
        </div>
        <StepBar current={displayStep()} total={totalSteps} />
      </div>

      {/* Content */}
      <div key={step} className={animClass} style={{ flex: 1, padding: `4px ${SP.lg}px 0`, overflowY: "auto", position: "relative", zIndex: 2 }}>

        {/* ── O2: Safety & Allergies ── */}
        {step === 1 && (
          <>
            <StageHead title="Any food allergies or dietary restrictions?" sub="This keeps you safe. Select all that apply." />
            <SectionLabel>Allergens</SectionLabel>
            <div role="group" aria-label="Select your allergens" style={{ display: "flex", flexWrap: "wrap", gap: SP.sm, marginBottom: SP.lg }}>
              {ALLERGENS.map(a => (
                <Chip key={a.key} label={a.label} icon={a.icon}
                  variant={a.key === "none" ? "default" : "danger"}
                  selected={!!formData.allergens[a.key]}
                  onClick={() => toggleAllergen(a.key)} />
              ))}
            </div>
            <SectionLabel>Dietary Preferences</SectionLabel>
            <div role="group" aria-label="Select your dietary preferences" style={{ display: "flex", flexWrap: "wrap", gap: SP.sm, marginBottom: SP.lg }}>
              {DIETARY.map(d => (
                <Chip key={d.key} label={d.label} icon={d.icon}
                  selected={!!formData.dietary[d.key]} onClick={() => toggleDietary(d.key)} />
              ))}
            </div>
            {/* Safety disclaimer — spec: persistent, non-dismissible */}
            <div style={{ display: "flex", gap: SP.sm, padding: "12px 14px", background: `${T.safetyRed}15`, borderRadius: RAD.md, border: `1px solid ${T.safetyRed}30`, alignItems: "flex-start" }}>
              <span style={{ fontSize: 14, flexShrink: 0 }}>🛡️</span>
              <p style={{ ...TYPE.caption, color: `${T.safetyRed}DD`, fontWeight: 500, lineHeight: "16px" }}>
                Consoome helps identify potential allergens based on available data. Always confirm directly with restaurant staff before ordering.
              </p>
            </div>

            {/* MILK PREFERENCES — only if no dairy allergy */}
            {!formData.allergens.has_dairy && (
              <div style={{ marginTop: SP.lg }}>
                <SectionLabel>MILK & DAIRY PREFERENCES</SectionLabel>
                <p style={{ ...TYPE.caption, color: T.darkTextSec, marginBottom: SP.md }}>
                  Which milk do you prefer? Helps us personalise coffee, tea, and cooking.
                </p>
                {/* Lactose intolerant toggle */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", background: "rgba(255,255,255,0.05)", borderRadius: RAD.md, marginBottom: SP.sm, border: `1px solid ${T.gold}20` }}>
                  <span style={{ ...TYPE.bodySmall, fontWeight: 600, color: T.cream }}>
                    🥛 Lactose intolerant (not full allergy)
                  </span>
                  <button
                    role="switch" aria-checked={!!formData.milkIntolerant}
                    onClick={() => setFormData(d => ({ ...d, milkIntolerant: !d.milkIntolerant, milkPreference: d.milkIntolerant ? d.milkPreference : null }))}
                    style={{ width: 44, height: 24, borderRadius: 12, border: "none", cursor: "pointer", padding: 2, transition: "background 0.2s", background: formData.milkIntolerant ? T.gold : "rgba(255,255,255,0.15)", position: "relative" }}
                  >
                    <span style={{ display: "block", width: 20, height: 20, borderRadius: "50%", background: T.cream, transition: "transform 0.2s", transform: formData.milkIntolerant ? "translateX(20px)" : "translateX(0px)" }} />
                  </button>
                </div>
                {/* Milk type — only if NOT intolerant */}
                {!formData.milkIntolerant && (
                  <>
                    <div style={{ ...TYPE.caption, color: T.darkTextSec, marginBottom: SP.sm }}>Preferred cow's milk type:</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: SP.sm, marginBottom: SP.md }}>
                      {[
                        { key: "full_fat",     label: "Full Fat" },
                        { key: "semi_skimmed", label: "Semi-Skimmed" },
                        { key: "skimmed",      label: "Skimmed" },
                        { key: "none",         label: "No preference" },
                      ].map(opt => (
                        <Chip key={opt.key} label={opt.label} icon="🥛"
                          selected={formData.milkPreference === opt.key}
                          onClick={() => setFormData(d => ({ ...d, milkPreference: d.milkPreference === opt.key ? null : opt.key }))}
                        />
                      ))}
                    </div>
                  </>
                )}
                {/* Alternatives — always shown */}
                <div style={{ ...TYPE.caption, color: T.darkTextSec, marginBottom: SP.sm }}>Preferred alternatives:</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: SP.sm }}>
                  {[
                    { key: "oat",          label: "Oat",          icon: "🌾" },
                    { key: "almond",       label: "Almond",        icon: "🌰" },
                    { key: "soy",          label: "Soy",           icon: "🫘" },
                    { key: "coconut",      label: "Coconut",       icon: "🥥" },
                    { key: "rice",         label: "Rice",          icon: "🍚" },
                    { key: "lactose_free", label: "Lactose-Free",  icon: "🥛" },
                  ].map(opt => (
                    <Chip key={opt.key} label={opt.label} icon={opt.icon}
                      selected={(formData.milkAlternatives || []).includes(opt.key)}
                      onClick={() => setFormData(d => {
                        const cur = d.milkAlternatives || [];
                        return { ...d, milkAlternatives: cur.includes(opt.key) ? cur.filter(k => k !== opt.key) : [...cur, opt.key] };
                      })}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* ── O3: Dish Selection Grid ── */}
        {step === 2 && (
          <>
            <StageHead title="Pick 3 dishes you'd order right now." sub="Tap the ones that make you hungry." />
            <div style={{ display: "flex", alignItems: "center", gap: SP.sm, marginBottom: SP.md, padding: "8px 12px", background: `${T.gold}15`, borderRadius: RAD.pill, border: `1px solid ${T.gold}25` }}>
              <span style={{ fontSize: 13, color: T.gold }}>✦</span>
              <span style={{ ...TYPE.caption, color: T.gold, fontWeight: 600 }}>
                {formData.dishes.length} of 3 selected
              </span>
            </div>
            {/* 3-column grid — compact tiles ~120px height */}
            <div role="group" aria-label="Pick 3 dishes" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: SP.sm }}>
              {DISH_GRID.map((d, i) => {
                const sel = formData.dishes.includes(i);
                return (
                  <button key={i} onClick={() => toggleDish(i)} aria-label={`${d.label}${sel ? ", selected" : ""}`} aria-pressed={sel} style={{
                    position: "relative", borderRadius: RAD.md, overflow: "hidden",
                    border: sel ? `2px solid ${T.gold}` : `1px solid ${T.gold}20`,
                    background: "rgba(255,255,255,0.04)", cursor: "pointer", fontFamily: "Montserrat",
                    transition: "all 0.2s ease", height: 120,
                    transform: sel ? "scale(1.02)" : "scale(1)",
                  }}>
                    {/* Emoji fallback as background */}
                    <div style={{
                      position: "absolute", inset: 0,
                      background: sel ? `${T.gold}18` : "rgba(255,255,255,0.03)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 32, opacity: 0.85,
                    }}>
                      {d.icon}
                    </div>
                    {/* Dark gradient for text */}
                    <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "55%", background: "linear-gradient(transparent, rgba(26,26,26,0.85))" }} />
                    {/* Label */}
                    <div style={{ position: "absolute", bottom: 6, left: 6, right: 6 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: T.white, lineHeight: "13px" }}>{d.label}</span>
                    </div>
                    {/* Selected badge */}
                    {sel && (
                      <div style={{
                        position: "absolute", top: 6, right: 6,
                        width: 20, height: 20, borderRadius: "50%",
                        background: T.gold, display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <span style={{ fontSize: 11, color: T.ink, fontWeight: 800 }}>✓</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* ── O4: Taste Sliders — Spice + Richness + Adventure ── */}
        {step === 3 && (() => {
          const richnessVal = formData.richness ?? 3;
          const adventureVal = formData.adventure ?? 3;
          return (
          <>
            <StageHead title="Dial in your taste." sub="Three quick sliders — this shapes every recommendation." />
            <SectionLabel>Spice Tolerance</SectionLabel>
            <div style={{ marginBottom: SP.lg }}>
              <div style={{ padding: "20px", background: "rgba(255,255,255,0.05)", borderRadius: RAD.xl, border: `1px solid ${T.gold}20`, backdropFilter: "blur(8px)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 32, minWidth: 40, textAlign: "center" }}>
                    {["🌶", "🌶🌶", "🔥", "🔥🔥", "💀"][formData.spice]}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ ...TYPE.h2, color: T.cream, marginBottom: 8 }}>
                      {SPICE_LABELS[formData.spice]}
                    </div>
                    <input type="range" min={0} max={4} value={formData.spice}
                      aria-label="Spice tolerance" aria-valuemin={0} aria-valuemax={4} aria-valuenow={formData.spice} aria-valuetext={SPICE_LABELS[formData.spice]}
                      onChange={e => setFormData(d => ({ ...d, spice: +e.target.value }))}
                      style={{ background: `linear-gradient(to right, ${T.gold} 0%, ${T.gold} ${formData.spice * 25}%, ${T.gold}20 ${formData.spice * 25}%, ${T.gold}20 100%)` }}
                    />
                    <div style={{ display: "flex", justifyContent: "space-between", ...TYPE.overline, color: T.darkTextSec, marginTop: 4 }}>
                      <span>None</span><span>Volcanic</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <SectionLabel>Richness Preference</SectionLabel>
            <div style={{ marginBottom: SP.lg }}>
              <div style={{ padding: "20px", background: "rgba(255,255,255,0.05)", borderRadius: RAD.xl, border: `1px solid ${T.gold}20`, backdropFilter: "blur(8px)" }}>
                <input type="range" min={1} max={5} value={richnessVal}
                  aria-label="Richness preference" aria-valuemin={1} aria-valuemax={5} aria-valuenow={richnessVal}
                  onChange={e => setFormData(d => ({ ...d, richness: +e.target.value }))}
                  style={{ background: `linear-gradient(to right, ${T.gold} 0%, ${T.gold} ${(richnessVal - 1) * 25}%, ${T.gold}20 ${(richnessVal - 1) * 25}%, ${T.gold}20 100%)`, marginBottom: 8 }}
                />
                <div style={{ display: "flex", justifyContent: "space-between", ...TYPE.overline, color: T.darkTextSec }}>
                  <span>Light & Clean</span><span>Rich & Indulgent</span>
                </div>
              </div>
            </div>

            <SectionLabel>Adventure Level</SectionLabel>
            <div style={{ marginBottom: SP.lg }}>
              <div style={{ padding: "20px", background: "rgba(255,255,255,0.05)", borderRadius: RAD.xl, border: `1px solid ${T.gold}20`, backdropFilter: "blur(8px)" }}>
                <input type="range" min={1} max={5} value={adventureVal}
                  aria-label="Adventure level" aria-valuemin={1} aria-valuemax={5} aria-valuenow={adventureVal}
                  onChange={e => setFormData(d => ({ ...d, adventure: +e.target.value }))}
                  style={{ background: `linear-gradient(to right, ${T.gold} 0%, ${T.gold} ${(adventureVal - 1) * 25}%, ${T.gold}20 ${(adventureVal - 1) * 25}%, ${T.gold}20 100%)`, marginBottom: 8 }}
                />
                <div style={{ display: "flex", justifyContent: "space-between", ...TYPE.overline, color: T.darkTextSec }}>
                  <span>Comfort Food</span><span>Try Anything</span>
                </div>
              </div>
            </div>
          </>
          );
        })()}

        {/* ── O5: Drink Signal ── */}
        {step === 4 && (
          <>
            <StageHead title="What do you like to drink with dinner?" sub="This helps us pair beverages with your meals." />
            <div role="radiogroup" aria-label="Drink preference" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: SP.md }}>
              {DRINK_OPTIONS.map(o => {
                const sel = formData.drinkChoice === o.value;
                return (
                  <button key={o.value} onClick={() => setFormData(d => ({ ...d, drinkChoice: o.value }))} role="radio" aria-checked={sel} aria-label={o.label} style={{
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
                    padding: "16px 8px", borderRadius: RAD.lg,
                    border: `1px solid ${sel ? T.gold : `${T.gold}20`}`,
                    background: sel ? `${T.gold}18` : "rgba(255,255,255,0.04)",
                    cursor: "pointer", fontFamily: "Montserrat", transition: "all 0.2s",
                    backdropFilter: "blur(8px)",
                  }}>
                    <span style={{ fontSize: 28 }}>{o.icon}</span>
                    <span style={{ ...TYPE.caption, fontWeight: 600, color: sel ? T.gold : T.darkTextSec }}>{o.label}</span>
                  </button>
                );
              })}
            </div>
            {/* Conditional wine follow-up */}
            {formData.drinkChoice === "Wine" && (
              <div className="anim-fadein" style={{ marginTop: SP.md }}>
                <p style={{ ...TYPE.bodySmall, fontWeight: 600, color: T.cream, marginBottom: SP.md }}>What style do you usually go for?</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: SP.sm }}>
                  {WINE_STYLES.map(s => (
                    <Chip key={s} label={s} variant="gold"
                      selected={formData.wineStyles.includes(s)}
                      onClick={() => setFormData(d => ({
                        ...d, wineStyles: d.wineStyles.includes(s) ? d.wineStyles.filter(x => x !== s) : [...d.wineStyles, s]
                      }))} />
                  ))}
                </div>
              </div>
            )}
            <button onClick={goNext} style={{ ...TYPE.caption, fontWeight: 500, color: T.darkTextSec, background: "none", border: "none", cursor: "pointer", fontFamily: "Montserrat", marginTop: SP.lg }}>
              Skip for now — we'll learn as you go
            </button>
          </>
        )}

        {/* ── O5 Wine depth (4.5) ── */}
        {step === 4.5 && (
          <>
            <StageHead title="How do you like your wine?" sub="One slider tells us a lot." />
            <div style={{ padding: "10px 14px", background: `${T.gold}15`, borderRadius: RAD.pill, marginBottom: SP.lg, display: "flex", gap: 8, alignItems: "center", border: `1px solid ${T.gold}25` }}>
              <span style={{ fontSize: 13, color: T.gold }}>✦</span>
              <span style={{ ...TYPE.caption, color: T.gold, fontWeight: 600 }}>This replaces 5 separate wine preference questions.</span>
            </div>
            <SectionLabel>Body & Richness</SectionLabel>
            <div style={{ padding: "20px", background: "rgba(255,255,255,0.05)", borderRadius: RAD.xl, border: `1px solid ${T.gold}20`, marginBottom: SP.md, backdropFilter: "blur(8px)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", ...TYPE.overline, color: T.darkTextSec, marginBottom: 14 }}>
                <span>LIGHT & CRISP</span><span>BOLD & RICH</span>
              </div>
              <input type="range" min={0} max={5} value={formData.wineBody || 3}
                aria-label="Wine body preference" aria-valuemin={0} aria-valuemax={5} aria-valuenow={formData.wineBody || 3} aria-valuetext={BODY_LABELS[formData.wineBody || 3]}
                onChange={e => setFormData(d => ({ ...d, wineBody: +e.target.value }))}
                style={{ background: `linear-gradient(to right, ${T.gold} 0%, ${T.gold} ${(formData.wineBody || 3) * 20}%, ${T.gold}20 ${(formData.wineBody || 3) * 20}%, ${T.gold}20 100%)`, marginBottom: 12 }}
              />
              <div style={{ textAlign: "center", ...TYPE.h2, color: T.cream }}>
                {BODY_LABELS[formData.wineBody || 3]}
              </div>
            </div>
            <p style={{ ...TYPE.bodySmall, color: T.darkTextSec, fontStyle: "italic", lineHeight: "20px" }}>
              We'll infer tannin level, acidity, oak preference, and regional affinity from this.
            </p>
          </>
        )}

        {/* ── O6: Context Signal ── */}
        {step === 5 && (
          <>
            <StageHead title="When you eat out, what's the usual occasion?" sub="Pick all that apply. This helps us match the mood." />
            <div role="group" aria-label="Select dining occasions" style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              {OCCASIONS.map(o => {
                const sel = formData.occasions.includes(o.key);
                return (
                  <button key={o.key} onClick={() => toggleOccasion(o.key)} aria-pressed={sel} style={{
                    display: "flex", alignItems: "center", gap: SP.md, padding: "15px 18px", borderRadius: RAD.lg,
                    border: `1px solid ${sel ? T.gold : `${T.gold}20`}`, background: sel ? `${T.gold}18` : "rgba(255,255,255,0.04)",
                    cursor: "pointer", fontFamily: "Montserrat", transition: "all 0.2s",
                    backdropFilter: "blur(8px)",
                  }}>
                    <span style={{ fontSize: 24, flexShrink: 0 }}>{o.icon}</span>
                    <span style={{ ...TYPE.body, fontWeight: 600, color: sel ? T.gold : `${T.cream}CC` }}>{o.label}</span>
                    {sel && <span style={{ marginLeft: "auto", fontSize: 14, color: T.gold }}>✓</span>}
                  </button>
                );
              })}
            </div>
            <button onClick={goNext} style={{ ...TYPE.caption, fontWeight: 500, color: T.darkTextSec, background: "none", border: "none", cursor: "pointer", fontFamily: "Montserrat", marginTop: SP.md }}>
              Skip for now
            </button>
          </>
        )}

      </div>

      {/* Footer CTA */}
      <div style={{ padding: `20px ${SP.lg}px 40px`, position: "relative", zIndex: 2 }}>
        <GoldBtn onClick={goNext} disabled={!canContinue()}>
          {step === 5 ? "Create my Food ID" : "Continue"}
        </GoldBtn>
      </div>
    </div>
  );
};

// Swipe Card component for O7
const SwipeCard = ({ card, index, total, onSwipe }) => {
  const [offset, setOffset] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [hintVisible, setHintVisible] = useState(index === 0);
  const startX = useRef(0);
  const startY = useRef(0);
  const directionDecided = useRef(false);
  const isHorizontal = useRef(false);

  useEffect(() => {
    if (index === 0 && hintVisible) {
      const t = setTimeout(() => setHintVisible(false), 4000);
      return () => clearTimeout(t);
    }
    if (index > 0) setHintVisible(false);
  }, [index, hintVisible]);

  // Reset exiting state when card index changes
  useEffect(() => { setExiting(false); setOffset(0); }, [index]);

  const handleStart = (x, y) => {
    startX.current = x;
    startY.current = y || 0;
    directionDecided.current = false;
    isHorizontal.current = false;
    setSwiping(true);
    if (hintVisible) setHintVisible(false);
  };
  const handleMove = (x, y, e) => {
    if (!swiping || exiting) return;
    const dx = x - startX.current;
    const dy = y - (startY.current || 0);
    // Decide direction on first significant movement (>10px)
    if (!directionDecided.current && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
      directionDecided.current = true;
      isHorizontal.current = Math.abs(dx) > Math.abs(dy);
    }
    if (directionDecided.current && isHorizontal.current) {
      if (e) e.preventDefault(); // Suppress scroll during horizontal swipe
      setOffset(dx);
    }
    // If vertical, do nothing — let the browser scroll naturally
  };
  const handleEnd = () => {
    if (exiting) return;
    setSwiping(false);
    if (isHorizontal.current && Math.abs(offset) > 80) {
      onSwipe(offset > 0);
    }
    setOffset(0);
    directionDecided.current = false;
    isHorizontal.current = false;
  };

  // Button click: animate card off-screen then fire onSwipe
  const handleButtonSwipe = (liked) => {
    if (exiting) return;
    setExiting(true);
    setSwiping(false);
    setOffset(liked ? 400 : -400);
    setTimeout(() => onSwipe(liked), 280);
  };

  const rot = offset * 0.08;
  const likeOp = Math.min(offset / 120, 1);
  const skipOp = Math.min(-offset / 120, 1);

  return (
    <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}>
      {/* Swipe instruction hint — first card only, auto-fades */}
      {index === 0 && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: SP.lg,
          marginBottom: SP.md, padding: "10px 20px", borderRadius: RAD.pill,
          background: `${T.gold}12`, border: `1px solid ${T.gold}20`,
          opacity: hintVisible ? 1 : 0, transition: "opacity 0.6s ease",
          pointerEvents: "none",
        }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6, ...TYPE.caption, color: T.darkTextSec, fontWeight: 500 }}>
            <span style={{ fontSize: 16 }}>←</span> Not for me
          </span>
          <span style={{ width: 1, height: 16, background: `${T.darkTextSec}40` }} />
          <span style={{ display: "flex", alignItems: "center", gap: 6, ...TYPE.caption, color: T.gold, fontWeight: 600 }}>
            Love it <span style={{ fontSize: 16 }}>→</span>
          </span>
        </div>
      )}
      <div
        role="group" aria-label={`${card.name} — swipe right to like, left to skip`} tabIndex={0}
        onKeyDown={(e) => { if (e.key === "ArrowRight") { e.preventDefault(); handleButtonSwipe(true); } if (e.key === "ArrowLeft") { e.preventDefault(); handleButtonSwipe(false); } }}
        onMouseDown={(e) => handleStart(e.clientX, e.clientY)}
        onMouseMove={(e) => handleMove(e.clientX, e.clientY)}
        onMouseUp={handleEnd}
        onMouseLeave={() => { if (swiping) handleEnd(); }}
        onTouchStart={(e) => handleStart(e.touches[0].clientX, e.touches[0].clientY)}
        onTouchMove={(e) => handleMove(e.touches[0].clientX, e.touches[0].clientY, e)}
        onTouchEnd={handleEnd}
        style={{
          width: "100%", maxWidth: 300, height: 220, borderRadius: RAD.xl,
          background: `linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))`,
          border: `1px solid ${T.gold}25`,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          cursor: "grab", userSelect: "none", WebkitUserSelect: "none", position: "relative",
          touchAction: "pan-y", /* Allow vertical scroll, we handle horizontal */
          transform: `translateX(${offset}px) rotate(${rot}deg)`,
          transition: swiping ? "none" : "all 0.3s ease",
          boxShadow: `0 8px 32px rgba(0,0,0,0.3)`,
          backdropFilter: "blur(12px)",
          opacity: exiting ? 0 : 1,
        }}
      >
        {/* Like/Skip indicators */}
        {likeOp > 0 && (
          <div style={{ position: "absolute", top: 16, left: 16, padding: "6px 16px", borderRadius: RAD.pill, border: `2px solid ${T.safetyGreen}`, color: T.safetyGreen, fontWeight: 700, fontSize: 14, opacity: likeOp }}>
            LOVE
          </div>
        )}
        {skipOp > 0 && (
          <div style={{ position: "absolute", top: 16, right: 16, padding: "6px 16px", borderRadius: RAD.pill, border: `2px solid ${T.safetyRed}`, color: T.safetyRed, fontWeight: 700, fontSize: 14, opacity: skipOp }}>
            SKIP
          </div>
        )}

        <span style={{ fontSize: 56, marginBottom: SP.md }}>{card.img}</span>
        <span style={{ ...TYPE.h2, color: T.cream }}>{card.name}</span>
        {card.sub && (
          <span style={{ ...TYPE.bodySmall, color: T.darkTextSec, textAlign: "center", maxWidth: 220, marginTop: SP.xs, padding: `0 ${SP.md}px` }}>
            {card.sub}
          </span>
        )}
      </div>
      {/* Like / Dislike buttons */}
      <div style={{ display: "flex", justifyContent: "center", gap: 40, marginTop: SP.md }}>
        <button onClick={() => handleButtonSwipe(false)} disabled={exiting} aria-label="Not for me" style={{
          width: 52, height: 52, borderRadius: "50%", border: `2px solid ${T.safetyRed}`,
          background: `${T.safetyRed}15`, color: T.safetyRed, fontSize: 22, fontWeight: 700,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", fontFamily: "Montserrat", transition: "all 0.2s",
        }}>✕</button>
        <button onClick={() => handleButtonSwipe(true)} disabled={exiting} aria-label="Love it" style={{
          width: 52, height: 52, borderRadius: "50%", border: `2px solid ${T.safetyGreen}`,
          background: `${T.safetyGreen}15`, color: T.safetyGreen, fontSize: 22, fontWeight: 700,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", fontFamily: "Montserrat", transition: "all 0.2s",
        }}>✓</button>
      </div>
      <div style={{ ...TYPE.caption, color: T.darkTextSec, marginTop: SP.sm }}>
        {index + 1} of {total}
      </div>
    </div>
  );
};

const StageHead = ({ title, sub }) => (
  <div style={{ marginBottom: SP.lg }}>
    <h2 style={{ ...TYPE.h1, color: T.cream, marginBottom: SP.sm }}>{title}</h2>
    {sub && <p style={{ ...TYPE.body, color: T.darkTextSec }}>{sub}</p>}
  </div>
);

// ─────────────────────────────────────────────────────────────
// SCREEN: HOME (H1) — spec: search-first, personalized greeting
// ─────────────────────────────────────────────────────────────
const CUISINE_ICONS = { steakhouse: "🥩", japanese: "🍣", seafood: "🐠", british: "🍽️", italian: "🍝", indian: "🍛", chinese: "🥡", french: "🇫🇷", mexican: "🌮", thai: "🍜", mediterranean: "🫒", arabic: "🧆" };
const guessCuisineIcon = (name) => {
  const lower = (name || "").toLowerCase();
  for (const [k, v] of Object.entries(CUISINE_ICONS)) { if (lower.includes(k)) return v; }
  return "🍽️";
};

// ─── Skeleton shimmer for venue cards ─────────────────────────
const ShimmerCard = () => (
  <div style={{
    flexShrink: 0, width: 270, borderRadius: RAD.md, overflow: "hidden",
    background: "rgba(255,255,255,0.05)", border: `1px solid ${T.gold}15`,
  }}>
    <div style={{ height: 130, background: `linear-gradient(90deg, rgba(255,255,255,0.03) 25%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.03) 75%)`, backgroundSize: "200% 100%", animation: "shimmer 1.5s infinite" }} />
    <div style={{ padding: SP.md }}>
      <div style={{ height: 14, width: "70%", borderRadius: 4, background: "rgba(255,255,255,0.08)", marginBottom: 8, animation: "shimmer 1.5s infinite" }} />
      <div style={{ height: 11, width: "50%", borderRadius: 4, background: "rgba(255,255,255,0.05)", animation: "shimmer 1.5s infinite" }} />
    </div>
  </div>
);

const OUTLET_FILTERS = [
  { key: "", label: "All" },
  { key: "fine_dining", label: "Fine Dining" },
  { key: "casual", label: "Casual" },
  { key: "hotel_bar", label: "Hotel Bar" },
  { key: "cafe", label: "Café" },
];

const OUTLET_LABELS = { fine_dining: "Fine Dining", casual: "Casual", hotel_bar: "Hotel Bar", cafe: "Café" };

const formatRelativeTime = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
};

const HomeScreen = ({ profile, onVenue, onHotel, onScan, onShare, onProfile }) => {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMatchScores, setHasMatchScores] = useState(false);
  const [selectedHotelName, setSelectedHotelName] = useState(null);
  const [hotelPickerOpen, setHotelPickerOpen] = useState(false);
  const [locationFilter, setLocationFilter] = useState("all");
  const [locationSearch, setLocationSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [scansRemaining, setScansRemaining] = useState(null);
  const [nudgeDismissed, setNudgeDismissed] = useState(() => localStorage.getItem("consoome_nudge_dismissed") === "true");
  const [dismissedCards, setDismissedCards] = useState(new Set());
  const [lastScan, setLastScan] = useState(null);
  const [showScanNudge, setShowScanNudge] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [modalFeedback, setModalFeedback] = useState({});
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 200);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Close search on click outside
  useEffect(() => {
    if (!searchFocused) return;
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [searchFocused]);

  // Close search on Escape
  useEffect(() => {
    if (!searchFocused) return;
    const handler = (e) => { if (e.key === "Escape") { setSearchFocused(false); searchInputRef.current?.blur(); } };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [searchFocused]);

  const dedupeVenues = (items) => {
    const norm = (n) => (n || "").toLowerCase().trim()
      .replace(/\s+/g, " ")
      .replace(/\b(hotel|resort|the|&|jumeirah|by)\b/gi, "")
      .replace(/[^\w\s]/g, "")
      .trim();
    const seen = new Map();
    return items.filter((item) => {
      const key = norm(item.hotel_name || item.hotel || "") + "|" + norm(item.restaurant_name || item.name || "");
      if (seen.has(key)) return false;
      seen.set(key, true);
      return true;
    });
  };

  const mapVenue = (v) => ({
    id: v.venue_id,
    venue_id: v.venue_id,
    name: v.restaurant_name,
    hotel: v.hotel_name,
    city: v.city || "",
    cuisine: v.cuisine_type || (v.city ? `${v.city}` : ""),
    outletType: OUTLET_LABELS[v.outlet_type] || v.outlet_type || (v.scrape_status === "enriched" ? "Fine Dining" : "Restaurant"),
    outlet_type_raw: v.outlet_type || "",
    icon: guessCuisineIcon(v.restaurant_name),
    matchScore: v.match_score != null ? Math.round(v.match_score * 100) : null,
    greatMatches: v.great_matches || v.dish_count,
    reasons: v.reasons || [],
    description: `${v.restaurant_name} at ${v.hotel_name}`,
    priceRange: "",
    dishes: [],
    dish_count: v.dish_count,
    star_rating: v.star_rating,
    location_type: v.location_type || "hotel",
  });

  const fetchVenues = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("sb_token");
      const foodId = profile._foodId;

      if (token && foodId) {
        try {
          const res = await fetch(`${API_BASE}/venues/recommended`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            if (data.venues && data.venues.length > 0) {
              setVenues(dedupeVenues(data.venues).map(mapVenue));
              setHasMatchScores(true);
              setLoading(false);
              return;
            }
          }
        } catch (_) { /* fall through */ }
      }

      try {
        const res = await fetch(`${API_BASE}/venues/popular`);
        if (res.ok) {
          const data = await res.json();
          if (data.venues && data.venues.length > 0) {
            setVenues(dedupeVenues(data.venues).map(mapVenue));
            setHasMatchScores(false);
            setLoading(false);
            return;
          }
        }
      } catch (_) { /* fall through */ }

      const res = await fetch(`${API_BASE}/venues`);
      if (!res.ok) throw new Error("Failed to load venues");
      const data = await res.json();
      setVenues(dedupeVenues(data.venues || []).map(mapVenue));
      setHasMatchScores(false);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVenues(); }, []); // eslint-disable-line

  useEffect(() => {
    const token = localStorage.getItem('sb_token');
    if (!token) return;
    fetch(`${API_BASE}/scan/history`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => { if (data.history?.length > 0) setLastScan(data.history[0]); })
      .catch(() => {});
  }, []);

  // Scan nudge: show if last_scan_at is > 5 days ago
  useEffect(() => {
    if (!profile?.last_scan_at) return;
    const daysSince = (Date.now() - new Date(profile.last_scan_at).getTime()) / 86400000;
    if (daysSince > 5) setShowScanNudge(true);
  }, [profile]);

  // Post-dining modal: show if last scan was 1-12h ago and has no feedback
  useEffect(() => {
    if (!lastScan) return;
    const hoursSince = (Date.now() - new Date(lastScan.created_at).getTime()) / 3600000;
    if (hoursSince >= 1 && hoursSince <= 12 && !lastScan.has_feedback) setShowFeedbackModal(true);
  }, [lastScan]);

  // Fetch scan quota for CTA
  useEffect(() => {
    try {
      const token = localStorage.getItem("sb_token");
      if (!token) return;
      const payload = JSON.parse(atob(token.split(".")[1]));
      const uid = payload.sub;
      if (!uid) return;
      fetch(`${API_BASE}/scan/quota/${uid}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.ok ? r.json() : null).then(data => {
        if (data) setScansRemaining(data.scans_remaining);
      }).catch(() => {});
    } catch (_) {}
  }, []);

  // --- Derived data ---
  const firstName = (profile.display_name || "").split(" ")[0];
  const isNewUser = !profile._foodId;
  const completeness = profile.completeness_score || 0;

  // Safety strip data
  const safetyAllergens = ALLERGENS.filter(a => a.key !== "none" && profile.safety?.[a.key]);
  const safetyDietary = DIETARY.filter(d => profile.safety?.[d.key]);

  // Group venues by hotel (case-insensitive to avoid "Burj Al Arab" vs "Burj AL Arab" dupes)
  const hotelMap = {};
  const hotelKeyMap = {}; // normalized key → display name (first seen)
  venues.forEach(v => {
    const raw = v.hotel || "Other";
    const normKey = raw.toLowerCase().trim();
    if (!hotelKeyMap[normKey]) hotelKeyMap[normKey] = raw;
    const displayName = hotelKeyMap[normKey];
    if (!hotelMap[normKey]) hotelMap[normKey] = { hotel_name: displayName, city: v.city || "", star_rating: v.star_rating, location_type: v.location_type || "hotel", outlets: [], bestScore: null, totalDishes: 0 };
    hotelMap[normKey].outlets.push(v);
    hotelMap[normKey].totalDishes += (v.dish_count || 0);
    if (v.matchScore != null && (hotelMap[normKey].bestScore === null || v.matchScore > hotelMap[normKey].bestScore)) {
      hotelMap[normKey].bestScore = v.matchScore;
    }
  });
  const hotels = Object.values(hotelMap).sort((a, b) => (b.bestScore || 0) - (a.bestScore || 0) || b.totalDishes - a.totalDishes);

  // Auto-select: single hotel → that one; multiple → first (best match); otherwise null
  const autoHotel = hotels.length >= 1 ? hotels[0].hotel_name : null;
  const activeHotelName = selectedHotelName || autoHotel;
  const activeHotel = hotels.find(h => h.hotel_name === activeHotelName) || null;
  const hotelVenues = activeHotel
    ? [...activeHotel.outlets].sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0))
    : [];

  // Search filtering
  const searchResults = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    if (!q) return [];
    return venues
      .filter(v =>
        (v.name || "").toLowerCase().includes(q) ||
        (v.cuisine || "").toLowerCase().includes(q) ||
        (v.hotel || "").toLowerCase().includes(q)
      )
      .sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0));
  }, [debouncedQuery, venues]);
  const showSearchResults = searchFocused && debouncedQuery.trim().length > 0;

  // Date formatting for greeting
  const greetingDate = useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
  }, []);

  // Small completion ring (36px)
  const SmallRing = ({ pct }) => {
    const r = 14, circ = 2 * Math.PI * r;
    return (
      <svg width="36" height="36" viewBox="0 0 36 36" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`Profile completeness ${pct} percent`} tabIndex={0} style={{ flexShrink: 0, cursor: "pointer" }} onClick={onProfile} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onProfile(); } }}>
        <circle cx="18" cy="18" r={r} fill="none" stroke={`${T.cream}15`} strokeWidth="3" />
        <circle cx="18" cy="18" r={r} fill="none" stroke={T.gold} strokeWidth="3"
          strokeDasharray={`${circ * pct / 100} ${circ}`}
          strokeLinecap="round" transform="rotate(-90 18 18)"
          style={{ transition: "stroke-dasharray 1s cubic-bezier(0.22,1,0.36,1)" }}
        />
        <text x="18" y="22" textAnchor="middle" fill={T.cream} fontSize="9" fontWeight="800" fontFamily="Montserrat">
          {pct}%
        </text>
      </svg>
    );
  };

  return (
    <div style={{ minHeight: "100%", background: T.darkBg, paddingBottom: SP.lg, position: "relative" }}>
      <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
      <DarkBgLayers />

      {/* Safety Strip — always first, below status bar */}
      <button onClick={onProfile} style={{
        width: "100%", minHeight: 44, display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: `52px ${SP.lg}px 0`, paddingBottom: SP.sm, background: T.darkCard,
        borderBottom: `1px solid ${T.darkBorder}`,
        position: "relative", zIndex: 2, cursor: "pointer", border: "none", fontFamily: "Montserrat",
      }}>
        {(safetyAllergens.length > 0 || safetyDietary.length > 0) ? (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: SP.xs }}>
              {safetyAllergens.map(a => (
                <span key={a.key} title={a.label} style={{ fontSize: 16 }}>{a.icon}</span>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: SP.xs }}>
              {safetyDietary.map(d => (
                <span key={d.key} style={{
                  ...TYPE.caption, color: T.cream, fontWeight: 600,
                  padding: "2px 8px", borderRadius: RAD.pill,
                  background: `${T.gold}20`, border: `1px solid ${T.gold}30`,
                }}>{d.label}</span>
              ))}
            </div>
          </>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: SP.sm, width: "100%" }}>
            <span style={{ fontSize: 14 }}>🛡️</span>
            <span style={{ ...TYPE.bodySmall, color: T.darkTextSec, flex: 1 }}>Set up your dietary profile</span>
            <span style={{ ...TYPE.bodySmall, color: T.gold }}>→</span>
          </div>
        )}
      </button>

      {/* Offline banner */}
      {!navigator.onLine && (
        <div style={{
          padding: `${SP.sm}px ${SP.lg}px`,
          background: T.darkCard, borderBottom: `1px solid ${T.darkBorder}`,
          display: "flex", alignItems: "center", gap: SP.sm,
          position: "relative", zIndex: 2,
        }}>
          <span style={{ fontSize: 14 }}>📡</span>
          <span style={{ ...TYPE.caption, color: T.darkTextSec }}>You're offline — your Food ID is still available</span>
        </div>
      )}

      {/* Greeting bar */}
      <div className="anim-fadein" style={{
        padding: `${SP.md}px ${SP.lg}px ${SP.xs}px`,
        position: "relative", zIndex: 1,
        display: "flex", alignItems: "flex-start", justifyContent: "space-between",
      }}>
        <div>
          <div style={{ fontFamily: "Montserrat", fontSize: 22, fontWeight: 600, color: T.cream, lineHeight: "28px" }}>
            {getGreeting()}{firstName ? `, ${firstName}` : ""}
          </div>
          <div style={{ fontFamily: "Montserrat", fontSize: 13, fontWeight: 300, color: T.darkTextSec, marginTop: 4 }}>
            {greetingDate}
          </div>
        </div>
        <SmallRing pct={completeness} />
      </div>

      {/* Context-aware smart card */}
      {!isNewUser && (() => {
        const meal = getMealPeriod();
        const cards = [];
        if (meal && scansRemaining !== 0) cards.push({ key: "meal", gold: true, svgIcon: "camera", title: `Time for ${meal}?`, subtitle: "Scan a menu for instant recommendations", action: onScan, credits: scansRemaining });
        if (completeness < 70) cards.push({ key: "refine", gold: false, svgIcon: "sliders", title: "Quick refine", subtitle: "Answer 3 questions to improve your matches", action: onProfile });
        if (scansRemaining === 0) cards.push({ key: "nocredits", gold: false, svgIcon: "bar-chart", title: "Scan credits used", subtitle: "They'll refresh soon — browse venues instead", action: null });
        cards.push({ key: "default", gold: false, svgIcon: "compass", title: "Discover your next great meal", subtitle: "Search venues matched to your Food ID", action: null });
        const card = cards.find(c => !dismissedCards.has(c.key));
        if (!card) return null;
        const isGold = card.gold;
        return (
          <div className="anim-fadeup" style={{ padding: `${SP.sm}px ${SP.lg}px ${SP.md}px`, position: "relative", zIndex: 1 }}>
            <button className="ctx-card" onClick={card.action || (() => searchInputRef.current?.focus())} style={{
              width: "100%", minHeight: 100, borderRadius: RAD.lg, position: "relative",
              background: isGold ? `linear-gradient(135deg, ${T.gold} 0%, ${T.goldDark || "#A68A3E"} 100%)` : T.darkCard,
              border: isGold ? "none" : `1px solid ${T.gold}20`,
              cursor: "pointer", fontFamily: "Montserrat", textAlign: "left",
              display: "flex", alignItems: "center", gap: SP.md,
              padding: `${SP.md}px ${SP.lg}px`, overflow: "hidden",
              boxShadow: isGold ? `0 4px 20px ${T.gold}40` : "none",
              transition: "transform 0.15s ease-out",
            }}>
              <span style={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}><UiIcon name={card.svgIcon} size={28} color={isGold ? "#fff" : T.gold} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ ...TYPE.h2, color: isGold ? "#fff" : T.cream }}>{card.title}</div>
                <div style={{ ...TYPE.bodySmall, color: isGold ? "rgba(255,255,255,0.85)" : T.darkTextSec, marginTop: 2 }}>
                  {card.subtitle}
                </div>
              </div>
              {card.credits != null && card.credits > 0 && (
                <span style={{ ...TYPE.caption, color: isGold ? "rgba(255,255,255,0.7)" : T.darkTextSec, flexShrink: 0, alignSelf: "flex-end", marginBottom: SP.xs }}>
                  {card.credits} scan{card.credits !== 1 ? "s" : ""} left
                </span>
              )}
            </button>
            {/* Dismiss button */}
            <button onClick={(e) => { e.stopPropagation(); setDismissedCards(prev => new Set([...prev, card.key])); }} style={{
              position: "absolute", top: SP.sm + 8, right: SP.lg + 8,
              background: "none", border: "none", cursor: "pointer",
              ...TYPE.caption, color: isGold ? "rgba(255,255,255,0.5)" : T.darkTextSec,
              padding: 4, lineHeight: 1, fontSize: 14,
            }}>✕</button>
          </div>
        );
      })()}

      {/* Search bar */}
      {!loading && venues.length > 0 && (
        <div ref={searchRef} className="anim-fadein" style={{ padding: `${SP.sm}px ${SP.lg}px ${SP.md}px`, position: "relative", zIndex: 10 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: SP.sm,
            height: 48, borderRadius: RAD.pill,
            background: T.darkCard, border: `1px solid ${searchFocused ? T.gold + "50" : T.gold + "20"}`,
            padding: `0 ${SP.md}px`,
            transition: "border-color 0.2s ease",
          }}>
            <span style={{ flexShrink: 0, opacity: 0.6, display: "flex", alignItems: "center" }}><UiIcon name="search" size={16} color={T.darkTextSec} /></span>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              placeholder="Search venues, cuisines, dishes..."
              style={{
                flex: 1, background: "none", border: "none", outline: "none",
                fontFamily: "Montserrat", fontSize: 15, fontWeight: 400,
                color: T.cream, lineHeight: "22px",
              }}
            />
            {searchQuery && (
              <button onClick={() => { setSearchQuery(""); setDebouncedQuery(""); searchInputRef.current?.focus(); }} style={{
                background: "none", border: "none", cursor: "pointer", padding: 4,
                fontSize: 16, color: T.darkTextSec, fontFamily: "Montserrat", lineHeight: 1,
              }}>✕</button>
            )}
          </div>

          {/* Search results dropdown */}
          {showSearchResults && (
            <div style={{
              position: "absolute", left: SP.lg, right: SP.lg, top: 60,
              background: T.darkCard, borderRadius: RAD.md, border: `1px solid ${T.gold}25`,
              maxHeight: 360, overflowY: "auto",
              boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
              zIndex: 20,
            }}>
              {searchResults.length === 0 ? (
                <div style={{ padding: `${SP.lg}px`, textAlign: "center" }}>
                  <p style={{ ...TYPE.body, color: T.darkTextSec }}>No venues match '{debouncedQuery}'</p>
                </div>
              ) : (
                searchResults.map(v => (
                  <button key={v.id || v.name} onClick={() => { setSearchFocused(false); setSearchQuery(""); onVenue(v); }} style={{
                    width: "100%", display: "flex", alignItems: "center", gap: SP.md,
                    padding: `${SP.md}px ${SP.lg}px`,
                    background: "none", border: "none", borderBottom: `1px solid ${T.gold}10`,
                    cursor: "pointer", fontFamily: "Montserrat", textAlign: "left",
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: RAD.sm, background: `${T.gold}10`,
                      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0,
                    }}>
                      {v.icon}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ ...TYPE.bodySmall, fontWeight: 700, color: T.cream, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {v.name}
                      </div>
                      <div style={{ ...TYPE.caption, color: T.darkTextSec }}>
                        {v.cuisine}{v.hotel ? ` · ${v.hotel}` : ""}
                      </div>
                    </div>
                    {v.matchScore != null && (
                      <MatchBadge score={v.matchScore} size="sm" />
                    )}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ padding: `${SP.lg}px ${SP.lg}px`, position: "relative", zIndex: 1 }}>
          <div style={{
            height: 72, borderRadius: RAD.md, marginBottom: SP.md,
            background: "linear-gradient(90deg, rgba(255,255,255,0.03) 25%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.03) 75%)",
            backgroundSize: "200% 100%", animation: "shimmer 1.5s infinite",
          }} />
          {[1, 2, 3].map(i => (
            <div key={i} style={{
              height: 68, borderRadius: RAD.md, marginBottom: SP.sm,
              background: "linear-gradient(90deg, rgba(255,255,255,0.03) 25%, rgba(255,255,255,0.06) 50%, rgba(255,255,255,0.03) 75%)",
              backgroundSize: "200% 100%", animation: "shimmer 1.5s infinite",
              animationDelay: `${i * 0.1}s`,
            }} />
          ))}
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div style={{ padding: `${SP.xxl}px ${SP.lg}px`, textAlign: "center", position: "relative", zIndex: 1 }}>
          <div style={{ fontSize: 40, marginBottom: SP.md }}>:/</div>
          <p style={{ ...TYPE.body, color: T.cream, fontWeight: 600, marginBottom: SP.sm }}>Couldn't load recommendations</p>
          <p style={{ ...TYPE.caption, color: T.darkTextSec, marginBottom: SP.lg }}>Tap below to try again.</p>
          <GoldBtn onClick={fetchVenues} small fullWidth={false}>Retry</GoldBtn>
        </div>
      )}

      {/* Main content */}
      {!loading && !error && (
        <div style={{ position: "relative", zIndex: 1 }}>

          {/* Scan nudge banner (Finding #8) */}
          {showScanNudge && (
            <div style={{
              background: 'rgba(200,169,81,0.1)', border: '1px solid rgba(200,169,81,0.25)',
              borderRadius: 12, padding: '12px 16px',
              margin: `${SP.md}px ${SP.lg}px 0`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ fontSize: 13, color: T.cream, flex: 1, fontFamily: 'Montserrat' }}>
                ✦ Your Food ID improves with every scan. Ready to try?
              </div>
              <div style={{ display: 'flex', gap: 8, marginLeft: 12 }}>
                <button onClick={onScan} style={{
                  background: T.gold, color: T.darkBg, border: 'none', borderRadius: 16,
                  padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'Montserrat',
                }}>Scan</button>
                <button onClick={() => setShowScanNudge(false)} style={{
                  background: 'none', color: T.darkTextSec, border: 'none', fontSize: 18, cursor: 'pointer', lineHeight: 1,
                }}>×</button>
              </div>
            </div>
          )}

          {/* Last Scan card */}
          {lastScan && (
            <div
              style={{
                background: 'rgba(200,169,81,0.08)',
                border: '1px solid rgba(200,169,81,0.2)',
                borderRadius: 16, padding: '14px 18px',
                margin: `${SP.md}px ${SP.lg}px 0`,
                cursor: 'pointer',
              }}
              onClick={onScan}
            >
              <div style={{ fontSize: 11, color: T.gold, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 4, fontFamily: 'Montserrat' }}>LAST SCAN</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.cream, fontFamily: 'Montserrat' }}>{lastScan.venue_name}</div>
              <div style={{ fontSize: 12, color: T.darkTextSec, marginTop: 2, fontFamily: 'Montserrat' }}>
                {lastScan.matched_count} match{lastScan.matched_count !== 1 ? 'es' : ''} · {lastScan.created_at ? formatRelativeTime(lastScan.created_at) : ''}
              </div>
            </div>
          )}

          {/* Empty state — no venues matched */}
          {(isNewUser || venues.length === 0) && (
            <div className="anim-fadeup" style={{ padding: `${SP.xl}px ${SP.lg}px`, textAlign: "center" }}>
              <div style={{
                width: 80, height: 80, borderRadius: "50%", background: `${T.gold}15`,
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: `0 auto ${SP.lg}px`, fontSize: 40,
              }}>📸</div>
              <div style={{ ...TYPE.h2, color: T.cream, marginBottom: SP.sm }}>No restaurants matched yet</div>
              <p style={{ ...TYPE.bodySmall, color: T.darkTextSec, marginBottom: SP.lg, maxWidth: 280, margin: `0 auto ${SP.lg}px` }}>
                Scan a menu or search to discover venues that match your Food ID
              </p>
              <button onClick={onScan} style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "12px 28px", borderRadius: RAD.pill,
                background: T.gold, color: T.ink, border: "none", cursor: "pointer",
                ...TYPE.button, fontFamily: "Montserrat",
              }}>
                Scan a Menu
              </button>
            </div>
          )}

          {/* Hotel selected (or auto-selected) — restaurant list */}
          {!isNewUser && venues.length > 0 && activeHotel && (
            <>
              {/* Location context bar */}
              <button
                onClick={() => setHotelPickerOpen(true)}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: SP.sm,
                  padding: `${SP.sm}px ${SP.lg}px`,
                  background: `${T.darkCard}E0`, borderBottom: `1px solid ${T.gold}15`,
                  cursor: "pointer",
                  fontFamily: "Montserrat", textAlign: "left",
                  backdropFilter: "blur(12px)", border: "none",
                }}
              >
                <span style={{ fontSize: 16 }}>{getLocationIcon(activeHotel.location_type)}</span>
                <div style={{
                  ...TYPE.caption, fontWeight: 700, color: T.cream, flex: 1,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  textTransform: "uppercase", letterSpacing: "1px",
                }}>
                  {activeHotel.hotel_name}
                  {activeHotel.city && <span style={{ fontWeight: 400, opacity: 0.6, letterSpacing: "0.5px" }}>{" \u00B7 "}{activeHotel.city}</span>}
                </div>
                <span style={{ color: T.darkTextSec, fontSize: 16, flexShrink: 0 }}>{"\u203A"}</span>
              </button>

              {/* Section title */}
              <div style={{ padding: `${SP.md}px ${SP.lg}px 0`, paddingBottom: 0 }}>
                <SectionLabel>{getVenueSubtitle()}</SectionLabel>
              </div>

              {/* Tier-grouped restaurant list */}
              {(() => {
                // Group venues into 4 tiers for better distinction
                const topMatches = hotelVenues.filter(v => v.matchScore != null && v.matchScore >= 80);
                const greatMatches = hotelVenues.filter(v => v.matchScore != null && v.matchScore >= 73 && v.matchScore < 80);
                const goodOptions = hotelVenues.filter(v => v.matchScore != null && v.matchScore >= 68 && v.matchScore < 73);
                const alsoAvailable = hotelVenues.filter(v => v.matchScore == null || v.matchScore < 68);
                // If no match scores, show all as one flat list
                const hasScores = hasMatchScores && hotelVenues.some(v => v.matchScore != null);
                const groups = hasScores
                  ? [
                    { label: "TOP MATCHES", venues: topMatches, accent: "top" },
                    { label: "GREAT MATCHES", venues: greatMatches, accent: "great" },
                    { label: "GOOD OPTIONS", venues: goodOptions, accent: false },
                    { label: "ALSO AVAILABLE", venues: alsoAvailable, accent: false },
                  ].filter(g => g.venues.length > 0)
                  : [{ label: "RESTAURANTS", venues: hotelVenues, accent: false }];

                const VenueRow = ({ v, isPromoted, isTopTier }) => (
                  <button onClick={() => onVenue(v)} style={{
                    width: "100%", display: "flex", alignItems: "center", gap: SP.md,
                    padding: isPromoted ? `${SP.lg}px` : `${SP.md}px ${SP.lg}px`,
                    borderRadius: RAD.md,
                    background: isPromoted
                      ? `linear-gradient(135deg, ${T.gold}12 0%, ${T.gold}06 100%)`
                      : isTopTier ? `rgba(255,255,255,0.04)` : "rgba(255,255,255,0.03)",
                    border: `1px solid ${isPromoted ? T.gold + "40" : isTopTier ? T.gold + "18" : T.gold + "10"}`,
                    cursor: "pointer", fontFamily: "Montserrat", textAlign: "left",
                    marginBottom: SP.sm, transition: "all 0.15s ease",
                    boxShadow: isPromoted ? `0 2px 12px ${T.gold}15` : "none",
                  }}>
                    <div style={{
                      width: isPromoted ? 48 : 40, height: isPromoted ? 48 : 40,
                      borderRadius: RAD.sm, background: `${T.gold}10`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: isPromoted ? 24 : 20, flexShrink: 0,
                    }}>
                      {v.icon}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {isPromoted && (
                        <div style={{ ...TYPE.caption, color: T.gold, fontWeight: 700, fontSize: 9, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 3 }}>
                          Best match for you
                        </div>
                      )}
                      <div style={{
                        ...(isPromoted ? TYPE.h2 : TYPE.bodySmall), fontWeight: 700, color: T.cream,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {v.name}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
                        <span style={{ ...TYPE.caption, color: T.darkTextSec }}>{v.cuisine}</span>
                        {v.outletType && (
                          <span style={{
                            ...TYPE.caption, color: T.darkTextSec, fontSize: 9,
                            padding: "1px 6px", borderRadius: RAD.pill, background: `${T.cream}08`,
                          }}>
                            {v.outletType}
                          </span>
                        )}
                      </div>
                      {isPromoted && v.reasons && v.reasons[0] && (
                        <div style={{ ...TYPE.bodySmall, color: T.darkTextSec, marginTop: 6, lineHeight: "18px" }}>
                          {v.reasons[0]}
                        </div>
                      )}
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      {v.matchScore != null ? (
                        <MatchBadge score={v.matchScore} size={isPromoted ? "lg" : "sm"} />
                      ) : (
                        <span style={{ ...TYPE.caption, color: T.darkTextSec }}>{v.dish_count || 0} dishes</span>
                      )}
                    </div>
                  </button>
                );

                const VENUE_CAP = 3;

                const TierSection = ({ g, gi, groupCount }) => {
                  const [expanded, setExpanded] = useState(false);
                  const capped = !expanded && g.venues.length > VENUE_CAP;
                  const visible = capped ? g.venues.slice(0, VENUE_CAP) : g.venues;
                  const remaining = g.venues.length - VENUE_CAP;
                  const isTopGroup = g.accent === "top";
                  return (
                    <div style={{ marginBottom: gi < groupCount - 1 ? SP.md : 0 }}>
                      <div style={{ paddingLeft: SP.lg, paddingRight: SP.lg }}>
                        <SectionLabel>{g.label} ({g.venues.length})</SectionLabel>
                      </div>
                      {visible.map((v, vi) => (
                        <VenueRow key={v.id || v.name} v={v} isPromoted={isTopGroup && vi === 0} isTopTier={isTopGroup} />
                      ))}
                      {capped && (
                        <button onClick={() => setExpanded(true)} style={{
                          width: "100%", padding: `${SP.sm}px 0`, marginTop: 2,
                          background: "none", border: "none",
                          cursor: "pointer", fontFamily: "Montserrat",
                          ...TYPE.bodySmall, color: T.gold, fontWeight: 600, textAlign: "center",
                        }}>
                          View all {g.venues.length} restaurants {"\u2193"}
                        </button>
                      )}
                    </div>
                  );
                };

                return (
                  <div className="anim-fadeup" style={{ padding: `${SP.md}px 0`, animationDelay: "0.08s" }}>
                    {groups.map((g, gi) => (
                      <TierSection key={g.label} g={g} gi={gi} groupCount={groups.length} />
                    ))}
                  </div>
                );
              })()}

              {/* View full location details link */}
              <div className="anim-fadein" style={{ padding: `0 ${SP.lg}px ${SP.sm}px`, paddingLeft: SP.lg, animationDelay: "0.12s" }}>
                <button onClick={() => onHotel(activeHotel)} style={{
                  width: "100%", padding: `${SP.sm}px 0`,
                  background: "none", border: "none", cursor: "pointer",
                  fontFamily: "Montserrat", ...TYPE.bodySmall, color: T.gold, fontWeight: 600, textAlign: "center",
                }}>
                  {activeHotel.location_type === "mall" ? "View all mall restaurants"
                    : activeHotel.location_type === "airport" ? "View all terminal restaurants"
                    : activeHotel.location_type === "standalone" ? "View restaurant details"
                    : "View full hotel details"}
                </button>
              </div>
            </>
          )}

          {/* Completeness nudge — between venues and quick actions */}
          {completeness < 80 && !isNewUser && !nudgeDismissed && (
            <div className="anim-fadeup" style={{ padding: `${SP.md}px ${SP.lg}px 0`, animationDelay: "0.15s" }}>
              <button onClick={onProfile} style={{
                width: "100%", padding: `${SP.md}px ${SP.lg}px`,
                borderRadius: RAD.md, background: T.cream + "08",
                borderLeft: `3px solid ${T.gold}`, border: `1px solid ${T.gold}20`,
                borderLeftWidth: 3, borderLeftColor: T.gold,
                cursor: "pointer", fontFamily: "Montserrat", textAlign: "left",
                display: "flex", alignItems: "center", gap: SP.md, position: "relative",
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ ...TYPE.bodySmall, color: T.cream, fontWeight: 600, marginBottom: 2 }}>
                    Your Food ID is {completeness}% complete — refine it for better matches
                  </div>
                  <div style={{ ...TYPE.caption, color: T.darkTextSec }}>
                    Tap to improve your recommendations
                  </div>
                </div>
                <span style={{ color: T.gold, fontSize: 16 }}>→</span>
                <span onClick={(e) => { e.stopPropagation(); setNudgeDismissed(true); localStorage.setItem("consoome_nudge_dismissed", "true"); }} style={{
                  position: "absolute", top: 4, right: 8,
                  background: "none", border: "none", cursor: "pointer",
                  ...TYPE.caption, color: T.darkTextSec, padding: 4,
                }}>✕</span>
              </button>
            </div>
          )}

          {/* Quick Actions */}
          {!isNewUser && (
            <div className="anim-fadeup" style={{ padding: `${SP.lg}px ${SP.lg}px ${SP.md}px`, animationDelay: "0.18s" }}>
              <div style={{ display: "flex", justifyContent: "space-evenly" }}>
                {[
                  { svgIcon: "share", label: "Share Food ID", action: onShare },
                  { svgIcon: "edit", label: "Refine Profile", action: onProfile },
                  { svgIcon: "user", label: "View Full Profile", action: onProfile },
                ].map((a, i) => (
                  <button key={i} onClick={a.action} style={{
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                    padding: 0, background: "none", border: "none",
                    cursor: "pointer", fontFamily: "Montserrat",
                  }}>
                    <div
                      onPointerDown={(e) => { e.currentTarget.style.borderColor = T.gold; }}
                      onPointerUp={(e) => { e.currentTarget.style.borderColor = "transparent"; }}
                      onPointerLeave={(e) => { e.currentTarget.style.borderColor = "transparent"; }}
                      style={{
                      width: 72, height: 72, borderRadius: "50%",
                      background: T.darkCard,
                      border: "2px solid transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "border-color 0.15s ease",
                    }}>
                      <UiIcon name={a.svgIcon} size={26} color={T.gold} />
                    </div>
                    <span style={{ ...TYPE.caption, color: T.darkTextSec, fontWeight: 600, textAlign: "center", maxWidth: 72 }}>{a.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Bottom padding for tab bar clearance */}
          <div style={{ height: 90 }} />
        </div>
      )}

      {/* Location picker overlay */}
      {hotelPickerOpen && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
          zIndex: 200, display: "flex", flexDirection: "column", justifyContent: "flex-end",
          backdropFilter: "blur(4px)",
        }} onClick={() => { setHotelPickerOpen(false); setLocationSearch(""); setLocationFilter("all"); }}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="anim-fadeup"
            style={{
              background: T.darkBg, borderRadius: `${RAD.xl}px ${RAD.xl}px 0 0`,
              padding: `${SP.lg}px ${SP.lg}px ${SP.xxl}px`,
              maxHeight: "85vh", overflowY: "auto",
              border: `1px solid ${T.gold}15`, borderBottom: "none",
            }}
          >
            {/* Drag handle */}
            <div style={{
              width: 36, height: 4, borderRadius: 2,
              background: `${T.cream}20`, margin: `0 auto ${SP.md}px`,
            }} />

            {/* Header with close button */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: SP.md }}>
              <div style={{ fontFamily: "Montserrat", fontSize: 18, fontWeight: 700, color: T.cream }}>
                Where are you dining?
              </div>
              <button onClick={() => { setHotelPickerOpen(false); setLocationSearch(""); setLocationFilter("all"); }} style={{
                width: 32, height: 32, borderRadius: "50%", background: `${T.cream}10`,
                border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16, color: T.darkTextSec,
              }}>{"\u2715"}</button>
            </div>

            {/* Search input */}
            <input
              type="text"
              value={locationSearch}
              onChange={(e) => setLocationSearch(e.target.value)}
              placeholder="Search hotels, malls, airports\u2026"
              autoFocus
              style={{
                width: "100%", padding: `${SP.sm + 2}px ${SP.md}px`,
                borderRadius: RAD.md, background: `${T.cream}08`,
                border: `1px solid ${T.gold}20`, color: T.cream,
                fontFamily: "Montserrat", fontSize: 14, outline: "none",
                marginBottom: SP.md, boxSizing: "border-box",
              }}
              onFocus={(e) => e.target.style.borderColor = T.gold + "60"}
              onBlur={(e) => e.target.style.borderColor = T.gold + "20"}
            />

            {/* Filter chips */}
            <div style={{ display: "flex", gap: SP.sm, marginBottom: SP.lg, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              {LOCATION_TYPE_FILTERS.map(f => {
                const isActive = locationFilter === f.key;
                return (
                  <button key={f.key} onClick={() => setLocationFilter(f.key)} style={{
                    padding: `${SP.xs + 2}px ${SP.md}px`, borderRadius: RAD.pill,
                    background: isActive ? T.gold : "transparent",
                    border: `1px solid ${isActive ? T.gold : T.gold + "30"}`,
                    color: isActive ? T.darkBg : T.cream,
                    fontFamily: "Montserrat", fontSize: 12, fontWeight: 600,
                    cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.15s ease",
                    flexShrink: 0,
                  }}>
                    {f.icon ? `${f.icon} ${f.label}` : f.label}
                  </button>
                );
              })}
            </div>

            {/* Results */}
            {(() => {
              const lq = locationSearch.trim().toLowerCase();
              let filtered = hotels;
              if (locationFilter !== "all") {
                filtered = filtered.filter(h => h.location_type === locationFilter);
              }
              if (lq) {
                filtered = filtered.filter(h =>
                  h.hotel_name.toLowerCase().includes(lq) ||
                  (h.city || "").toLowerCase().includes(lq)
                );
              }

              if (filtered.length === 0) {
                return (
                  <div style={{ padding: `${SP.xl}px 0`, textAlign: "center" }}>
                    <div style={{ fontSize: 32, marginBottom: SP.sm }}>{"\u{1F50D}"}</div>
                    <div style={{ ...TYPE.bodySmall, color: T.darkTextSec }}>No locations found</div>
                  </div>
                );
              }

              // Group by city if multiple cities
              const cities = [...new Set(filtered.map(h => h.city || ""))];
              const groupByCity = cities.length > 1;

              if (groupByCity) {
                return cities.map(city => {
                  const cityHotels = filtered.filter(h => (h.city || "") === city);
                  return (
                    <div key={city} style={{ marginBottom: SP.md }}>
                      <div style={{
                        ...TYPE.caption, fontWeight: 700, color: T.darkTextSec,
                        textTransform: "uppercase", letterSpacing: "1.5px",
                        padding: `${SP.xs}px 0`, marginBottom: SP.xs,
                      }}>{city || "Unknown"}</div>
                      {cityHotels.map(h => {
                        const isSelected = h.hotel_name === activeHotelName;
                        const venueCount = h.outlets.length;
                        return (
                          <button key={h.hotel_name} onClick={() => { setSelectedHotelName(h.hotel_name); setHotelPickerOpen(false); setLocationSearch(""); setLocationFilter("all"); }} style={{
                            width: "100%", display: "flex", alignItems: "center", gap: SP.md,
                            padding: `${SP.md}px ${SP.lg}px`, borderRadius: RAD.md,
                            background: isSelected ? `${T.gold}15` : "rgba(255,255,255,0.04)",
                            border: `1px solid ${isSelected ? T.gold : T.gold + "15"}`,
                            cursor: "pointer", fontFamily: "Montserrat", textAlign: "left",
                            marginBottom: SP.sm, transition: "all 0.2s ease",
                          }}>
                            <div style={{
                              width: 44, height: 44, borderRadius: RAD.sm, background: `${T.gold}10`,
                              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0,
                            }}>{getLocationIcon(h.location_type)}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ ...TYPE.bodySmall, fontWeight: 700, color: T.cream, marginBottom: 2 }}>{h.hotel_name}</div>
                              <div style={{ ...TYPE.caption, color: T.darkTextSec }}>
                                {venueCount} restaurant{venueCount !== 1 ? "s" : ""}
                              </div>
                            </div>
                            {h.bestScore != null && (
                              <div style={{ flexShrink: 0 }}><MatchBadge score={h.bestScore} /></div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  );
                });
              }

              return filtered.map(h => {
                const isSelected = h.hotel_name === activeHotelName;
                const venueCount = h.outlets.length;
                return (
                  <button key={h.hotel_name} onClick={() => { setSelectedHotelName(h.hotel_name); setHotelPickerOpen(false); setLocationSearch(""); setLocationFilter("all"); }} style={{
                    width: "100%", display: "flex", alignItems: "center", gap: SP.md,
                    padding: `${SP.md}px ${SP.lg}px`, borderRadius: RAD.md,
                    background: isSelected ? `${T.gold}15` : "rgba(255,255,255,0.04)",
                    border: `1px solid ${isSelected ? T.gold : T.gold + "15"}`,
                    cursor: "pointer", fontFamily: "Montserrat", textAlign: "left",
                    marginBottom: SP.sm, transition: "all 0.2s ease",
                  }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: RAD.sm, background: `${T.gold}10`,
                      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0,
                    }}>{getLocationIcon(h.location_type)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ ...TYPE.bodySmall, fontWeight: 700, color: T.cream, marginBottom: 2 }}>{h.hotel_name}</div>
                      <div style={{ ...TYPE.caption, color: T.darkTextSec }}>
                        {h.city && `${h.city} \u00B7 `}{venueCount} restaurant{venueCount !== 1 ? "s" : ""}
                      </div>
                    </div>
                    {h.bestScore != null && (
                      <div style={{ flexShrink: 0 }}><MatchBadge score={h.bestScore} /></div>
                    )}
                  </button>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* Post-dining feedback modal (Finding #9) */}
      {showFeedbackModal && lastScan?.results_json && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9998, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: '#242424', borderRadius: '24px 24px 0 0', padding: '24px 20px 40px', width: '100%', maxWidth: 430, margin: '0 auto' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.cream, fontFamily: 'Montserrat', marginBottom: 6 }}>How was dinner?</div>
            <div style={{ fontSize: 13, color: T.darkTextSec, marginBottom: 20, fontFamily: 'Montserrat' }}>
              Did you try anything from {lastScan.venue_name}?
            </div>
            {lastScan.results_json.slice(0, 3).map(dish => (
              <div key={dish.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <div style={{ fontSize: 14, color: T.cream, flex: 1, fontFamily: 'Montserrat' }}>{dish.name}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['up', 'down'].map(r => (
                    <button key={r}
                      onClick={() => setModalFeedback(prev => ({ ...prev, [dish.id]: r }))}
                      style={{
                        background: modalFeedback[dish.id] === r ? T.gold : 'rgba(255,255,255,0.08)',
                        border: 'none', borderRadius: 20, padding: '5px 14px',
                        color: modalFeedback[dish.id] === r ? T.darkBg : T.darkTextSec,
                        cursor: 'pointer', fontSize: 13, fontFamily: 'Montserrat',
                      }}
                    >{r === 'up' ? '👍' : '👎'}</button>
                  ))}
                </div>
              </div>
            ))}
            <button
              onClick={async () => {
                const token = localStorage.getItem('sb_token');
                for (const [dishId, rating] of Object.entries(modalFeedback)) {
                  const dish = lastScan.results_json.find(d => String(d.id) === String(dishId));
                  await fetch(`${API_BASE}/scan/dish-feedback`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ scan_id: lastScan.session_id, dish_id: parseInt(dishId), dish_name: dish?.name, rating }),
                  }).catch(() => {});
                }
                setShowFeedbackModal(false);
              }}
              style={{ width: '100%', background: T.gold, color: T.darkBg, border: 'none', borderRadius: 24, padding: '14px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'Montserrat', marginTop: 8 }}
            >Done</button>
            <button onClick={() => setShowFeedbackModal(false)} style={{ width: '100%', background: 'none', border: 'none', color: T.darkTextSec, fontSize: 13, cursor: 'pointer', marginTop: 12, fontFamily: 'Montserrat' }}>Skip</button>
          </div>
        </div>
      )}
    </div>
  );
};

const VenueCard = ({ venue, onClick }) => {
  const tier = getMatchTier(venue.matchScore);
  return (
    <button onClick={onClick} style={{
      flexShrink: 0, width: 270, background: "rgba(255,255,255,0.05)", borderRadius: RAD.md,
      border: `1px solid ${T.gold}20`, overflow: "hidden", cursor: "pointer",
      fontFamily: "Montserrat", textAlign: "left",
      boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
      backdropFilter: "blur(8px)",
    }}>
      {/* Photo placeholder */}
      <div style={{ height: 120, background: `${T.gold}0A`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 40, position: "relative" }}>
        {venue.icon}
        {venue.matchScore != null && (
          <div style={{ position: "absolute", top: 8, right: 8 }}>
            <MatchBadge score={venue.matchScore} />
          </div>
        )}
      </div>
      <div style={{ padding: SP.md }}>
        <div style={{ ...TYPE.bodySmall, fontWeight: 700, color: T.cream, marginBottom: 2 }}>{venue.name}</div>
        <div style={{ ...TYPE.caption, color: T.darkTextSec }}>{venue.hotel || venue.cuisine}</div>
        {venue.consoomeScore && <div style={{ marginTop: 4 }}><ConsoomeScoreBadge score={venue.consoomeScore} /></div>}
        <div style={{ ...TYPE.caption, color: T.gold, fontWeight: 600, marginTop: 4 }}>
          {venue.dish_count || venue.greatMatches} dish{(venue.dish_count || venue.greatMatches) !== 1 ? "es" : ""}
        </div>
      </div>
    </button>
  );
};

const VenueListItem = ({ venue, onClick }) => (
  <button onClick={onClick} style={{
    display: "flex", alignItems: "center", gap: SP.md, width: "100%",
    padding: `${SP.md}px 0`, borderBottom: `1px solid ${T.gold}15`,
    background: "none", border: "none", borderBottom: `1px solid ${T.gold}15`,
    cursor: "pointer", fontFamily: "Montserrat", textAlign: "left",
  }}>
    <div style={{ width: 44, height: 44, borderRadius: RAD.md, background: "rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>
      {venue.icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ ...TYPE.bodySmall, fontWeight: 700, color: T.cream }}>{venue.hotel}</div>
      <div style={{ ...TYPE.caption, color: T.cream, opacity: 0.8 }}>{venue.name}</div>
      <div style={{ ...TYPE.caption, color: T.darkTextSec, fontSize: 10 }}>{venue.cuisine}</div>
    </div>
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
      {venue.matchScore != null ? <MatchBadge score={venue.matchScore} /> : (
        <span style={{ ...TYPE.caption, color: T.gold, fontWeight: 600 }}>{venue.dish_count || 0} dishes</span>
      )}
      {venue.outletType && (
        <span style={{ ...TYPE.caption, color: T.darkTextSec, padding: "2px 8px", borderRadius: RAD.pill, background: `${T.cream}08`, fontSize: 9 }}>
          {venue.outletType}
        </span>
      )}
    </div>
  </button>
);

// ─────────────────────────────────────────────────────────────
// SCREEN: HOTEL DETAIL (V2 — Hotel Property View)
// ─────────────────────────────────────────────────────────────
const HotelDetailScreen = ({ hotel, profile, onBack, onOutlet }) => {
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hotelMeta, setHotelMeta] = useState({ city: hotel.city, star_rating: hotel.star_rating });

  useEffect(() => {
    const fetchOutlets = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("sb_token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await fetch(`${API_BASE}/hotels/${encodeURIComponent(hotel.hotel_name)}/outlets`, { headers });
        if (!res.ok) throw new Error("Failed to load outlets");
        const data = await res.json();
        setOutlets(data.outlets || []);
        setHotelMeta({ city: data.city || hotel.city, star_rating: data.star_rating || hotel.star_rating });
      } catch (e) {
        setError(friendlyError(e));
      } finally {
        setLoading(false);
      }
    };
    fetchOutlets();
  }, [hotel]);

  const starDisplay = hotelMeta.star_rating ? "★".repeat(hotelMeta.star_rating) : "";

  return (
    <div style={{ minHeight: "100%", background: T.darkBg, paddingBottom: 40, position: "relative" }}>
      <DarkBgLayers />
      {/* Hero */}
      <div style={{ height: 180, background: `${T.gold}0A`, position: "relative", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 56, zIndex: 1 }}>
        🏨
        <button onClick={onBack} style={{
          position: "absolute", top: 48, left: SP.lg,
          ...TYPE.bodySmall, fontWeight: 600, color: T.darkTextSec,
          background: "rgba(255,255,255,0.06)", border: `1px solid ${T.gold}20`, borderRadius: RAD.pill,
          padding: "8px 14px", cursor: "pointer", fontFamily: "Montserrat",
          backdropFilter: "blur(8px)",
        }}>
          ← Back
        </button>
      </div>

      {/* Hotel info */}
      <div style={{ padding: SP.lg, position: "relative", zIndex: 1 }}>
        {starDisplay && <div style={{ ...TYPE.caption, color: T.gold, marginBottom: 4, letterSpacing: 2 }}>{starDisplay}</div>}
        <h1 style={{ ...TYPE.display, color: T.cream, marginBottom: 4 }}>{hotel.hotel_name}</h1>
        <div style={{ ...TYPE.bodySmall, color: T.darkTextSec, marginBottom: SP.md }}>{hotelMeta.city || ""}</div>
        <SectionLabel>{outlets.length} Restaurant{outlets.length !== 1 ? "s" : ""} & Bar{outlets.length !== 1 ? "s" : ""}</SectionLabel>
      </div>

      {/* Outlet list */}
      <div style={{ padding: `0 ${SP.lg}px`, position: "relative", zIndex: 1 }}>
        {loading ? (
          <div style={{ padding: SP.xl, textAlign: "center" }}>
            <p style={{ ...TYPE.body, color: T.darkTextSec }}>Loading outlets...</p>
          </div>
        ) : error ? (
          <div style={{ padding: SP.xl, textAlign: "center" }}>
            <p style={{ ...TYPE.body, color: T.cream }}>{error}</p>
            <GoldBtn onClick={() => window.location.reload()} small>Retry</GoldBtn>
          </div>
        ) : outlets.length === 0 ? (
          <div style={{ padding: SP.xl, textAlign: "center" }}>
            <p style={{ ...TYPE.body, color: T.darkTextSec }}>No restaurants found for this hotel yet.</p>
          </div>
        ) : outlets.map((outlet, i) => {
          const matchPct = outlet.match_score != null ? Math.round(outlet.match_score * 100) : null;
          const cuisineLabel = (outlet.cuisine_types || []).join(" · ") || "";
          const outletLabel = OUTLET_LABELS[outlet.outlet_type] || outlet.outlet_type || "";
          const noDishes = outlet.dish_count === 0;

          return (
            <button key={outlet.venue_id || i} onClick={() => !noDishes && onOutlet({
              id: outlet.venue_id,
              venue_id: outlet.venue_id,
              name: outlet.restaurant_name,
              hotel: hotel.hotel_name,
              cuisine: cuisineLabel,
              outletType: outletLabel,
              icon: guessCuisineIcon(outlet.restaurant_name),
              matchScore: matchPct,
              greatMatches: outlet.great_matches,
              description: `${outlet.restaurant_name} at ${hotel.hotel_name}`,
              priceRange: "",
              dishes: [],
              dish_count: outlet.dish_count,
              reasons: [],
            })} style={{
              display: "flex", alignItems: "center", gap: 12, width: "100%",
              padding: `${SP.md}px 0`, background: "none", border: "none", borderBottom: `1px solid ${T.gold}10`,
              cursor: noDishes ? "default" : "pointer", textAlign: "left", fontFamily: "Montserrat",
              opacity: noDishes ? 0.5 : 1,
            }}>
              {/* Icon */}
              <div style={{
                width: 44, height: 44, borderRadius: RAD.sm, background: `${T.gold}10`,
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0,
              }}>
                {guessCuisineIcon(outlet.restaurant_name)}
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ ...TYPE.bodySmall, fontWeight: 700, color: T.cream }}>{outlet.restaurant_name}</div>
                <div style={{ ...TYPE.caption, color: T.darkTextSec, marginTop: 2, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", textOverflow: "ellipsis", lineHeight: "16px" }}>
                  {outlet.description || (cuisineLabel ? `${cuisineLabel}${outletLabel ? ` · ${outletLabel}` : ""}` : outletLabel || "")}
                </div>
                {cuisineLabel && <div style={{ ...TYPE.caption, color: T.darkTextSec, marginTop: 2 }}>{cuisineLabel}</div>}
                {outletLabel && (
                  <span style={{
                    ...TYPE.caption, color: T.darkTextSec, padding: "2px 8px", borderRadius: RAD.pill,
                    background: `${T.cream}08`, fontSize: 9, display: "inline-block", marginTop: 4,
                  }}>
                    {outletLabel}
                  </span>
                )}
              </div>

              {/* Match indicator */}
              <div style={{ flexShrink: 0, textAlign: "right" }}>
                {noDishes ? (
                  <span style={{ ...TYPE.caption, color: T.darkTextSec, fontStyle: "italic", fontSize: 10 }}>Menu coming soon</span>
                ) : matchPct != null ? (
                  <div>
                    <MatchBadge score={matchPct} />
                    {outlet.great_matches > 0 && (
                      <div style={{ ...TYPE.caption, color: T.gold, fontSize: 9, marginTop: 2 }}>
                        {outlet.great_matches < 3
                          ? `${outlet.great_matches} dish${outlet.great_matches !== 1 ? "es" : ""} matching your profile`
                          : `${outlet.great_matches} great dish match${outlet.great_matches !== 1 ? "es" : ""}`}
                      </div>
                    )}
                    {outlet.best_match_percentage != null && (
                      <div style={{ ...TYPE.caption, color: T.darkTextSec, fontSize: 9 }}>
                        Best: {outlet.best_match_percentage}%
                      </div>
                    )}
                  </div>
                ) : (
                  <span style={{ ...TYPE.caption, color: T.gold, fontWeight: 600 }}>
                    {outlet.dish_count} dish{outlet.dish_count !== 1 ? "es" : ""}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// SCREEN: VENUE DETAIL (V2 → V3 Menu Match)
// ─────────────────────────────────────────────────────────────
const ConsoomeScoreBadge = ({ score }) => {
  if (!score || !score.star_score) return null;
  const stars = score.star_score;
  const cert = score.certification;
  const label = cert === "elite" ? "Consoome Elite" : cert === "certified" ? "Consoome Certified" : null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ ...TYPE.caption, fontWeight: 700, color: T.gold }}>
        {"★".repeat(Math.round(stars))}{"☆".repeat(5 - Math.round(stars))}
      </span>
      {label && (
        <span style={{
          ...TYPE.caption, fontWeight: 700, fontSize: 9, padding: "2px 8px",
          borderRadius: RAD.pill,
          background: cert === "elite" ? `${T.gold}25` : `${T.gold}15`,
          color: T.gold, border: `1px solid ${T.gold}35`,
        }}>
          {label}
        </span>
      )}
    </div>
  );
};

const VenueExperienceFeedback = ({ venue, profile, onSubmit }) => {
  const [ratings, setRatings] = useState({ matched: 0, restrictions: 0, personalized: 0, returnScore: 0 });
  const [submitted, setSubmitted] = useState(false);

  const questions = [
    { key: "matched", label: "Matched my Food ID", emoji: "🎯" },
    { key: "restrictions", label: "Respected restrictions", emoji: "🛡️" },
    { key: "personalized", label: "Felt personalized", emoji: "✨" },
    { key: "returnScore", label: "Would return", emoji: "🔄" },
  ];

  const handleSubmit = () => {
    if (Object.values(ratings).some(v => v === 0)) return;
    setSubmitted(true);
    if (onSubmit) onSubmit({
      venue_id: venue.id,
      matched_food_id: ratings.matched,
      respected_restrictions: ratings.restrictions,
      felt_personalized: ratings.personalized,
      would_return: ratings.returnScore,
    });
  };

  if (submitted) {
    return (
      <div style={{ padding: SP.md, background: `${T.gold}10`, borderRadius: RAD.md, border: `1px solid ${T.gold}20`, textAlign: "center" }}>
        <span style={{ ...TYPE.bodySmall, color: T.gold, fontWeight: 600 }}>Thank you for your Consoome feedback</span>
      </div>
    );
  }

  return (
    <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: RAD.md, border: `1px solid ${T.gold}20`, padding: SP.md, backdropFilter: "blur(8px)" }}>
      <div style={{ ...TYPE.overline, color: T.gold, marginBottom: SP.sm }}>RATE YOUR EXPERIENCE</div>
      {questions.map(q => (
        <div key={q.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <span style={{ ...TYPE.caption, color: T.cream, fontWeight: 500 }}>{q.emoji} {q.label}</span>
          <div style={{ display: "flex", gap: 4 }}>
            {[1,2,3,4,5].map(n => (
              <button key={n} onClick={() => setRatings(r => ({ ...r, [q.key]: n }))}
                style={{
                  width: 28, height: 28, borderRadius: "50%", border: "none", cursor: "pointer",
                  fontFamily: "Montserrat", fontSize: 12, fontWeight: 700,
                  background: ratings[q.key] >= n ? T.gold : "rgba(255,255,255,0.08)",
                  color: ratings[q.key] >= n ? T.darkBg : T.darkTextSec,
                }}>
                {n}
              </button>
            ))}
          </div>
        </div>
      ))}
      <button onClick={handleSubmit}
        disabled={Object.values(ratings).some(v => v === 0)}
        style={{
          width: "100%", padding: "10px", borderRadius: RAD.md, border: "none", cursor: "pointer",
          fontFamily: "Montserrat", ...TYPE.bodySmall, fontWeight: 700,
          background: Object.values(ratings).every(v => v > 0) ? T.gold : "rgba(255,255,255,0.08)",
          color: Object.values(ratings).every(v => v > 0) ? T.darkBg : T.darkTextSec,
          marginTop: SP.sm,
        }}>
        Submit
      </button>
    </div>
  );
};

const VenueDetailScreen = ({ venue, profile, onBack, onDishFeedback, onVenueExperience }) => {
  const [expandedDish, setExpandedDish] = useState(0);
  const [menuTab, setMenuTab] = useState("best");
  const [dishes, setDishes] = useState(venue.dishes || []);
  const [beverages, setBeverages] = useState([]);
  const [venueScore, setVenueScore] = useState(venue.matchScore);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchError, setMatchError] = useState(null);
  const [safetyInfo, setSafetyInfo] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("sb_token");
    const foodId = profile?._foodId;
    const venueId = venue.venue_id || venue.id;
    if (!token || !foodId || !venueId) return;

    let cancelled = false;
    const fetchMatches = async () => {
      setMatchLoading(true);
      try {
        const res = await fetch(`${API_BASE}/food-id/${foodId}/match/${venueId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to load matches");
        const data = await res.json();
        if (cancelled) return;
        // Map API dishes to the shape DishCard expects
        const mapped = (data.dishes || []).map(d => ({
          dish_id: d.dish_id,
          name: d.name,
          desc: d.description || "",
          price: d.price ? `${d.price}` : "",
          match: d.fit_percentage,
          safe: true,
          whyLine: (d.reasons || [])[0] || "",
          whyTags: d.reasons || [],
          icon: guessCuisineIcon(d.name),
          allergens: d.allergens,
          allergen_warnings: d.allergen_warnings || [],
          staff_confirm_required: d.staff_confirm_required || false,
          match_tier: d.match_tier,
          course: d.course,
        }));
        setDishes(mapped);
        setBeverages(data.beverages || []);
        if (data.venue_fit_percentage != null) setVenueScore(Math.round(data.venue_fit_percentage));
        if (data.total_dishes != null && data.matched_dishes != null) {
          const filtered = data.total_dishes - data.matched_dishes;
          if (filtered > 0) setSafetyInfo({ total: data.total_dishes, matched: data.matched_dishes, filtered });
        }
      } catch (e) {
        if (!cancelled) setMatchError(friendlyError(e));
      } finally {
        if (!cancelled) setMatchLoading(false);
      }
    };
    fetchMatches();
    return () => { cancelled = true; };
  }, [venue, profile]);

  // Sort dishes by match score
  const sortedDishes = [...dishes].sort((a, b) => b.match - a.match);
  const bestMatches = sortedDishes.filter(d => d.match >= 73).slice(0, 5);
  const displayDishes = menuTab === "best" && bestMatches.length > 0 ? bestMatches : sortedDishes;

  // Consoome Recommends — top dishes in "Excellent" or "Good Fit" tiers
  const recommendedDishes = (() => {
    const excellent = sortedDishes.filter(d => d.match >= 85).slice(0, 3);
    if (excellent.length > 0) return excellent;
    const good = sortedDishes.filter(d => d.match >= 75).slice(0, 2);
    return good;
  })();
  // Names of dishes shown as ★★★ Best Match in the compact Recommends strip
  const bestMatchSet = new Set(
    recommendedDishes.slice(0, 2).filter(d => d.match >= 85).map(d => d.name)
  );

  return (
    <div style={{ minHeight: "100%", background: T.darkBg, paddingBottom: 40, position: "relative" }}>
      <DarkBgLayers />
      {/* Hero */}
      <div style={{ height: 180, background: `${T.gold}0A`, position: "relative", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 56, zIndex: 1 }}>
        {venue.icon}
        <button onClick={onBack} style={{
          position: "absolute", top: 48, left: SP.lg,
          ...TYPE.bodySmall, fontWeight: 600, color: T.darkTextSec,
          background: "rgba(255,255,255,0.06)", border: `1px solid ${T.gold}20`, borderRadius: RAD.pill,
          padding: "8px 14px", cursor: "pointer", fontFamily: "Montserrat",
          backdropFilter: "blur(8px)",
        }}>
          ← Back
        </button>
        {venueScore != null && (
          <div style={{ position: "absolute", bottom: SP.md, right: SP.lg }}>
            <MatchBadge score={venueScore} size="lg" />
          </div>
        )}
      </div>

      {/* Venue info */}
      <div style={{ padding: SP.lg, position: "relative", zIndex: 1 }}>
        <div style={{ ...TYPE.caption, color: T.darkTextSec, marginBottom: 4 }}>{venue.hotel}</div>
        <h1 style={{ ...TYPE.display, color: T.cream, marginBottom: 4 }}>{venue.name}</h1>
        <div style={{ ...TYPE.bodySmall, color: T.darkTextSec, marginBottom: 6 }}>{venue.cuisine} · {venue.priceRange}</div>
        {venue.consoomeScore && <div style={{ marginBottom: SP.sm }}><ConsoomeScoreBadge score={venue.consoomeScore} /></div>}
        <p style={{ ...TYPE.body, color: T.darkTextSec, lineHeight: "22px", marginBottom: SP.lg }}>{venue.description}</p>

        {/* Why this fits you */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: SP.lg }}>
          {venue.reasons.map((r, i) => (
            <span key={i} style={{
              ...TYPE.caption, fontWeight: 600, padding: "6px 12px",
              borderRadius: RAD.pill, background: `${T.gold}15`, color: T.gold,
              border: `1px solid ${T.gold}25`,
            }}>
              {r}
            </span>
          ))}
        </div>
      </div>

      {/* Menu tabs */}
      <div style={{ display: "flex", gap: 0, padding: `0 ${SP.lg}px`, marginBottom: SP.md, position: "relative", zIndex: 1 }}>
        {[
          { key: "best", label: "Top Picks" },
          { key: "full", label: "Full Menu" },
          { key: "wine", label: "Wine" },
        ].map(tab => (
          <button key={tab.key} onClick={() => setMenuTab(tab.key)} style={{
            flex: 1, padding: "10px 0", background: "none", border: "none", borderBottom: `2px solid ${menuTab === tab.key ? T.gold : `${T.gold}20`}`,
            ...TYPE.bodySmall, fontWeight: menuTab === tab.key ? 700 : 500,
            color: menuTab === tab.key ? T.gold : T.darkTextSec,
            cursor: "pointer", fontFamily: "Montserrat",
          }}>
            {tab.label}
          </button>
        ))}
      </div>


      {/* Restriction filter notice */}
      {safetyInfo && (
        <div style={{
          margin: `0 ${SP.lg}px ${SP.md}px`, padding: "10px 14px",
          background: "rgba(76,175,80,0.08)", border: "1px solid rgba(76,175,80,0.2)",
          borderRadius: RAD.md, display: "flex", alignItems: "center", gap: 8,
          position: "relative", zIndex: 1,
        }}>
          <span style={{ fontSize: 16 }}>🛡️</span>
          <span style={{ ...TYPE.caption, color: "#8BC34A", fontWeight: 600 }}>
            {safetyInfo.filtered} dish{safetyInfo.filtered !== 1 ? "es" : ""} filtered for your dietary restrictions
          </span>
        </div>
      )}

      {/* Consoome Recommends */}
      {recommendedDishes.length > 0 && menuTab !== "wine" && !matchLoading && (
        <div style={{
          margin: `0 ${SP.lg}px ${SP.md}px`, padding: SP.md,
          background: `${T.safetyGreen}12`, border: `1px solid ${T.safetyGreen}30`,
          borderRadius: RAD.lg, position: "relative", zIndex: 1,
        }}>
          <div style={{ ...TYPE.overline, color: T.safetyGreen, fontWeight: 700, marginBottom: SP.sm, display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 16 }}>🏆</span> CONSOOME RECOMMENDS
          </div>
          {recommendedDishes.map((dish, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: SP.sm, padding: "8px 0",
              borderTop: i > 0 ? `1px solid ${T.safetyGreen}15` : "none",
            }}>
              <span style={{ fontSize: 22 }}>{dish.icon || "🍽️"}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ ...TYPE.body, color: T.cream, fontWeight: 600 }}>{dish.name}</div>
                {dish.whyLine && <div style={{ ...TYPE.caption, color: T.safetyGreen, fontWeight: 500 }}>{dish.whyLine}</div>}
              </div>
              <MatchBadge score={dish.match} variant={i < 2 && dish.match >= 85 ? "bestMatch" : undefined} />
            </div>
          ))}
        </div>
      )}

      {/* Dish list or Wine */}
      <div style={{ padding: `0 ${SP.lg}px`, position: "relative", zIndex: 1 }}>
        {matchLoading ? (
          <div>
            {[1, 2, 3, 4].map(i => (
              <div key={i} style={{
                height: 72, borderRadius: RAD.md, marginBottom: SP.sm,
                background: "linear-gradient(90deg, rgba(255,255,255,0.03) 25%, rgba(255,255,255,0.07) 50%, rgba(255,255,255,0.03) 75%)",
                backgroundSize: "200% 100%", animation: "shimmer 1.5s infinite",
                animationDelay: `${i * 0.12}s`,
              }} />
            ))}
            <p style={{ ...TYPE.caption, color: T.darkTextSec, textAlign: "center", marginTop: SP.sm }}>Matching dishes to your Food ID...</p>
          </div>
        ) : matchError ? (
          <div style={{ padding: SP.xl, textAlign: "center" }}>
            <p style={{ ...TYPE.body, color: T.safetyRed }}>{matchError}</p>
          </div>
        ) : menuTab === "wine" ? (
          <WineSection wine={venue.wine} beverages={beverages} />
        ) : (
          <>
            {displayDishes.map((dish, i) => (
              <DishCard key={i} dish={dish} expanded={expandedDish === i} onToggle={() => setExpandedDish(expandedDish === i ? null : i)} onFeedback={(d, type) => onDishFeedback && onDishFeedback(d, type, venue)} variant={bestMatchSet.has(dish.name) ? "bestMatch" : undefined} />
            ))}
            {menuTab === "best" && bestMatches.length === 0 && (
              <div style={{ padding: SP.xl, textAlign: "center" }}>
                <p style={{ ...TYPE.body, color: T.darkTextSec }}>
                  No standout matches at this venue yet. Switch to Full Menu to see all dishes ranked for you.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Venue experience feedback */}
      <div style={{ padding: `${SP.lg}px ${SP.lg}px 0`, position: "relative", zIndex: 1 }}>
        <VenueExperienceFeedback venue={venue} profile={profile} onSubmit={(data) => onVenueExperience && onVenueExperience(data)} />
      </div>

      {/* Safety disclaimer */}
      <div style={{ margin: `${SP.lg}px ${SP.lg}px 0`, padding: "10px 14px", background: `${T.safetyRed}15`, borderRadius: RAD.sm, border: `1px solid ${T.safetyRed}25`, position: "relative", zIndex: 1 }}>
        <p style={{ ...TYPE.caption, color: T.safetyRed, lineHeight: "16px" }}>
          Consoome helps identify potential allergens based on available data. Always confirm directly with restaurant staff before ordering.
        </p>
      </div>
    </div>
  );
};

const DishCard = ({ dish, expanded, onToggle, onFeedback, variant }) => {
  const [feedbackGiven, setFeedbackGiven] = useState(null);
  const tier = getMatchTier(dish.match);
  const borderColor = dish.match >= 85 ? T.gold : dish.match >= 65 ? `${T.gold}60` : `${T.gold}25`;

  const handleFeedback = (type, e) => {
    e.stopPropagation();
    setFeedbackGiven(type);
    if (onFeedback) onFeedback(dish, type);
  };

  return (
    <div onClick={onToggle} role="button" tabIndex={0} aria-expanded={!!expanded} aria-label={`${dish.name || dish.dish_name || "Dish"}, ${tier.label}`} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }} style={{
      background: "rgba(255,255,255,0.05)", borderRadius: RAD.md,
      border: `1px solid ${T.gold}20`,
      borderLeft: `3px solid ${borderColor}`,
      padding: SP.md, marginBottom: 10, cursor: "pointer",
      transition: "all 0.2s",
      boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
      backdropFilter: "blur(8px)",
    }}>
      <div style={{ display: "flex", gap: SP.md, alignItems: "flex-start" }}>
        <span aria-hidden="true" style={{ fontSize: 28, flexShrink: 0 }}>{dish.icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
            <div style={{ ...TYPE.h2, color: T.cream, lineHeight: "22px" }}>
              {dish.name}
            </div>
            <MatchBadge score={dish.match} variant={variant} />
          </div>
          {dish.whyLine && <div style={{ ...TYPE.caption, color: T.gold, fontWeight: 600, marginBottom: 4 }}>{dish.whyLine}</div>}
          <div style={{ ...TYPE.bodySmall, color: T.darkTextSec, lineHeight: "18px", marginBottom: 6 }}>{dish.desc}</div>
          {dish.staff_confirm_required && dish.allergen_warnings?.length > 0 && (
            <div style={{
              marginTop: 6, padding: "6px 10px", borderRadius: RAD.sm,
              background: `${T.safetyRed}40`, border: `1px solid ${T.safetyRed}90`,
              borderLeft: `3px solid ${T.safetyRed}`,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <span style={{ fontSize: 14, flexShrink: 0 }}>⚠️</span>
              <span style={{ ...TYPE.caption, color: T.safetyRed, fontWeight: 700 }}>
                Confirm with staff: {dish.allergen_warnings.filter(a => ["nuts", "shellfish", "sesame"].includes(a)).join(", ") || dish.allergen_warnings.join(", ")}
              </span>
            </div>
          )}
        </div>
      </div>

      {expanded && (
        <div className="anim-fadein" style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.gold}15` }}>
          <div style={{ ...TYPE.overline, color: T.darkTextSec, marginBottom: 8 }}>
            WHY THIS FITS YOU
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {dish.whyTags.map((tag, i) => (
              <span key={i} style={{
                padding: "5px 10px", borderRadius: RAD.pill, ...TYPE.caption, fontWeight: 600,
                background: `${T.gold}15`, color: T.gold,
                border: `1px solid ${T.gold}25`,
              }}>
                ✓ {tag}
              </span>
            ))}
          </div>
          <div style={{ marginTop: 12, padding: "10px 14px", background: "rgba(255,255,255,0.04)", borderRadius: RAD.sm, border: `1px solid ${T.gold}15` }}>
            <span style={{ ...TYPE.caption, color: T.darkTextSec }}>
              🍷 Pair with: based on your bold red wine preference
            </span>
          </div>
          <div style={{ display: "flex", gap: SP.sm, marginTop: SP.md, flexWrap: "wrap" }}>
            {feedbackGiven ? (
              <span style={{ ...TYPE.caption, fontWeight: 600, color: T.gold, padding: "6px 14px" }}>
                {feedbackGiven === "saved" ? "Saved" : feedbackGiven === "great_pick" ? "Noted" : feedbackGiven === "not_for_me" ? "Got it" : "Recorded"}
              </span>
            ) : (
              <>
                <button onClick={(e) => handleFeedback("saved", e)} style={{ ...TYPE.caption, fontWeight: 600, color: T.darkTextSec, background: "rgba(255,255,255,0.06)", border: `1px solid ${T.gold}15`, borderRadius: RAD.pill, padding: "6px 14px", cursor: "pointer", fontFamily: "Montserrat" }}>
                  🔖 Save
                </button>
                <button onClick={(e) => handleFeedback("great_pick", e)} style={{ ...TYPE.caption, fontWeight: 600, color: T.gold, background: `${T.gold}12`, border: `1px solid ${T.gold}25`, borderRadius: RAD.pill, padding: "6px 14px", cursor: "pointer", fontFamily: "Montserrat" }}>
                  Great pick
                </button>
                <button onClick={(e) => handleFeedback("not_for_me", e)} style={{ ...TYPE.caption, fontWeight: 600, color: T.safetyRed, background: `${T.safetyRed}15`, border: `1px solid ${T.safetyRed}25`, borderRadius: RAD.pill, padding: "6px 14px", cursor: "pointer", fontFamily: "Montserrat" }}>
                  Not for me
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const WineSection = ({ wine, beverages = [] }) => {
  const wineItems = beverages.filter(b => (b.category || "").toLowerCase() === "wine");
  const allItems = wineItems.length > 0 ? wineItems : beverages;
  if (!allItems.length && !wine) return (
    <div style={{ padding: SP.xl, textAlign: "center" }}>
      <p style={{ ...TYPE.body, color: T.darkTextSec }}>No wine or beverage matches available for this venue yet.</p>
    </div>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {wine && (
        <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: RAD.md, border: `1px solid ${T.gold}20`, padding: SP.md, backdropFilter: "blur(8px)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: SP.md }}>
            <span style={{ fontSize: 32 }}>🍷</span>
            <div style={{ flex: 1 }}>
              <div style={{ ...TYPE.bodySmall, fontWeight: 700, color: T.cream }}>{wine.name}</div>
              <div style={{ ...TYPE.caption, color: T.darkTextSec, marginTop: 2 }}>{wine.region}</div>
            </div>
            <MatchBadge score={wine.match} />
          </div>
        </div>
      )}
      {allItems.map((bev, i) => (
        <div key={i} style={{ background: "rgba(255,255,255,0.05)", borderRadius: RAD.md, border: `1px solid ${T.gold}20`, borderLeft: `3px solid ${(bev.fit_percentage || 0) >= 85 ? T.gold : (bev.fit_percentage || 0) >= 65 ? `${T.gold}60` : `${T.gold}25`}`, padding: SP.md, backdropFilter: "blur(8px)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: SP.md }}>
            <span style={{ fontSize: 28 }}>{(bev.category || "").toLowerCase() === "wine" ? "🍷" : (bev.category || "").toLowerCase() === "cocktail" ? "🍸" : "🥤"}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ ...TYPE.bodySmall, fontWeight: 700, color: T.cream }}>{bev.name || bev.dish_name}</div>
              {bev.description && <div style={{ ...TYPE.caption, color: T.darkTextSec, marginTop: 2 }}>{bev.description}</div>}
            </div>
            <MatchBadge score={bev.fit_percentage || 0} />
          </div>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// SCREEN: PROFILE (P1) — spec: Spotify-style taste identity
// ─────────────────────────────────────────────────────────────
const TEXTURE_OPTIONS = ["Crispy", "Creamy", "Crunchy", "Tender", "Chewy", "Smooth", "Flaky", "Silky", "Al Dente", "Crumbly"];
const COOKING_OPTIONS = ["Grilled", "Steamed", "Roasted", "Fried", "Braised", "Sautéed", "Smoked", "Raw", "Slow-cooked", "Baked"];
const PROTEIN_OPTIONS = ["Beef", "Chicken", "Seafood", "Lamb", "Pork", "Plant", "Fish", "Duck", "Tofu", "Veal"];
const CUISINE_OPTIONS = ["Japanese", "Italian", "French", "Thai", "Indian", "American", "Lebanese", "Mexican", "Chinese", "Korean", "Greek", "Spanish", "Turkish", "Vietnamese", "Ethiopian"];
const WINE_REGION_OPTIONS = ["France", "Italy", "Spain", "Australia", "Argentina", "Chile", "South Africa", "New Zealand", "USA", "Germany", "Portugal"];
const COCKTAIL_STYLE_OPTIONS = ["Spirit-forward", "Citrus & Sour", "Tropical & Tiki", "Bitter & Aperitivo", "Sweet & Creamy", "Light & Refreshing"];
const SPIRIT_OPTIONS = ["Whisky", "Gin", "Vodka", "Rum", "Tequila", "Mezcal", "Brandy", "Cognac"];
const NA_BEV_OPTIONS = ["Craft Sodas", "Kombucha", "Mocktails", "Fresh Juice", "Specialty Tea", "Specialty Coffee", "Sparkling Water"];
const MEAL_PERIODS = ["breakfast", "lunch", "dinner", "any"];
const GROUP_SIZES = ["solo", "couple", "small group", "large group"];
const MODES = ["health", "indulgence", "balanced"];
const BUDGETS = ["budget", "mid", "premium", "prestige"];

const ProfileScreen = ({ profile, setProfile, onShare, isOffline }) => {
  const [expandedLayer, setExpandedLayer] = useState(null);
  const [refineSection, setRefineSection] = useState(null); // Active refinement swipe session
  const [refineIndex, setRefineIndex] = useState(0);
  const [refineData, setRefineData] = useState({}); // Collects effects during refinement
  const [refineDeck, setRefineDeck] = useState([]); // Memoized deck — picked once when section opens, prevents reshuffle on every render
  // Phase 1 Level 2 slider refinement (taste + beverage_wine only)
  const [level2Section, setLevel2Section] = useState(null);
  const [level2Values, setLevel2Values] = useState({});
  const fileRef = useRef();

  // ── Edit mode state ──
  const [editSection, setEditSection] = useState(null); // "safety"|"taste"|"cuisine"|"beverage"|"context"|null
  const [editData, setEditData] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // { type, allergenKey, label } or { type: "discard" }
  const [editDirty, setEditDirty] = useState(false);

  const allergens = getAllergensList(profile.safety);
  const topCuisines = getTopCuisines(profile.cuisine?.cuisine_affinities);

  // ── Edit helpers ──
  const openEdit = (section) => {
    const sectionData = JSON.parse(JSON.stringify(profile[section] || {}));
    // Normalize cuisine data to Title Case (fix legacy lowercase entries)
    if (section === "cuisine") {
      // Normalize cuisine_affinities keys: "japanese" → "Japanese"
      if (sectionData.cuisine_affinities) {
        const norm = {};
        for (const [k, v] of Object.entries(sectionData.cuisine_affinities)) {
          const title = k.charAt(0).toUpperCase() + k.slice(1).toLowerCase();
          norm[title] = Math.max(norm[title] || 0, v); // merge duplicates keeping max
        }
        sectionData.cuisine_affinities = norm;
      }
      // Normalize protein_ranking: "beef" → "Beef", deduplicate
      if (sectionData.protein_ranking) {
        const seen = new Set();
        sectionData.protein_ranking = sectionData.protein_ranking
          .map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
          .filter(p => { if (seen.has(p)) return false; seen.add(p); return true; });
      }
    }
    setEditData(sectionData);
    setEditSection(section);
    setEditDirty(false);
  };

  const closeEdit = () => {
    if (editDirty) {
      setConfirmDialog({ type: "discard" });
    } else {
      setEditSection(null);
      setEditData({});
    }
  };

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      const token = localStorage.getItem("sb_token");
      const res = await fetch(`${API_BASE}/food-id/me/${editSection}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(editData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Failed (${res.status})`);
      setProfile(p => {
        const next = { ...p, [editSection]: data.updated || editData, completeness_score: data.completeness ?? p.completeness_score };
        if (editSection === "safety") cacheSafetyCard(next);
        return next;
      });
      setEditSection(null);
      setEditData({});
      setEditDirty(false);
    } catch (err) {
      alert(`Save failed: ${err.message}`);
    } finally {
      setEditSaving(false);
    }
  };

  const updateField = (key, value) => {
    setEditData(d => ({ ...d, [key]: value }));
    setEditDirty(true);
  };

  const toggleArrayItem = (key, item) => {
    setEditData(d => {
      const arr = d[key] || [];
      const next = arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item];
      return { ...d, [key]: next };
    });
    setEditDirty(true);
  };

  // Mutual exclusivity: adding to preferred removes from avoided, and vice versa
  const toggleTexture = (category, item) => {
    const opposite = category === "preferred_textures" ? "avoided_textures" : "preferred_textures";
    setEditData(d => {
      const arr = d[category] || [];
      const isAdding = !arr.includes(item);
      const next = isAdding ? [...arr, item] : arr.filter(x => x !== item);
      const oppArr = isAdding ? (d[opposite] || []).filter(x => x !== item) : (d[opposite] || []);
      return { ...d, [category]: next, [opposite]: oppArr };
    });
    setEditDirty(true);
  };

  const toggleAllergenEdit = (allergenKey, label) => {
    const currentValue = editData[allergenKey];
    if (currentValue) {
      // Removing an allergen — safety-critical confirmation
      setConfirmDialog({ type: "allergen", allergenKey, label });
    } else {
      updateField(allergenKey, true);
    }
  };

  const handleAvatar = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setProfile(p => ({ ...p, avatar: ev.target.result }));
    reader.readAsDataURL(file);
  };

  // Safety strip data
  const safetyAllergens = ALLERGENS.filter(a => a.key !== "none" && profile.safety?.[a.key]);
  const safetyDietary = DIETARY.filter(d => profile.safety?.[d.key]);
  const completeness = profile.completeness_score || 0;

  return (
    <div style={{ minHeight: "100%", background: T.darkBg, paddingBottom: 40, position: "relative" }}>
      <DarkBgLayers />

      {/* Safety Strip — P0: always visible */}
      <div style={{
        width: "100%", minHeight: 44, display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: `52px ${SP.lg}px 0`, paddingBottom: SP.sm, background: T.darkCard,
        borderBottom: `1px solid ${T.darkBorder}`,
        position: "relative", zIndex: 2, fontFamily: "Montserrat",
      }}>
        {(safetyAllergens.length > 0 || safetyDietary.length > 0) ? (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: SP.xs }}>
              {safetyAllergens.map(a => (
                <span key={a.key} title={a.label} style={{ fontSize: 16 }}>{a.icon}</span>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: SP.xs }}>
              {safetyDietary.map(d => (
                <span key={d.key} style={{
                  ...TYPE.caption, color: T.cream, fontWeight: 600,
                  padding: "2px 8px", borderRadius: RAD.pill,
                  background: `${T.gold}20`, border: `1px solid ${T.gold}30`,
                }}>{d.label}</span>
              ))}
            </div>
          </>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: SP.sm, width: "100%" }}>
            <span style={{ fontSize: 14 }}>🛡️</span>
            <span style={{ ...TYPE.bodySmall, color: T.darkTextSec, flex: 1 }}>Dietary profile not configured</span>
          </div>
        )}
      </div>

      {/* Header */}
      <div style={{ padding: `${SP.lg}px ${SP.lg}px ${SP.lg}px`, position: "relative", overflow: "hidden", zIndex: 1 }}>
        <div style={{ position: "absolute", top: -60, right: -60, width: 200, height: 200, borderRadius: "50%", background: `radial-gradient(circle, ${T.gold}15 0%, transparent 70%)` }} />

        <div style={{ display: "flex", alignItems: "flex-start", gap: SP.md, marginBottom: SP.lg }}>
          {/* Avatar */}
          <button type="button" aria-label="Change profile photo" onClick={() => fileRef.current?.click()} style={{ position: "relative", flexShrink: 0, background: "none", border: "none", padding: 0, cursor: "pointer" }}>
            <div style={{
              width: 72, height: 72, borderRadius: "50%", overflow: "hidden",
              border: `2.5px solid ${T.gold}40`,
              background: profile.avatar ? "transparent" : T.gold,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {profile.avatar
                ? <img src={profile.avatar} alt={`${profile.display_name} profile photo`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : <span style={{ fontSize: 28, fontWeight: 800, color: T.ink }}>{profile.display_name.charAt(0)}</span>
              }
            </div>
            <div aria-hidden="true" style={{
              position: "absolute", bottom: 1, right: 1, width: 22, height: 22, borderRadius: "50%",
              background: T.cream, border: `2px solid ${T.darkBg}`,
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10,
            }}>📷</div>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleAvatar} aria-label="Upload profile photo" />
          </button>

          <div style={{ flex: 1 }}>
            <div style={{ ...TYPE.overline, color: T.darkTextSec }}>FOOD ID</div>
            <div style={{ ...TYPE.display, color: T.cream }}>@{profile.handle}</div>
            <div style={{ ...TYPE.bodySmall, color: T.darkTextSec, marginTop: 2 }}>{profile.display_name}</div>
          </div>

          <CompletionRing pct={profile.completeness_score} />
        </div>

        {/* Summary chips */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {allergens.length > 0 && (
            <SummaryChip icon="🛡️" label={`No ${allergens.slice(0, 2).join(", ")}`} color={T.safetyRed} bg={`${T.safetyRed}20`} />
          )}
          {topCuisines.slice(0, 2).map(c => (
            <SummaryChip key={c} icon="🌍" label={c} />
          ))}
          {profile.beverage.drinks_alcohol && (
            <SummaryChip icon="🍷" label={`${profile.beverage.wine_style_pref[0]} wine`} />
          )}
        </div>
      </div>

      {/* CTAs */}
      <div style={{ padding: `${SP.md}px ${SP.lg}px 0`, position: "relative", zIndex: 1, display: "flex", flexDirection: "column", gap: SP.sm }}>
        {isOffline ? (
          <div style={{
            width: "100%", padding: "12px 16px", borderRadius: RAD.pill, textAlign: "center",
            background: "rgba(255,255,255,0.06)", border: `1px solid ${T.gold}20`,
            ...TYPE.bodySmall, color: T.darkTextSec, fontFamily: "Montserrat",
          }}>
            Sharing requires an internet connection
          </div>
        ) : (
          <>
            <GoldBtn onClick={onShare}>Share My Food ID</GoldBtn>
            <div style={{ display: "flex", gap: SP.sm }}>
              <button onClick={() => { setExpandedLayer("safety"); }} style={{
                flex: 1, padding: `${SP.md}px`, borderRadius: RAD.md,
                background: "rgba(255,255,255,0.06)", border: `1px solid ${T.gold}20`,
                cursor: "pointer", fontFamily: "Montserrat", textAlign: "center",
                ...TYPE.bodySmall, color: T.cream, fontWeight: 600,
              }}>
                Edit Preferences
              </button>
              <button onClick={() => {
                const key = REFINEMENT_SECTIONS[0]?.key || "texture";
                const answeredOnOpen = loadAnsweredTileIds();
                setRefineDeck(selectTilesForRefine(key, profile, answeredOnOpen, 8));
                setRefineSection(key); setRefineIndex(0); setRefineData({});
              }} style={{
                flex: 1, padding: `${SP.md}px`, borderRadius: RAD.md,
                background: "rgba(255,255,255,0.06)", border: `1px solid ${T.gold}20`,
                cursor: "pointer", fontFamily: "Montserrat", textAlign: "center",
                ...TYPE.bodySmall, color: T.cream, fontWeight: 600,
              }}>
                Refine with Swipe Deck
              </button>
            </div>
          </>
        )}
      </div>

      {/* Completeness breakdown */}
      {completeness < 100 && !isOffline && (
        <div style={{ padding: `${SP.md}px ${SP.lg}px 0`, position: "relative", zIndex: 1 }}>
          <div style={{
            padding: `${SP.md}px`, borderRadius: RAD.md,
            background: `${T.gold}08`, border: `1px solid ${T.gold}15`,
          }}>
            <div style={{ ...TYPE.bodySmall, color: T.cream, fontWeight: 600, marginBottom: SP.sm }}>
              Profile {completeness}% complete
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: SP.xs }}>
              {!profile.safety?.has_nuts && !profile.safety?.has_shellfish && !profile.safety?.has_gluten && !profile.safety?.has_dairy && (
                <div style={{ ...TYPE.caption, color: T.darkTextSec }}>+ Add your allergens for safer matches</div>
              )}
              {(!profile.cuisine?.cuisine_affinities || Object.keys(profile.cuisine.cuisine_affinities).length === 0) && (
                <div style={{ ...TYPE.caption, color: T.darkTextSec }}>+ Set cuisine preferences</div>
              )}
              {!profile.beverage?.wine_style_pref?.[0] && (
                <div style={{ ...TYPE.caption, color: T.darkTextSec }}>+ Add wine or beverage preferences</div>
              )}
              {(!profile.context?.occasions || profile.context.occasions.length === 0) && (
                <div style={{ ...TYPE.caption, color: T.darkTextSec }}>+ Tell us your dining occasions</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Offline notice */}
      {isOffline && (() => {
        const cached = loadCachedSafetyCard();
        return cached ? (
          <div style={{
            margin: `${SP.md}px ${SP.lg}px 0`, padding: "10px 14px",
            background: `${T.safetyRed}15`, border: `1px solid ${T.safetyRed}30`,
            borderRadius: RAD.md, position: "relative", zIndex: 1,
          }}>
            <p style={{ ...TYPE.caption, color: T.darkTextSec, fontWeight: 500 }}>
              Showing saved data from {new Date(cached.updatedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
            </p>
          </div>
        ) : null;
      })()}

      {/* Activity Feed — moved from HomeScreen */}
      {(() => {
        const activities = getActivities();
        if (activities.length === 0) return null;
        return (
          <div style={{ padding: `${SP.md}px ${SP.lg}px 0`, position: "relative", zIndex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: SP.sm, marginBottom: SP.md }}>
              <div style={{
                width: 8, height: 8, borderRadius: "50%", background: T.gold,
                animation: "pulseDot 2s ease-in-out infinite",
              }} />
              <span style={{ ...TYPE.caption, color: T.darkTextSec, fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px" }}>
                Your Food ID is Learning
              </span>
            </div>
            {activities.slice(0, 5).map((a, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", gap: SP.md, minHeight: 56,
                borderBottom: i < Math.min(activities.length, 5) - 1 ? `1px solid ${T.darkBorder}` : "none",
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: "50%", background: `${T.gold}10`,
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <UiIcon name="activity" size={16} color={T.gold} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ ...TYPE.bodySmall, color: T.cream, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {a.text}
                  </div>
                  <div style={{ ...TYPE.caption, color: T.darkTextSec, marginTop: 1 }}>
                    {timeAgo(a.timestamp)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Profile layers */}
      <div style={{ padding: `${SP.lg}px ${SP.lg}px 0`, position: "relative", zIndex: 1 }}>
        <SectionLabel>Your Profile Layers</SectionLabel>

        <ProfileLayer
          icon="🛡️" title="Restrictions" subtitle="Allergens, dietary, never-eat list"
          accent={T.safetyRed} expanded={expandedLayer === "safety" || isOffline}
          onToggle={() => setExpandedLayer(expandedLayer === "safety" ? null : "safety")}
          onEdit={isOffline ? undefined : () => openEdit("safety")}
        >
          <div style={{ ...TYPE.bodySmall, color: T.darkTextSec, lineHeight: "24px" }}>
            {allergens.length > 0 ? (
              <>
                <div style={{ fontWeight: 700, color: T.safetyRed, marginBottom: 4 }}>⚠️ Allergies / حساسية</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                  {allergens.map(a => (
                    <div key={a} style={{ padding: "6px 12px", borderRadius: 8, background: `${T.safetyRed}18`, border: `1px solid ${T.safetyRed}40` }}>
                      <div style={{ fontWeight: 600, color: T.cream, fontSize: 13 }}>{ALLERGENS.find(x => x.label === a)?.icon} {a}</div>
                      {SAFETY_AR[a] && <div style={{ fontFamily: AR_FONT, direction: "rtl", textAlign: "right", color: T.cream, fontSize: 13, fontWeight: 600, marginTop: 2 }}>{SAFETY_AR[a]} {ALLERGENS.find(x => x.label === a)?.icon}</div>}
                    </div>
                  ))}
                </div>
              </>
            ) : <div style={{ color: T.darkTextSec }}>No allergens flagged</div>}
            {(() => {
              const dietaryItems = DIETARY.filter(d => profile.safety?.[d.key]).map(d => d.label);
              return dietaryItems.length > 0 ? (
                <>
                  <div style={{ fontWeight: 700, color: T.cream, marginBottom: 4 }}>Dietary / قيود غذائية</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                    {dietaryItems.map(d => (
                      <div key={d} style={{ padding: "6px 12px", borderRadius: 8, background: `${T.safetyGreen}18`, border: `1px solid ${T.safetyGreen}40` }}>
                        <div style={{ fontWeight: 600, color: T.cream, fontSize: 13 }}>{DIETARY.find(x => x.label === d)?.icon} {d}</div>
                        {SAFETY_AR[d] && <div style={{ fontFamily: AR_FONT, direction: "rtl", textAlign: "right", color: T.cream, fontSize: 13, fontWeight: 600, marginTop: 2 }}>{SAFETY_AR[d]}</div>}
                      </div>
                    ))}
                  </div>
                </>
              ) : null;
            })()}
            {profile.safety.never_eat_list?.length > 0 && <div><span style={{ fontWeight: 700 }}>Never eat: </span>{profile.safety.never_eat_list.join(", ")}</div>}
            {/* Milk & dairy preferences */}
            {(profile.safety.milk_intolerant || profile.safety.milk_alternatives?.length > 0 || profile.safety.milk_preference) && (
              <div style={{ marginTop: SP.sm, paddingTop: SP.sm, borderTop: `1px solid ${T.gold}15` }}>
                <div style={{ ...TYPE.overline, color: T.darkTextSec, marginBottom: 4 }}>MILK & DAIRY</div>
                {profile.safety.milk_intolerant && (
                  <div style={{ color: T.cream }}>⚠️ Lactose intolerant</div>
                )}
                {profile.safety.milk_preference && profile.safety.milk_preference !== "none" && (
                  <div style={{ color: T.darkTextSec }}>
                    Prefers: <span style={{ color: T.cream, fontWeight: 600 }}>{profile.safety.milk_preference.replace(/_/g, " ")}</span>
                  </div>
                )}
                {profile.safety.milk_alternatives?.length > 0 && (
                  <div style={{ color: T.darkTextSec }}>
                    Alternatives: <span style={{ color: T.cream, fontWeight: 600 }}>{profile.safety.milk_alternatives.join(", ")}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </ProfileLayer>

        <ProfileLayer
          icon="👅" title="Taste Profile" subtitle="Flavor scores, textures, cooking"
          accent={T.gold} expanded={!isOffline && expandedLayer === "taste"}
          onToggle={isOffline ? undefined : () => setExpandedLayer(expandedLayer === "taste" ? null : "taste")}
          onEdit={isOffline ? undefined : () => openEdit("taste")}
        >
          <ScoreBar label="Richness" value={profile.taste.richness_pref} max={5} color={T.gold} />
          <ScoreBar label="Umami" value={profile.taste.umami_pref} max={5} color={T.gold} />
          <ScoreBar label="Spice" value={profile.taste.spice_tolerance} max={4} color={T.safetyRed} />
          <ScoreBar label="Acidity" value={profile.taste.acidity_pref} max={5} color={T.teal} />
          <div style={{ marginTop: SP.md, ...TYPE.bodySmall, color: T.darkTextSec, lineHeight: "24px" }}>
            <div><span style={{ fontWeight: 700, color: T.cream }}>Textures: </span>{(profile.taste.preferred_textures || []).join(", ") || "None set"}</div>
            <div><span style={{ fontWeight: 700, color: T.cream }}>Avoids: </span>{(profile.taste.avoided_textures || []).join(", ") || "None"}</div>
          </div>
        </ProfileLayer>

        <ProfileLayer
          icon="🌍" title="Cuisine Identity" subtitle="Affinities, proteins, ingredients"
          accent={T.sapphire} expanded={!isOffline && expandedLayer === "cuisine"}
          onToggle={isOffline ? undefined : () => setExpandedLayer(expandedLayer === "cuisine" ? null : "cuisine")}
          onEdit={isOffline ? undefined : () => openEdit("cuisine")}
        >
          {Object.entries(profile.cuisine?.cuisine_affinities || {}).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([k, v]) => (
            <ScoreBar key={k} label={k} value={v} max={5} color={T.sapphire} />
          ))}
          <div style={{ marginTop: SP.md, ...TYPE.bodySmall, color: T.darkTextSec, lineHeight: "24px" }}>
            <div><span style={{ fontWeight: 700, color: T.cream }}>Proteins: </span>{[...new Set((profile.cuisine.protein_ranking || []).map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()))].join(" > ")}</div>
            <div><span style={{ fontWeight: 700, color: T.cream }}>Loves: </span>{(profile.cuisine.loved_ingredients || []).join(", ") || "None set"}</div>
          </div>
        </ProfileLayer>

        <ProfileLayer
          icon="🍷" title="Beverage & Wine" subtitle="Sommelier-grade matching"
          accent="#7D3C98" expanded={!isOffline && expandedLayer === "bev"}
          onToggle={isOffline ? undefined : () => setExpandedLayer(expandedLayer === "bev" ? null : "bev")}
          onEdit={isOffline ? undefined : () => openEdit("beverage")}
        >
          {profile.beverage.drinks_alcohol ? (
            <>
              <ScoreBar label="Wine Body" value={profile.beverage.wine_body_pref} max={5} color="#7D3C98" />
              <ScoreBar label="Tannins" value={profile.beverage.wine_tannin_pref} max={5} color="#7D3C98" />
              <div style={{ marginTop: SP.md, ...TYPE.bodySmall, color: T.darkTextSec, lineHeight: "24px" }}>
                <div><span style={{ fontWeight: 700, color: T.cream }}>Styles: </span>{(profile.beverage.wine_style_pref || []).join(", ")}</div>
                <div><span style={{ fontWeight: 700, color: T.cream }}>Regions: </span>{(profile.beverage.wine_region_pref || []).join(", ")}</div>
              </div>
            </>
          ) : (
            <div style={{ ...TYPE.bodySmall, color: T.darkTextSec }}>Non-alcoholic only</div>
          )}
        </ProfileLayer>

        <ProfileLayer
          icon="🎯" title="Context & Occasions" subtitle="When, where, how you dine"
          accent={T.teal || "#3AA89F"} expanded={!isOffline && expandedLayer === "context"}
          onToggle={isOffline ? undefined : () => setExpandedLayer(expandedLayer === "context" ? null : "context")}
          onEdit={isOffline ? undefined : () => openEdit("context")}
        >
          <div style={{ ...TYPE.bodySmall, color: T.darkTextSec, lineHeight: "24px" }}>
            {(profile.context?.default_occasions || []).length > 0
              ? <div><span style={{ fontWeight: 700, color: T.cream }}>Occasions: </span>{profile.context.default_occasions.join(", ")}</div>
              : <div>No occasions set</div>}
            {profile.context?.current_mode && <div><span style={{ fontWeight: 700, color: T.cream }}>Mode: </span>{profile.context.current_mode}</div>}
            {profile.context?.budget_signal && <div><span style={{ fontWeight: 700, color: T.cream }}>Budget: </span>{profile.context.budget_signal}</div>}
            {profile.context?.preferred_meal_period && <div><span style={{ fontWeight: 700, color: T.cream }}>Meal: </span>{profile.context.preferred_meal_period}</div>}
          </div>
        </ProfileLayer>
      </div>

      {/* ── Phase 1: Level 2 Slider Refinement Overlay (taste + beverage_wine) ── */}
      {level2Section && (() => {
        const sliders = LEVEL2_SLIDERS[level2Section] || [];
        const titleMap = { taste: "Fine-tune your taste", beverage_wine: "Fine-tune your wine" };
        const submitLevel2 = () => {
          // Optimistic local profile update
          setProfile(p => {
            const next = JSON.parse(JSON.stringify(p));
            if (level2Section === "beverage_wine") {
              next.beverage = next.beverage || {};
              Object.assign(next.beverage, level2Values);
            } else {
              next.taste = next.taste || {};
              Object.assign(next.taste, level2Values);
            }
            const prog = { ...(next.refinement_progress || {}) };
            prog[level2Section] = Math.max(prog[level2Section] || 0, 2);
            // Also bump non-suffixed section name so the grid reflects it
            if (level2Section === "beverage_wine") {
              prog["beverage"] = Math.max(prog["beverage"] || 0, 2);
            }
            next.refinement_progress = prog;
            return next;
          });
          // POST to backend
          if (profile._foodId) {
            const token = localStorage.getItem("sb_token");
            fetch(`${API_BASE}/food-id/${profile._foodId}/refine`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({
                answers: [],
                sliders: level2Values,
                section: level2Section,
                level: 2,
              }),
            }).then(r => r.json()).then(data => {
              if (data.completeness != null) {
                setProfile(p => ({
                  ...p,
                  completeness_score: Math.max(p.completeness_score || 0, data.completeness),
                }));
              }
              // Refetch authoritative profile so badges/progress are truthful
              const tk = localStorage.getItem("sb_token");
              fetch(`${API_BASE}/food-id/me`, { headers: tk ? { Authorization: `Bearer ${tk}` } : {} })
                .then(r => r.ok ? r.json() : null)
                .then(fresh => {
                  if (!fresh) return;
                  setProfile(p => ({
                    ...p,
                    ...fresh,
                    _foodId: fresh.food_id || p._foodId,
                    completeness_score: Math.max(p.completeness_score || 0, fresh.completeness || 0),
                  }));
                }).catch(() => {});
            }).catch(() => {});
          }
          addActivity({ type: "profile_update", icon: "🎯", text: `Refined ${level2Section === "beverage_wine" ? "wine" : "taste"} — Level 2 sliders` });
          setLevel2Section(null);
          setLevel2Values({});
        };
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 101, display: "flex", flexDirection: "column", padding: SP.lg, backdropFilter: "blur(10px)", overflowY: "auto" }}>
            <div style={{ maxWidth: 480, width: "100%", margin: "0 auto", paddingTop: 40 }}>
              <div style={{ ...TYPE.overline, color: T.gold, marginBottom: SP.xs }}>LEVEL 2 · PRECISE</div>
              <h2 style={{ ...TYPE.h1, color: T.cream, marginBottom: SP.md }}>{titleMap[level2Section] || "Fine-tune"}</h2>
              <p style={{ ...TYPE.bodySmall, color: T.darkTextSec, marginBottom: SP.lg }}>
                Slide each scale to match how you actually like it.
              </p>
              {sliders.map(s => {
                const v = level2Values[s.key] ?? 3;
                const pct = ((v - s.min) / (s.max - s.min)) * 100;
                return (
                  <div key={s.key} style={{ marginBottom: SP.lg }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <span style={{ ...TYPE.bodySmall, fontWeight: 700, color: T.cream }}>{s.label}</span>
                      <span style={{ ...TYPE.caption, color: T.gold, fontWeight: 700 }}>{v}</span>
                    </div>
                    <div style={{ padding: "14px 16px", background: "rgba(255,255,255,0.05)", borderRadius: RAD.lg, border: `1px solid ${T.gold}20` }}>
                      <input
                        type="range"
                        min={s.min}
                        max={s.max}
                        value={v}
                        onChange={e => setLevel2Values(d => ({ ...d, [s.key]: +e.target.value }))}
                        style={{ background: `linear-gradient(to right, ${T.gold} 0%, ${T.gold} ${pct}%, ${T.gold}20 ${pct}%, ${T.gold}20 100%)` }}
                      />
                      <div style={{ display: "flex", justifyContent: "space-between", ...TYPE.overline, color: T.darkTextSec, marginTop: 4 }}>
                        <span>{s.min}</span><span>{s.max}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
              <GoldBtn onClick={submitLevel2}>Save Preferences</GoldBtn>
              <button onClick={() => { setLevel2Section(null); setLevel2Values({}); }} style={{
                display: "block", width: "100%", textAlign: "center", marginTop: SP.md,
                ...TYPE.bodySmall, fontWeight: 600, color: T.darkTextSec,
                background: "none", border: "none", cursor: "pointer", fontFamily: "Montserrat",
              }}>
                Cancel
              </button>
            </div>
          </div>
        );
      })()}

      {/* ── Active Refinement Swipe Overlay ── */}
      {refineSection && (() => {
        const sectionObj = REFINEMENT_SECTIONS.find(s => s.key === refineSection);
        if (!sectionObj) return null;
        // Use the memoized deck (built once when the section opened) so each
        // re-render doesn't reshuffle the pool and surface different tiles.
        const answeredIds = loadAnsweredTileIds();
        const sectionCards = refineDeck;
        const currentCard = sectionCards[refineIndex];

        const handleRefineComplete = () => {
          const ref = refineData;
          // Track answered tile IDs for future non-repetition
          const answeredInSession = sectionCards.slice(0, refineIndex).map(c => c.id);
          if (answeredInSession.length > 0) markTilesAnswered(answeredInSession);

          setProfile(p => {
            const next = JSON.parse(JSON.stringify(p));
            // Taste updates
            if (ref.sweetness_pref !== undefined) next.taste.sweetness_pref = ref.sweetness_pref;
            if (ref.acidity_pref !== undefined) next.taste.acidity_pref = ref.acidity_pref;
            if (ref.bitterness_pref !== undefined) next.taste.bitterness_pref = ref.bitterness_pref;
            if (ref.umami_pref !== undefined) next.taste.umami_pref = ref.umami_pref;
            if (ref.richness_pref !== undefined) next.taste.richness_pref = ref.richness_pref;
            if (ref.accepts_raw !== undefined) next.taste.accepts_raw = ref.accepts_raw;
            if (ref.indulgence_default !== undefined) next.taste.indulgence_default = ref.indulgence_default;
            if (ref.health_default !== undefined) next.taste.health_default = ref.health_default;
            if (ref.portion_pref) next.taste.portion_pref = ref.portion_pref;
            if (ref.preferred_textures?.length) {
              next.taste.preferred_textures = [...new Set([...(next.taste.preferred_textures || []), ...ref.preferred_textures])];
              next.taste.avoided_textures = (next.taste.avoided_textures || []).filter(t => !ref.preferred_textures.includes(t));
            }
            if (ref.avoided_textures?.length) {
              next.taste.avoided_textures = [...new Set([...(next.taste.avoided_textures || []), ...ref.avoided_textures])];
              next.taste.preferred_textures = (next.taste.preferred_textures || []).filter(t => !ref.avoided_textures.includes(t));
            }
            // Cuisine updates
            if (ref.preferred_cooking?.length) next.cuisine.preferred_cooking = [...new Set([...(next.cuisine.preferred_cooking || []), ...ref.preferred_cooking])];
            if (ref.novelty_pref !== undefined) next.cuisine.novelty_pref = ref.novelty_pref;
            if (ref.cuisine_boost) {
              for (const [c, s] of Object.entries(ref.cuisine_boost)) {
                next.cuisine.cuisine_affinities[c] = Math.max(next.cuisine.cuisine_affinities[c] || 0, s);
              }
            }
            if (ref.protein_boost?.length) next.cuisine.protein_ranking = [...new Set([...(next.cuisine.protein_ranking || []), ...ref.protein_boost])];
            if (ref.avoided_proteins?.length) next.cuisine.avoided_proteins = [...new Set([...(next.cuisine.avoided_proteins || []), ...ref.avoided_proteins])];
            // Beverage updates (wine, cocktails, beer, NA)
            if (ref.wine_style_pref?.length) next.beverage.wine_style_pref = [...new Set([...(next.beverage.wine_style_pref || []), ...ref.wine_style_pref])];
            if (ref.wine_body_pref !== undefined) next.beverage.wine_body_pref = ref.wine_body_pref;
            if (ref.wine_tannin_pref !== undefined) next.beverage.wine_tannin_pref = ref.wine_tannin_pref;
            if (ref.wine_acidity_pref !== undefined) next.beverage.wine_acidity_pref = ref.wine_acidity_pref;
            if (ref.wine_sweetness_pref !== undefined) next.beverage.wine_sweetness_pref = ref.wine_sweetness_pref;
            if (ref.wine_oak_pref !== undefined) next.beverage.wine_oak_pref = ref.wine_oak_pref;
            if (ref.wine_region_pref?.length) next.beverage.wine_region_pref = [...new Set([...(next.beverage.wine_region_pref || []), ...ref.wine_region_pref])];
            if (ref.cocktail_style_pref?.length) next.beverage.cocktail_style_pref = [...new Set([...(next.beverage.cocktail_style_pref || []), ...ref.cocktail_style_pref])];
            if (ref.spirit_pref?.length) next.beverage.spirit_pref = [...new Set([...(next.beverage.spirit_pref || []), ...ref.spirit_pref])];
            if (ref.na_bev_pref?.length) next.beverage.na_bev_pref = [...new Set([...(next.beverage.na_bev_pref || []), ...ref.na_bev_pref])];
            // Update confidence scores
            const scores = { ...(next._confidenceScores || {}) };
            sectionCards.slice(0, refineIndex).forEach(card => {
              card.targets.forEach(t => { if (scores[t] !== undefined) scores[t] = Math.min(1, (scores[t] || 0) + card.gain); });
            });
            next._confidenceScores = scores;
            // Optimistic: estimate completeness bump while waiting for server
            next.completeness_score = Math.min(100, (next.completeness_score || 0) + Math.min(5, refineIndex));
            return next;
          });
          addActivity({ type: "profile_update", icon: "🎯", text: `Refined ${sectionObj?.label || "profile"} — ${refineIndex} new preferences learned` });
          // Persist to backend — send per-card answers with individual effects
          if (profile._foodId) {
            const answeredCards = sectionCards.slice(0, refineIndex);
            const answers = answeredCards.map(card => ({ card_id: card.id || card.name, answer: refineData }));
            const scores = { ...(profile._confidenceScores || {}) };
            answeredCards.forEach(card => {
              card.targets.forEach(t => { if (scores[t] !== undefined) scores[t] = Math.min(1, (scores[t] || 0) + card.gain); });
            });
            const token = localStorage.getItem("sb_token");
            fetch(`${API_BASE}/food-id/${profile._foodId}/refine`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({ answers, scores, section: refineSection, level: 1 }),
            }).then(r => r.json()).then(data => {
              // Server is authoritative BUT completeness must never regress —
              // backend has a monotonic guard, but clamp client-side too as
              // belt-and-suspenders against stale recomputes.
              if (data.completeness != null) {
                setProfile(p => ({
                  ...p,
                  completeness_score: Math.max(p.completeness_score || 0, data.completeness),
                }));
              }
              // Bump local refinement_progress optimistically to at least 1
              setProfile(p => {
                const prog = { ...(p.refinement_progress || {}) };
                prog[refineSection] = Math.max(prog[refineSection] || 0, 1);
                return { ...p, refinement_progress: prog };
              });
              // Refetch authoritative profile so badges + progress reflect truth.
              const tk = localStorage.getItem("sb_token");
              fetch(`${API_BASE}/food-id/me`, { headers: tk ? { Authorization: `Bearer ${tk}` } : {} })
                .then(r => r.ok ? r.json() : null)
                .then(fresh => {
                  if (!fresh) return;
                  setProfile(p => ({
                    ...p,
                    ...fresh,
                    _foodId: fresh.food_id || p._foodId,
                    // never regress completeness on refetch
                    completeness_score: Math.max(p.completeness_score || 0, fresh.completeness || 0),
                  }));
                }).catch(() => {});
            }).catch(() => {});
          }
          setRefineSection(null);
          setRefineDeck([]);
          setRefineIndex(0);
          setRefineData({});
        };

        if (!currentCard) {
          // Only auto-complete when we've actually traversed a non-empty deck
          if (sectionCards.length > 0 && refineIndex >= sectionCards.length) {
            handleRefineComplete();
          }
          return null;
        }

        const depthInfo = getSectionDepthInfo(refineSection, answeredIds);

        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 100, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: SP.lg, backdropFilter: "blur(8px)" }}>
            <div style={{ ...TYPE.overline, color: T.gold, marginBottom: SP.xs }}>
              {sectionObj.icon} REFINING: {sectionObj.label.toUpperCase()}
            </div>
            {depthInfo.answered > 0 && (
              <div style={{ ...TYPE.caption, color: T.darkTextSec, marginBottom: SP.md }}>
                {depthInfo.answered} answered · {depthInfo.remaining} remaining
              </div>
            )}
            <SwipeCard
              card={currentCard}
              index={refineIndex}
              total={sectionCards.length}
              onSwipe={(liked) => {
                const fx = currentCard.effects ? (liked ? currentCard.effects.right : currentCard.effects.left) : {};
                setRefineData(d => mergeEffects(d, fx || {}));
                setRefineIndex(i => i + 1);
              }}
            />
            <button onClick={() => { if (refineIndex > 0) handleRefineComplete(); else { setRefineSection(null); setRefineDeck([]); setRefineIndex(0); setRefineData({}); } }} style={{ ...TYPE.bodySmall, fontWeight: 600, color: T.darkTextSec, background: "none", border: "none", cursor: "pointer", fontFamily: "Montserrat", marginTop: SP.lg }}>
              {refineIndex > 0 ? "Done" : "Close"}
            </button>
          </div>
        );
      })()}

      {/* ── Further Refinement ── */}
      {!isOffline && <div style={{ padding: `${SP.lg}px ${SP.lg}px 0`, position: "relative", zIndex: 1 }}>
        <SectionLabel>Further Refinement</SectionLabel>
        <p style={{ ...TYPE.bodySmall, color: T.darkTextSec, marginBottom: SP.md, lineHeight: "20px" }}>
          Tap a category to answer targeted questions.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {REFINEMENT_SECTIONS.map(section => {
            const depth = getSectionDepthInfo(section.key, loadAnsweredTileIds());
            const progressMap = profile.refinement_progress || {};
            // beverage → beverage_wine for Level 2 mapping
            const level2Key = section.key === "beverage" ? "beverage_wine" : section.key;
            const savedLevel = progressMap[level2Key] || progressMap[section.key] || 0;
            // Derive progress from actual profile data so onboarding shows "Basic" not "New"
            const sectionDataMap = {
              texture: () => (profile.taste?.preferred_textures || []).length > 0,
              taste: () => profile.taste?.richness_pref != null || profile.taste?.umami_pref != null,
              cooking: () => (profile.cuisine?.preferred_cooking || []).length > 0,
              indulgence: () => profile.taste?.indulgence_default != null,
              novelty: () => profile.cuisine?.novelty_pref != null,
              cuisine: () => Object.keys(profile.cuisine?.cuisine_affinities || {}).length > 0,
              protein: () => (profile.cuisine?.protein_ranking || []).length > 0,
              beverage: () => profile.beverage?.drinks_alcohol != null || (profile.beverage?.wine_style_pref || []).length > 0,
            };
            const hasOnboardingData = sectionDataMap[section.key]?.() || false;
            const depthLevel = depth.answered >= 5 ? 2 : depth.answered > 0 ? 1 : 0;
            const progressLevel = Math.max(savedLevel, hasOnboardingData ? 1 : 0, depthLevel);
            const depthLabel = progressLevel >= 3 ? "Expert"
              : progressLevel === 2 ? "Good"
              : progressLevel === 1 ? "Basic"
              : "New";
            const depthColor = progressLevel >= 2 ? T.safetyGreen
              : progressLevel === 1 ? T.gold : T.darkTextSec;
            const hasLevel2 = !!LEVEL2_SLIDERS[level2Key];
            const handleTap = () => {
              // Phase 1 pilot: Level 2 sliders only for taste + beverage_wine after Level 1 done
              if (hasLevel2 && progressLevel >= 1 && progressLevel < 3) {
                // Prefill with existing profile values
                const src = level2Key === "beverage_wine" ? (profile.beverage || {}) : (profile.taste || {});
                const init = {};
                LEVEL2_SLIDERS[level2Key].forEach(s => { init[s.key] = src[s.key] ?? 3; });
                setLevel2Values(init);
                setLevel2Section(level2Key);
                return;
              }
              // Otherwise fall back to Level 1 swipe flow.
              // Build deck ONCE on open so re-renders don't reshuffle the pool.
              const answeredOnOpen = loadAnsweredTileIds();
              const deck = selectTilesForRefine(section.key, profile, answeredOnOpen, 8);
              setRefineDeck(deck);
              setRefineSection(section.key); setRefineIndex(0); setRefineData({});
            };
            return (
              <button key={section.key} onClick={handleTap} style={{
                background: "rgba(255,255,255,0.05)", border: `1px solid ${T.gold}20`, borderRadius: RAD.md,
                padding: SP.md, cursor: "pointer", fontFamily: "Montserrat", textAlign: "left",
                transition: "all 0.2s", backdropFilter: "blur(8px)",
                position: "relative", overflow: "hidden",
              }}
              onMouseOver={(e) => { e.currentTarget.style.borderColor = T.gold; }}
              onMouseOut={(e) => { e.currentTarget.style.borderColor = `${T.gold}20`; }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: SP.sm }}>
                  <span style={{ fontSize: 24 }}>{section.icon}</span>
                  <span style={{ ...TYPE.overline, color: depthColor, fontSize: 9, fontWeight: 700 }}>
                    {depthLabel}
                  </span>
                </div>
                <div style={{ ...TYPE.bodySmall, fontWeight: 700, color: T.cream, marginBottom: 2 }}>{section.label}</div>
                <div style={{ ...TYPE.caption, color: T.darkTextSec }}>{section.desc}</div>
              </button>
            );
          })}
        </div>
      </div>}

      {/* ── Room Preferences — Coming Soon ── */}
      <div style={{ margin: `${SP.lg}px ${SP.lg}px 0`, padding: SP.lg, background: "rgba(255,255,255,0.04)", borderRadius: RAD.lg, border: `1px dashed ${T.gold}25`, textAlign: "center", position: "relative", zIndex: 1 }}>
        <div style={{ fontSize: 32, marginBottom: SP.sm }}>🏨</div>
        <div style={{ ...TYPE.h2, color: T.cream, marginBottom: SP.xs }}>Room Preferences</div>
        <p style={{ ...TYPE.bodySmall, color: T.darkTextSec, lineHeight: "20px", marginBottom: SP.md }}>
          Temperature, pillow type, minibar stocking, wake-up preferences — coming soon.
        </p>
        <div style={{ ...TYPE.caption, fontWeight: 700, color: T.gold, padding: "8px 16px", background: `${T.gold}15`, borderRadius: RAD.pill, display: "inline-block" }}>
          Coming Soon
        </div>
      </div>

      {/* Completeness nudge */}
      {profile.completeness_score < 85 && (
        <div style={{ margin: `${SP.lg}px ${SP.lg}px 0`, padding: SP.md, background: `${T.gold}12`, borderRadius: RAD.md, border: `1px solid ${T.gold}25`, position: "relative", zIndex: 1 }}>
          <div style={{ ...TYPE.bodySmall, fontWeight: 700, color: T.gold, marginBottom: 4 }}>
            ✦ Your Food ID is {profile.completeness_score}% complete
          </div>
          <div style={{ ...TYPE.bodySmall, color: T.darkTextSec }}>
            Refine your profile or scan a menu to push past 80%. Every interaction makes recommendations sharper.
          </div>
        </div>
      )}

      {/* ── Edit Section Overlays ── */}
      {editSection && (
        <div style={{ position: "fixed", inset: 0, background: T.darkBg, zIndex: 100, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
          {/* Edit Header */}
          <div style={{ position: "sticky", top: 0, background: T.darkBg, borderBottom: `1px solid ${T.gold}20`, padding: `${SP.md}px ${SP.lg}px`, display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 101 }}>
            <button onClick={closeEdit} style={{ background: "none", border: "none", color: T.darkTextSec, ...TYPE.bodySmall, fontWeight: 600, cursor: "pointer", fontFamily: "Montserrat", padding: SP.sm }}>Cancel</button>
            <div style={{ ...TYPE.bodySmall, fontWeight: 700, color: T.cream, textTransform: "capitalize" }}>Edit {editSection}</div>
            <button onClick={saveEdit} disabled={editSaving} style={{ background: "none", border: "none", color: editSaving ? T.darkTextSec : T.gold, ...TYPE.bodySmall, fontWeight: 700, cursor: editSaving ? "default" : "pointer", fontFamily: "Montserrat", padding: SP.sm }}>{editSaving ? "Saving..." : "Done"}</button>
          </div>

          <div style={{ padding: SP.lg }}>
            {/* ── Edit Safety ── */}
            {editSection === "safety" && (<>
              <SectionLabel>Allergens</SectionLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: SP.lg }}>
                {ALLERGENS.filter(a => a.key !== "none").map(a => {
                  const active = editData[a.key];
                  return (
                    <button key={a.key} onClick={() => toggleAllergenEdit(a.key, a.label)} style={{
                      padding: "10px 16px", borderRadius: RAD.pill, cursor: "pointer", fontFamily: "Montserrat",
                      background: active ? `${T.safetyRed}25` : "rgba(255,255,255,0.05)",
                      border: `1.5px solid ${active ? T.safetyRed : T.gold + "20"}`,
                      color: active ? T.safetyRed : T.darkTextSec, ...TYPE.bodySmall, fontWeight: 600,
                      transition: "all 0.2s",
                    }}>{a.icon} {a.label}</button>
                  );
                })}
              </div>

              <SectionLabel>Dietary</SectionLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: SP.lg }}>
                {DIETARY.map(d => {
                  const active = editData[d.key];
                  return (
                    <button key={d.key} onClick={() => updateField(d.key, !active)} style={{
                      padding: "10px 16px", borderRadius: RAD.pill, cursor: "pointer", fontFamily: "Montserrat",
                      background: active ? `${T.safetyGreen}25` : "rgba(255,255,255,0.05)",
                      border: `1.5px solid ${active ? T.safetyGreen : T.gold + "20"}`,
                      color: active ? T.safetyGreen : T.darkTextSec, ...TYPE.bodySmall, fontWeight: 600,
                      transition: "all 0.2s",
                    }}>{d.icon} {d.label}</button>
                  );
                })}
              </div>

              <SectionLabel>Never Eat</SectionLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: SP.lg }}>
                {NEVER_EAT.map(item => {
                  const active = (editData.never_eat_list || []).includes(item);
                  return (
                    <button key={item} onClick={() => toggleArrayItem("never_eat_list", item)} style={{
                      padding: "10px 16px", borderRadius: RAD.pill, cursor: "pointer", fontFamily: "Montserrat",
                      background: active ? `${T.safetyRed}15` : "rgba(255,255,255,0.05)",
                      border: `1.5px solid ${active ? T.safetyRed + "60" : T.gold + "20"}`,
                      color: active ? T.cream : T.darkTextSec, ...TYPE.bodySmall, fontWeight: 600,
                      transition: "all 0.2s",
                    }}>{item}</button>
                  );
                })}
              </div>

              {/* ── Milk & Dairy — hidden when dairy allergy is active ── */}
              {!editData.has_dairy && (<>
                <SectionLabel>Milk & Dairy</SectionLabel>
                {/* Lactose intolerant toggle */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", background: "rgba(255,255,255,0.05)", borderRadius: RAD.md, marginBottom: SP.sm, border: `1px solid ${T.gold}20` }}>
                  <span style={{ ...TYPE.bodySmall, fontWeight: 600, color: T.cream }}>🥛 Lactose intolerant</span>
                  <button
                    role="switch" aria-checked={!!editData.milk_intolerant}
                    onClick={() => updateField("milk_intolerant", !editData.milk_intolerant)}
                    style={{ width: 44, height: 24, borderRadius: 12, border: "none", cursor: "pointer", padding: 2, transition: "background 0.2s", background: editData.milk_intolerant ? T.gold : "rgba(255,255,255,0.15)", position: "relative" }}
                  >
                    <span style={{ display: "block", width: 20, height: 20, borderRadius: "50%", background: T.cream, transition: "transform 0.2s", transform: editData.milk_intolerant ? "translateX(20px)" : "translateX(0px)" }} />
                  </button>
                </div>
                {/* Cow's milk type — single select, hidden when intolerant */}
                {!editData.milk_intolerant && (<>
                  <div style={{ ...TYPE.caption, color: T.darkTextSec, marginBottom: SP.sm }}>Preferred cow's milk:</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: SP.md }}>
                    {[
                      { key: "full_fat",     label: "Full Fat" },
                      { key: "semi_skimmed", label: "Semi-Skimmed" },
                      { key: "skimmed",      label: "Skimmed" },
                      { key: "none",         label: "No preference" },
                    ].map(opt => (
                      <button key={opt.key} onClick={() => updateField("milk_preference", editData.milk_preference === opt.key ? null : opt.key)} style={{
                        padding: "10px 16px", borderRadius: RAD.pill, cursor: "pointer", fontFamily: "Montserrat",
                        background: editData.milk_preference === opt.key ? `${T.gold}25` : "rgba(255,255,255,0.05)",
                        border: `1.5px solid ${editData.milk_preference === opt.key ? T.gold : T.gold + "20"}`,
                        color: editData.milk_preference === opt.key ? T.gold : T.darkTextSec, ...TYPE.bodySmall, fontWeight: 600,
                        transition: "all 0.2s",
                      }}>🥛 {opt.label}</button>
                    ))}
                  </div>
                </>)}
                {/* Alternatives — multi-select */}
                <div style={{ ...TYPE.caption, color: T.darkTextSec, marginBottom: SP.sm }}>Preferred alternatives:</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: SP.lg }}>
                  {[
                    { key: "oat",          label: "Oat",          icon: "🌾" },
                    { key: "almond",       label: "Almond",       icon: "🌰" },
                    { key: "soy",          label: "Soy",          icon: "🫘" },
                    { key: "coconut",      label: "Coconut",      icon: "🥥" },
                    { key: "rice",         label: "Rice",         icon: "🍚" },
                    { key: "lactose_free", label: "Lactose-Free", icon: "🥛" },
                  ].map(opt => {
                    const active = (editData.milk_alternatives || []).includes(opt.key);
                    return (
                      <button key={opt.key} onClick={() => toggleArrayItem("milk_alternatives", opt.key)} style={{
                        padding: "10px 16px", borderRadius: RAD.pill, cursor: "pointer", fontFamily: "Montserrat",
                        background: active ? `${T.gold}25` : "rgba(255,255,255,0.05)",
                        border: `1.5px solid ${active ? T.gold : T.gold + "20"}`,
                        color: active ? T.gold : T.darkTextSec, ...TYPE.bodySmall, fontWeight: 600,
                        transition: "all 0.2s",
                      }}>{opt.icon} {opt.label}</button>
                    );
                  })}
                </div>
              </>)}
            </>)}

            {/* ── Edit Taste ── */}
            {editSection === "taste" && (<>
              <SectionLabel>Spice Tolerance</SectionLabel>
              <div style={{ marginBottom: SP.lg }}>
                <div style={{ display: "flex", justifyContent: "space-between", ...TYPE.bodySmall, color: T.darkTextSec, marginBottom: SP.sm }}>
                  <span>Mild</span><span>{editData.spice_tolerance ?? 0}/4</span><span>Fiery</span>
                </div>
                <input type="range" min="0" max="4" value={editData.spice_tolerance ?? 0} aria-label="Spice tolerance" onChange={e => updateField("spice_tolerance", parseInt(e.target.value))} style={{ width: "100%", accentColor: T.gold }} />
              </div>

              {[
                { key: "richness_pref", label: "Richness", max: 5 },
                { key: "umami_pref", label: "Umami", max: 5 },
                { key: "sweetness_pref", label: "Sweetness", max: 5 },
                { key: "acidity_pref", label: "Acidity", max: 5 },
                { key: "bitterness_pref", label: "Bitterness", max: 5 },
              ].map(s => (
                <div key={s.key} style={{ marginBottom: SP.md }}>
                  <div style={{ display: "flex", justifyContent: "space-between", ...TYPE.bodySmall, color: T.darkTextSec, marginBottom: SP.xs }}>
                    <span>{s.label}</span><span>{editData[s.key] ?? 0}/{s.max}</span>
                  </div>
                  <input type="range" min="0" max={s.max} value={editData[s.key] ?? 0} aria-label={s.label} onChange={e => updateField(s.key, parseInt(e.target.value))} style={{ width: "100%", accentColor: T.gold }} />
                </div>
              ))}

              <SectionLabel>Preferred Textures</SectionLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: SP.lg }}>
                {TEXTURE_OPTIONS.map(t => {
                  const active = (editData.preferred_textures || []).includes(t);
                  return (
                    <button key={t} onClick={() => toggleTexture("preferred_textures", t)} style={{
                      padding: "8px 14px", borderRadius: RAD.pill, cursor: "pointer", fontFamily: "Montserrat",
                      background: active ? `${T.gold}25` : "rgba(255,255,255,0.05)",
                      border: `1.5px solid ${active ? T.gold : T.gold + "20"}`,
                      color: active ? T.gold : T.darkTextSec, ...TYPE.bodySmall, fontWeight: 600,
                    }}>{t}</button>
                  );
                })}
              </div>

              <SectionLabel>Avoided Textures</SectionLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: SP.lg }}>
                {TEXTURE_OPTIONS.map(t => {
                  const active = (editData.avoided_textures || []).includes(t);
                  return (
                    <button key={t} onClick={() => toggleTexture("avoided_textures", t)} style={{
                      padding: "8px 14px", borderRadius: RAD.pill, cursor: "pointer", fontFamily: "Montserrat",
                      background: active ? `${T.safetyRed}20` : "rgba(255,255,255,0.05)",
                      border: `1.5px solid ${active ? T.safetyRed + "60" : T.gold + "20"}`,
                      color: active ? T.safetyRed : T.darkTextSec, ...TYPE.bodySmall, fontWeight: 600,
                    }}>{t}</button>
                  );
                })}
              </div>

              <SectionLabel>Portion Preference</SectionLabel>
              <div style={{ display: "flex", gap: 8, marginBottom: SP.lg }}>
                {["small_plates", "standard", "large", "sharing"].map(p => (
                  <button key={p} onClick={() => updateField("portion_pref", p)} style={{
                    flex: 1, padding: "10px 8px", borderRadius: RAD.md, cursor: "pointer", fontFamily: "Montserrat",
                    background: editData.portion_pref === p ? `${T.gold}25` : "rgba(255,255,255,0.05)",
                    border: `1.5px solid ${editData.portion_pref === p ? T.gold : T.gold + "20"}`,
                    color: editData.portion_pref === p ? T.gold : T.darkTextSec, ...TYPE.caption, fontWeight: 600,
                    textTransform: "capitalize",
                  }}>{p.replace("_", " ")}</button>
                ))}
              </div>
            </>)}

            {/* ── Edit Cuisine ── */}
            {editSection === "cuisine" && (<>
              <SectionLabel>Cuisine Affinities</SectionLabel>
              <div style={{ marginBottom: SP.lg }}>
                {CUISINE_OPTIONS.map(c => {
                  // Look up affinity case-insensitively (DB may have mixed case from legacy data)
                  const aff = editData.cuisine_affinities || {};
                  const val = aff[c] || aff[c.toLowerCase()] || aff[c.toUpperCase()] || 0;
                  return (
                    <div key={c} style={{ marginBottom: SP.sm }}>
                      <div style={{ display: "flex", justifyContent: "space-between", ...TYPE.bodySmall, color: T.darkTextSec, marginBottom: 2 }}>
                        <span>{c}</span><span>{val}/5</span>
                      </div>
                      <input type="range" min="0" max="5" value={val} aria-label={`${c} cuisine preference`} onChange={e => {
                        const newAff = { ...(editData.cuisine_affinities || {}) };
                        // Remove any case variants, then write canonical Title Case
                        delete newAff[c.toLowerCase()]; delete newAff[c.toUpperCase()]; delete newAff[c];
                        const v = parseInt(e.target.value);
                        if (v > 0) newAff[c] = v;
                        updateField("cuisine_affinities", newAff);
                      }} style={{ width: "100%", accentColor: T.sapphire }} />
                    </div>
                  );
                })}
              </div>

              <SectionLabel>Protein Ranking</SectionLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: SP.lg }}>
                {PROTEIN_OPTIONS.map(p => {
                  const ranking = (editData.protein_ranking || []).map(x => x.toLowerCase());
                  const active = ranking.includes(p.toLowerCase());
                  return (
                    <button key={p} onClick={() => toggleArrayItem("protein_ranking", p)} style={{
                      padding: "8px 14px", borderRadius: RAD.pill, cursor: "pointer", fontFamily: "Montserrat",
                      background: active ? `${T.sapphire}25` : "rgba(255,255,255,0.05)",
                      border: `1.5px solid ${active ? T.sapphire : T.gold + "20"}`,
                      color: active ? T.sapphire : T.darkTextSec, ...TYPE.bodySmall, fontWeight: 600,
                    }}>{p}</button>
                  );
                })}
              </div>

              <SectionLabel>Preferred Cooking Methods</SectionLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: SP.lg }}>
                {COOKING_OPTIONS.map(c => {
                  const active = (editData.preferred_cooking || []).includes(c.toLowerCase());
                  return (
                    <button key={c} onClick={() => toggleArrayItem("preferred_cooking", c.toLowerCase())} style={{
                      padding: "8px 14px", borderRadius: RAD.pill, cursor: "pointer", fontFamily: "Montserrat",
                      background: active ? `${T.gold}25` : "rgba(255,255,255,0.05)",
                      border: `1.5px solid ${active ? T.gold : T.gold + "20"}`,
                      color: active ? T.gold : T.darkTextSec, ...TYPE.bodySmall, fontWeight: 600,
                    }}>{c}</button>
                  );
                })}
              </div>

              <SectionLabel>Loved Ingredients</SectionLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: SP.lg }}>
                {["Truffle", "Saffron", "Garlic", "Chili", "Ginger", "Lemon", "Basil", "Mint", "Parmesan", "Sesame", "Miso", "Coconut"].map(i => {
                  const active = (editData.loved_ingredients || []).includes(i.toLowerCase());
                  return (
                    <button key={i} onClick={() => toggleArrayItem("loved_ingredients", i.toLowerCase())} style={{
                      padding: "8px 14px", borderRadius: RAD.pill, cursor: "pointer", fontFamily: "Montserrat",
                      background: active ? `${T.safetyGreen}20` : "rgba(255,255,255,0.05)",
                      border: `1.5px solid ${active ? T.safetyGreen : T.gold + "20"}`,
                      color: active ? T.safetyGreen : T.darkTextSec, ...TYPE.bodySmall, fontWeight: 600,
                    }}>{i}</button>
                  );
                })}
              </div>

              <SectionLabel>Disliked Ingredients</SectionLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: SP.lg }}>
                {["Anchovy", "Cilantro", "Blue Cheese", "Liver", "Olives", "Capers", "Raw Onion", "Fennel", "Dill", "Liquorice"].map(i => {
                  const active = (editData.disliked_ingredients || []).includes(i.toLowerCase());
                  return (
                    <button key={i} onClick={() => toggleArrayItem("disliked_ingredients", i.toLowerCase())} style={{
                      padding: "8px 14px", borderRadius: RAD.pill, cursor: "pointer", fontFamily: "Montserrat",
                      background: active ? `${T.safetyRed}20` : "rgba(255,255,255,0.05)",
                      border: `1.5px solid ${active ? T.safetyRed + "60" : T.gold + "20"}`,
                      color: active ? T.safetyRed : T.darkTextSec, ...TYPE.bodySmall, fontWeight: 600,
                    }}>{i}</button>
                  );
                })}
              </div>

              <SectionLabel>Novelty Preference</SectionLabel>
              <div style={{ marginBottom: SP.lg }}>
                <div style={{ display: "flex", justifyContent: "space-between", ...TYPE.bodySmall, color: T.darkTextSec, marginBottom: SP.xs }}>
                  <span>Familiar</span><span>{editData.novelty_pref ?? 0}/5</span><span>Adventurous</span>
                </div>
                <input type="range" min="0" max="5" value={editData.novelty_pref ?? 0} aria-label="Novelty preference" onChange={e => updateField("novelty_pref", parseInt(e.target.value))} style={{ width: "100%", accentColor: T.sapphire }} />
              </div>
            </>)}

            {/* ── Edit Beverage ── */}
            {editSection === "beverage" && (<>
              <SectionLabel>Alcohol</SectionLabel>
              <div style={{ display: "flex", gap: 8, marginBottom: SP.lg }}>
                {[true, false].map(v => (
                  <button key={String(v)} onClick={() => updateField("drinks_alcohol", v)} style={{
                    flex: 1, padding: "12px", borderRadius: RAD.md, cursor: "pointer", fontFamily: "Montserrat",
                    background: editData.drinks_alcohol === v ? `${T.gold}25` : "rgba(255,255,255,0.05)",
                    border: `1.5px solid ${editData.drinks_alcohol === v ? T.gold : T.gold + "20"}`,
                    color: editData.drinks_alcohol === v ? T.gold : T.darkTextSec, ...TYPE.bodySmall, fontWeight: 600,
                  }}>{v ? "Yes, I drink alcohol" : "Non-alcoholic only"}</button>
                ))}
              </div>

              {editData.drinks_alcohol && (<>
                <SectionLabel>Wine Style</SectionLabel>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: SP.lg }}>
                  {WINE_STYLES.map(s => {
                    const active = (editData.wine_style_pref || []).includes(s);
                    return (
                      <button key={s} onClick={() => toggleArrayItem("wine_style_pref", s)} style={{
                        padding: "8px 14px", borderRadius: RAD.pill, cursor: "pointer", fontFamily: "Montserrat",
                        background: active ? "#7D3C9825" : "rgba(255,255,255,0.05)",
                        border: `1.5px solid ${active ? "#7D3C98" : T.gold + "20"}`,
                        color: active ? "#7D3C98" : T.darkTextSec, ...TYPE.bodySmall, fontWeight: 600,
                      }}>{s}</button>
                    );
                  })}
                </div>

                {[
                  { key: "wine_body_pref", label: "Wine Body", lo: "Light", hi: "Full" },
                  { key: "wine_tannin_pref", label: "Tannin", lo: "Soft", hi: "Grippy" },
                  { key: "wine_acidity_pref", label: "Acidity", lo: "Smooth", hi: "Crisp" },
                  { key: "wine_sweetness_pref", label: "Sweetness", lo: "Dry", hi: "Sweet" },
                  { key: "wine_oak_pref", label: "Oak", lo: "Unoaked", hi: "Heavy Oak" },
                ].map(s => (
                  <div key={s.key} style={{ marginBottom: SP.md }}>
                    <div style={{ display: "flex", justifyContent: "space-between", ...TYPE.bodySmall, color: T.darkTextSec, marginBottom: SP.xs }}>
                      <span>{s.lo}</span><span style={{ fontWeight: 700 }}>{s.label} {editData[s.key] ?? 0}/5</span><span>{s.hi}</span>
                    </div>
                    <input type="range" min="0" max="5" value={editData[s.key] ?? 0} aria-label={s.label} onChange={e => updateField(s.key, parseInt(e.target.value))} style={{ width: "100%", accentColor: "#7D3C98" }} />
                  </div>
                ))}

                <SectionLabel>Wine Regions</SectionLabel>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: SP.lg }}>
                  {WINE_REGION_OPTIONS.map(r => {
                    const active = (editData.wine_region_pref || []).includes(r);
                    return (
                      <button key={r} onClick={() => toggleArrayItem("wine_region_pref", r)} style={{
                        padding: "8px 14px", borderRadius: RAD.pill, cursor: "pointer", fontFamily: "Montserrat",
                        background: active ? "#7D3C9820" : "rgba(255,255,255,0.05)",
                        border: `1.5px solid ${active ? "#7D3C98" : T.gold + "20"}`,
                        color: active ? "#7D3C98" : T.darkTextSec, ...TYPE.bodySmall, fontWeight: 600,
                      }}>{r}</button>
                    );
                  })}
                </div>

                <SectionLabel>Cocktail Style</SectionLabel>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: SP.lg }}>
                  {COCKTAIL_STYLE_OPTIONS.map(s => {
                    const active = (editData.cocktail_style_pref || []).includes(s);
                    return (
                      <button key={s} onClick={() => toggleArrayItem("cocktail_style_pref", s)} style={{
                        padding: "8px 14px", borderRadius: RAD.pill, cursor: "pointer", fontFamily: "Montserrat",
                        background: active ? `${T.gold}25` : "rgba(255,255,255,0.05)",
                        border: `1.5px solid ${active ? T.gold : T.gold + "20"}`,
                        color: active ? T.gold : T.darkTextSec, ...TYPE.bodySmall, fontWeight: 600,
                      }}>{s}</button>
                    );
                  })}
                </div>

                <SectionLabel>Spirit Preference</SectionLabel>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: SP.lg }}>
                  {SPIRIT_OPTIONS.map(s => {
                    const active = (editData.spirit_pref || []).includes(s);
                    return (
                      <button key={s} onClick={() => toggleArrayItem("spirit_pref", s)} style={{
                        padding: "8px 14px", borderRadius: RAD.pill, cursor: "pointer", fontFamily: "Montserrat",
                        background: active ? `${T.gold}25` : "rgba(255,255,255,0.05)",
                        border: `1.5px solid ${active ? T.gold : T.gold + "20"}`,
                        color: active ? T.gold : T.darkTextSec, ...TYPE.bodySmall, fontWeight: 600,
                      }}>{s}</button>
                    );
                  })}
                </div>
              </>)}

              <SectionLabel>Non-Alcoholic Beverages</SectionLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: SP.lg }}>
                {NA_BEV_OPTIONS.map(b => {
                  const active = (editData.na_bev_pref || []).includes(b);
                  return (
                    <button key={b} onClick={() => toggleArrayItem("na_bev_pref", b)} style={{
                      padding: "8px 14px", borderRadius: RAD.pill, cursor: "pointer", fontFamily: "Montserrat",
                      background: active ? `${T.gold}25` : "rgba(255,255,255,0.05)",
                      border: `1.5px solid ${active ? T.gold : T.gold + "20"}`,
                      color: active ? T.gold : T.darkTextSec, ...TYPE.bodySmall, fontWeight: 600,
                    }}>{b}</button>
                  );
                })}
              </div>
            </>)}

            {/* ── Edit Context ── */}
            {editSection === "context" && (<>
              <SectionLabel>Occasions</SectionLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: SP.lg }}>
                {OCCASIONS.map(o => {
                  const active = (editData.default_occasions || []).includes(o.key);
                  return (
                    <button key={o.key} onClick={() => toggleArrayItem("default_occasions", o.key)} style={{
                      padding: "10px 16px", borderRadius: RAD.pill, cursor: "pointer", fontFamily: "Montserrat",
                      background: active ? `${T.gold}25` : "rgba(255,255,255,0.05)",
                      border: `1.5px solid ${active ? T.gold : T.gold + "20"}`,
                      color: active ? T.gold : T.darkTextSec, ...TYPE.bodySmall, fontWeight: 600,
                    }}>{o.icon} {o.label}</button>
                  );
                })}
              </div>

              <SectionLabel>Preferred Meal Period</SectionLabel>
              <div style={{ display: "flex", gap: 8, marginBottom: SP.lg }}>
                {MEAL_PERIODS.map(p => (
                  <button key={p} onClick={() => updateField("preferred_meal_period", p)} style={{
                    flex: 1, padding: "10px 8px", borderRadius: RAD.md, cursor: "pointer", fontFamily: "Montserrat",
                    background: editData.preferred_meal_period === p ? `${T.gold}25` : "rgba(255,255,255,0.05)",
                    border: `1.5px solid ${editData.preferred_meal_period === p ? T.gold : T.gold + "20"}`,
                    color: editData.preferred_meal_period === p ? T.gold : T.darkTextSec, ...TYPE.caption, fontWeight: 600,
                    textTransform: "capitalize",
                  }}>{p}</button>
                ))}
              </div>

              <SectionLabel>Dining Mode</SectionLabel>
              <div style={{ display: "flex", gap: 8, marginBottom: SP.lg }}>
                {MODES.map(m => (
                  <button key={m} onClick={() => updateField("current_mode", m)} style={{
                    flex: 1, padding: "10px 8px", borderRadius: RAD.md, cursor: "pointer", fontFamily: "Montserrat",
                    background: editData.current_mode === m ? `${T.gold}25` : "rgba(255,255,255,0.05)",
                    border: `1.5px solid ${editData.current_mode === m ? T.gold : T.gold + "20"}`,
                    color: editData.current_mode === m ? T.gold : T.darkTextSec, ...TYPE.caption, fontWeight: 600,
                    textTransform: "capitalize",
                  }}>{m}</button>
                ))}
              </div>

              <SectionLabel>Budget Signal</SectionLabel>
              <div style={{ display: "flex", gap: 8, marginBottom: SP.lg }}>
                {BUDGETS.map(b => (
                  <button key={b} onClick={() => updateField("budget_signal", b)} style={{
                    flex: 1, padding: "10px 8px", borderRadius: RAD.md, cursor: "pointer", fontFamily: "Montserrat",
                    background: editData.budget_signal === b ? `${T.gold}25` : "rgba(255,255,255,0.05)",
                    border: `1.5px solid ${editData.budget_signal === b ? T.gold : T.gold + "20"}`,
                    color: editData.budget_signal === b ? T.gold : T.darkTextSec, ...TYPE.caption, fontWeight: 600,
                    textTransform: "capitalize",
                  }}>{b}</button>
                ))}
              </div>

              <SectionLabel>Travel Mode</SectionLabel>
              <div style={{ display: "flex", gap: 8, marginBottom: SP.lg }}>
                {[true, false].map(v => (
                  <button key={String(v)} onClick={() => updateField("travel_mode", v)} style={{
                    flex: 1, padding: "12px", borderRadius: RAD.md, cursor: "pointer", fontFamily: "Montserrat",
                    background: editData.travel_mode === v ? `${T.gold}25` : "rgba(255,255,255,0.05)",
                    border: `1.5px solid ${editData.travel_mode === v ? T.gold : T.gold + "20"}`,
                    color: editData.travel_mode === v ? T.gold : T.darkTextSec, ...TYPE.bodySmall, fontWeight: 600,
                  }}>{v ? "Travelling" : "At Home"}</button>
                ))}
              </div>
            </>)}
          </div>
        </div>
      )}

      {/* ── Confirm Dialogs ── */}
      {confirmDialog?.type === "allergen" && (
        <ConfirmDialog
          title="Remove Allergen?"
          message={`Removing ${confirmDialog.label} means dishes containing this allergen will appear in your recommendations. Are you sure?`}
          confirmLabel="Yes, Remove"
          cancelLabel="Keep"
          danger
          onConfirm={() => { updateField(confirmDialog.allergenKey, false); setConfirmDialog(null); }}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
      {confirmDialog?.type === "discard" && (
        <ConfirmDialog
          title="Discard Changes?"
          message="You have unsaved changes. Are you sure you want to discard them?"
          confirmLabel="Discard"
          cancelLabel="Keep Editing"
          onConfirm={() => { setEditSection(null); setEditData({}); setEditDirty(false); setConfirmDialog(null); }}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
    </div>
  );
};

const SummaryChip = ({ icon, label, color = T.darkTextSec, bg = `${T.cream}12` }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: RAD.pill, background: bg, ...TYPE.caption, fontWeight: 600, color }}>
    {icon} {label}
  </span>
);

const ProfileLayer = ({ icon, title, subtitle, accent, expanded, onToggle, onEdit, children }) => (
  <div style={{ marginBottom: 10, background: "rgba(255,255,255,0.05)", borderRadius: RAD.lg, border: `1px solid ${T.gold}20`, overflow: "hidden", backdropFilter: "blur(8px)" }}>
    <button type="button" onClick={onToggle} aria-expanded={!!expanded} aria-label={`${title}: ${subtitle}`} style={{ display: "flex", alignItems: "center", gap: SP.md, padding: `${SP.md}px 18px`, cursor: "pointer", background: "none", border: "none", width: "100%", textAlign: "left", fontFamily: "Montserrat" }}>
      <div aria-hidden="true" style={{ width: 40, height: 40, borderRadius: RAD.sm, background: `${T.gold}0A`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ ...TYPE.bodySmall, fontWeight: 700, color: T.cream }}>{title}</div>
        <div style={{ ...TYPE.caption, color: T.darkTextSec, marginTop: 1 }}>{subtitle}</div>
      </div>
      {onEdit && (
        <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); onEdit(); }} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); onEdit(); } }} aria-label={`Edit ${title}`} style={{
          background: "none", border: "none", cursor: "pointer", padding: 8,
          fontSize: 14, color: T.darkTextSec, transition: "color 0.2s",
        }} onMouseOver={(e) => e.currentTarget.style.color = T.gold} onMouseOut={(e) => e.currentTarget.style.color = T.darkTextSec}>
          ✏️
        </span>
      )}
      <span aria-hidden="true" style={{ fontSize: 12, color: T.darkTextSec, transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.25s ease" }}>▼</span>
    </button>
    {expanded && (
      <div className="anim-fadein" style={{ borderTop: `1px solid ${T.gold}15` }}>
        <div style={{ height: 3, background: accent }} />
        <div style={{ padding: `${SP.md}px 18px 18px` }}>{children}</div>
      </div>
    )}
  </div>
);

const CompletionRing = ({ pct }) => {
  const r = 22, circ = 2 * Math.PI * r;
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`Profile completeness ${pct} percent`} style={{ flexShrink: 0 }}>
      <circle cx="28" cy="28" r={r} fill="none" stroke={`${T.cream}15`} strokeWidth="4" />
      <circle cx="28" cy="28" r={r} fill="none" stroke={T.gold} strokeWidth="4"
        strokeDasharray={`${circ * pct / 100} ${circ}`}
        strokeLinecap="round" transform="rotate(-90 28 28)"
        style={{ transition: "stroke-dasharray 1s cubic-bezier(0.22,1,0.36,1)" }}
      />
      <text x="28" y="33" textAnchor="middle" fill={T.cream} fontSize="11" fontWeight="800" fontFamily="Montserrat" aria-hidden="true">
        {pct}%
      </text>
    </svg>
  );
};

// ─────────────────────────────────────────────────────────────
// SCREEN: SHARE (S1-S2) — spec: three-scope model
// ─────────────────────────────────────────────────────────────
// API base URL — adjust for production
const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:8765";

// ─────────────────────────────────────────────────────────────
// FRIENDLY ERROR MESSAGES — map raw Supabase/API errors
// ─────────────────────────────────────────────────────────────
const friendlyError = (rawError) => {
  const msg = typeof rawError === "string" ? rawError : rawError?.message || "";
  const lower = msg.toLowerCase();
  if (lower.includes("invalid login")) return "That email or password doesn't look right. Please try again.";
  if (lower.includes("already registered") || lower.includes("already been registered")) return "An account with this email already exists. Try signing in instead.";
  if (lower.includes("not confirmed") || lower.includes("email not confirmed")) return "Please check your email for a confirmation link, then try signing in.";
  if (lower.includes("network") || lower.includes("fetch") || lower.includes("failed to fetch")) return "Couldn't connect. Please check your internet and try again.";
  if (lower.includes("rate limit") || lower.includes("too many")) return "Too many attempts. Please wait a moment and try again.";
  if (lower.includes("weak password") || lower.includes("at least")) return "Password is too weak. Please use at least 6 characters.";
  if (lower.includes("invalid email")) return "Please enter a valid email address.";
  return "Something went wrong. Please try again.";
};

// ─────────────────────────────────────────────────────────────
// ERROR BOUNDARY — catches render crashes, shows branded screen
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// OFFLINE SAFETY CARD CACHE — persist allergy/dietary data for offline access
// ─────────────────────────────────────────────────────────────
const SAFETY_CARD_KEY = "consoome_safety_card";

const cacheSafetyCard = (profile) => {
  try {
    const allergens = getAllergensList(profile.safety);
    const dietary = DIETARY.filter(d => profile.safety[d.key]).map(d => d.label);
    localStorage.setItem(SAFETY_CARD_KEY, JSON.stringify({
      allergies: allergens,
      dietary,
      neverEat: profile.safety.never_eat_list || [],
      medicalNotes: profile.safety.medical_notes || "",
      displayName: profile.display_name || "",
      updatedAt: new Date().toISOString(),
    }));
  } catch (_) { /* storage full or unavailable */ }
};

const loadCachedSafetyCard = () => {
  try {
    const raw = localStorage.getItem(SAFETY_CARD_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
};

class ErrorBoundary extends Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) {
    console.error("Consoome crash:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: "100vh", background: "#1A1A1A", display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", padding: 32, textAlign: "center",
          fontFamily: "'Montserrat', sans-serif",
        }}>
          <img src={logoCream} alt="Consoome" style={{ width: 120, marginBottom: 24, opacity: 0.9 }} />
          <h2 style={{ color: "#FFF9EC", fontSize: 22, fontWeight: 600, marginBottom: 8 }}>Something went wrong</h2>
          <p style={{ color: "#A0998A", fontSize: 15, lineHeight: "22px", marginBottom: 32, maxWidth: 320 }}>
            The app encountered an error. Refreshing the page usually helps.
          </p>
          <button onClick={() => window.location.reload()} style={{
            padding: "14px 32px", borderRadius: 999, border: "none", cursor: "pointer",
            background: "#C8A951", color: "#1A1A1A", fontFamily: "Montserrat",
            fontSize: 15, fontWeight: 600, letterSpacing: "0.75px",
          }}>
            Reload App
          </button>
          {process.env.NODE_ENV === "development" && (
            <details style={{ marginTop: 32, textAlign: "left", fontSize: 12, color: "#A0998A", maxWidth: 360 }}>
              <summary style={{ cursor: "pointer" }}>Error details (dev only)</summary>
              <pre style={{ marginTop: 8, overflowX: "auto", whiteSpace: "pre-wrap" }}>
                {this.state.error?.toString()}
              </pre>
            </details>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

const SHARE_SCOPES = [
  { key: "safety_only", icon: "🛡️", label: "Restrictions Card", desc: "Allergies & restrictions only" },
  { key: "taste_summary", icon: "✦", label: "Taste Summary", desc: "Restrictions + taste, cuisine & beverage" },
  { key: "full", icon: "👤", label: "Full Profile", desc: "Complete guest preferences" },
];

const getScopeLabel = (s) => SHARE_SCOPES.find(x => x.key === s)?.label || "Food ID";

const ShareScreen = ({ profile, onBack }) => {
  const [scope, setScope] = useState("full");
  const [loading, setLoading] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedJson, setCopiedJson] = useState(false);
  const [status, setStatus] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const wakeLockRef = useRef(null);

  const allergens = getAllergensList(profile.safety);

  // Wake Lock for QR screen
  useEffect(() => {
    if (showQR && "wakeLock" in navigator) {
      navigator.wakeLock.request("screen").then(wl => { wakeLockRef.current = wl; }).catch(() => {});
    }
    return () => { if (wakeLockRef.current) { wakeLockRef.current.release(); wakeLockRef.current = null; } };
  }, [showQR]);

  // ── Create share link & show QR ──
  const handleCreateShare = async () => {
    setShareLoading(true);
    setStatus(null);
    try {
      const tokenRes = await fetch(`${API_BASE}/food-id/me/share`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("sb_token") || ""}`,
        },
        body: JSON.stringify({ scope, label: `${getScopeLabel(scope)} share`, expires_hours: 72 }),
      });
      if (!tokenRes.ok) throw new Error("Could not create share link");
      const tokenData = await tokenRes.json();
      const url = `${window.location.origin}/food-id/shared/${tokenData.token}`;
      setShareLink(url);
      setShowQR(true);
      addActivity({ type: "share", icon: "📤", text: `Shared Food ID (${scope === "full" ? "full profile" : scope === "safety_only" ? "restrictions only" : "taste summary"})` });
    } catch (e) {
      setStatus({ type: "error", msg: e.message || "Share failed" });
    } finally {
      setShareLoading(false);
    }
  };

  // ── Download PDF ──
  const handleDownloadPDF = async () => {
    setLoading(true);
    setStatus(null);
    try {
      const res = await fetch(`${API_BASE}/food-id/me/export/pdf`, {
        headers: { "Authorization": `Bearer ${localStorage.getItem("sb_token") || ""}` },
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `FoodID-${profile.handle || "profile"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setStatus({ type: "success", msg: "PDF downloaded" });
    } catch (e) {
      setStatus({ type: "error", msg: e.message || "Failed to export PDF" });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (!shareLink) return;
    navigator.clipboard.writeText(shareLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleCopyJson = () => {
    // Build a venue-friendly JSON snapshot from the profile prop (owner's own data)
    const allergens = getAllergensList(profile.safety);
    const dietaryItems = DIETARY.filter(d => profile.safety?.[d.key]).map(d => d.label);
    const topCuisines = getTopCuisines(profile.cuisine?.cuisine_affinities);
    const spiceLabel = SPICE_LABELS[profile.taste?.spice_tolerance] || null;
    const payload = {
      metadata: { version: "1.1", exportedAt: new Date().toISOString(), scope, shareUrl: shareLink },
      display_name: profile.display_name,
      handle: profile.handle,
      summary_line: [
        ...dietaryItems.slice(0, 2),
        ...allergens.slice(0, 3).map(a => `${a} allergy`),
        spiceLabel ? `${spiceLabel} spice` : null,
        topCuisines[0] ? `${topCuisines[0]} cuisine` : null,
      ].filter(Boolean).join(" · ") || "No restrictions on file",
      restrictions: {
        allergens,
        dietary: dietaryItems,
        never_eat: profile.safety?.never_eat_list || [],
      },
      allergen_flags: Object.fromEntries(
        ["has_nuts","has_shellfish","has_gluten","has_dairy","has_eggs","has_fish","has_soy","has_sesame","has_sulphites"]
          .map(k => [k.replace("has_",""), !!(profile.safety?.[k])])
      ),
    };
    if (scope !== "safety_only") {
      payload.taste = {
        spice: profile.taste?.spice_tolerance,
        spice_label: spiceLabel,
        richness: profile.taste?.richness_pref,
        umami: profile.taste?.umami_pref,
      };
      payload.cuisine = {
        top: topCuisines,
        proteins: (profile.cuisine?.protein_ranking || []).slice(0, 5),
      };
      payload.beverage = {
        drinks_alcohol: profile.beverage?.drinks_alcohol,
        wine_styles: profile.beverage?.wine_style_pref || [],
        wine_body: profile.beverage?.wine_body_pref,
        wine_region: profile.beverage?.wine_region_pref || [],
        non_alcoholic: profile.beverage?.na_bev_pref || [],
      };
    }
    if (scope === "full") {
      payload.context = {
        indulgence: profile.taste?.indulgence_default,
        portion: profile.taste?.portion_pref,
        occasions: profile.context?.default_occasions || [],
      };
    }
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2)).then(() => {
      setCopiedJson(true);
      setTimeout(() => setCopiedJson(false), 2500);
    });
  };

  const handleNativeShare = async () => {
    if (!shareLink || !navigator.share) return;
    try {
      await navigator.share({
        title: `${profile.display_name}'s ${getScopeLabel(scope)}`,
        text: `Here's my ${getScopeLabel(scope)} on Consoome.`,
        url: shareLink,
      });
    } catch (e) {
      if (e.name !== "AbortError") setStatus({ type: "error", msg: "Share cancelled" });
    }
  };

  // ── QR Code Screen ──
  if (showQR && shareLink) {
    return (
      <div className="anim-fadein" style={{ minHeight: "100%", background: T.darkBg, display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}>
        <div style={{ width: "100%", padding: `52px ${SP.lg}px ${SP.md}px`, position: "relative", zIndex: 1 }}>
          <button onClick={() => { setShowQR(false); }} aria-label="Close QR code" style={{ ...TYPE.bodySmall, fontWeight: 600, color: T.darkTextSec, background: "none", border: "none", cursor: "pointer", fontFamily: "Montserrat", display: "flex", alignItems: "center", gap: 6 }}>
            ← Back
          </button>
        </div>

        <div className="anim-fadeup" style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: SP.lg, gap: SP.lg, marginTop: -40 }}>
          {/* QR Code */}
          <div style={{ padding: SP.lg, background: "rgba(255,255,255,0.06)", borderRadius: RAD.xl, border: `1px solid ${T.gold}30`, backdropFilter: "blur(12px)" }}>
            <QRCodeSVG value={shareLink} size={280} bgColor="#1A1A1A" fgColor="#FFFFFF" level="M" />
          </div>

          {/* Labels */}
          <div style={{ textAlign: "center" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 16px", borderRadius: RAD.pill, background: `${T.gold}15`, border: `1px solid ${T.gold}30`, marginBottom: SP.sm }}>
              <span style={{ fontSize: 14 }}>{SHARE_SCOPES.find(s => s.key === scope)?.icon}</span>
              <span style={{ ...TYPE.caption, fontWeight: 700, color: T.gold }}>{getScopeLabel(scope)}</span>
            </div>
            <p style={{ ...TYPE.bodySmall, color: T.darkTextSec, marginTop: SP.sm }}>
              Scan to view — valid for 72 hours
            </p>
          </div>

          {/* Action buttons */}
          <div style={{ width: "100%", maxWidth: 340, display: "flex", flexDirection: "column", gap: SP.sm }}>
            <GoldBtn onClick={handleCopyLink}>
              {copied ? "✓ Copied!" : "Copy Link"}
            </GoldBtn>
            {navigator.share && (
              <GoldBtn variant="outline" onClick={handleNativeShare}>
                Share via...
              </GoldBtn>
            )}
            <GoldBtn variant="outline" onClick={handleCopyJson}>
              {copiedJson ? "✓ JSON Copied!" : "Copy as JSON (for venue systems)"}
            </GoldBtn>
          </div>

          {/* Truncated URL display */}
          <div style={{ ...TYPE.caption, color: T.darkTextSec, textAlign: "center", wordBreak: "break-all", maxWidth: 320, lineHeight: "16px", opacity: 0.6 }}>
            {shareLink}
          </div>
        </div>
      </div>
    );
  }

  // ── Main Share Screen ──
  return (
    <div style={{ minHeight: "100%", background: T.darkBg, paddingBottom: 40, position: "relative" }}>
      <DarkBgLayers />
      <div style={{ padding: `52px ${SP.lg}px ${SP.lg}px`, position: "relative", zIndex: 1 }}>
        <button onClick={onBack} style={{ ...TYPE.bodySmall, fontWeight: 600, color: T.darkTextSec, background: "none", border: "none", cursor: "pointer", fontFamily: "Montserrat", marginBottom: SP.md, display: "flex", alignItems: "center", gap: 6 }}>
          ← Back
        </button>
        <div style={{ ...TYPE.overline, color: T.darkTextSec, marginBottom: SP.sm }}>SHARE</div>
        <h1 style={{ ...TYPE.display, color: T.cream }}>Share Your Food ID</h1>
        <p style={{ ...TYPE.bodySmall, color: T.darkTextSec, marginTop: 6 }}>Choose what to share, then get a QR code or link.</p>
      </div>

      <div style={{ padding: SP.lg, position: "relative", zIndex: 1 }}>

        {/* Scope selection — 3 options */}
        <SectionLabel>What to Share</SectionLabel>
        <div role="radiogroup" aria-label="Share scope" style={{ display: "flex", gap: SP.sm, marginBottom: SP.lg }}>
          {SHARE_SCOPES.map(s => (
            <button key={s.key} onClick={() => setScope(s.key)} role="radio" aria-checked={scope === s.key} style={{
              flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
              padding: `${SP.md}px ${SP.sm}px`, borderRadius: RAD.lg,
              border: `2px solid ${scope === s.key ? T.gold : `${T.gold}20`}`,
              background: scope === s.key ? `${T.gold}10` : "rgba(255,255,255,0.04)",
              cursor: "pointer", fontFamily: "Montserrat", textAlign: "center",
              transition: "all 0.2s", backdropFilter: "blur(8px)",
            }}>
              <span style={{ fontSize: 22 }}>{s.icon}</span>
              <span style={{ ...TYPE.caption, fontWeight: 700, color: scope === s.key ? T.gold : T.cream, lineHeight: "14px" }}>{s.label}</span>
              <span style={{ ...TYPE.caption, fontSize: 9, color: T.darkTextSec, lineHeight: "12px" }}>{s.desc}</span>
            </button>
          ))}
        </div>

        {/* Primary: Generate QR */}
        <GoldBtn onClick={handleCreateShare} disabled={shareLoading}>
          {shareLoading ? "Creating..." : "Generate QR Code & Link"}
        </GoldBtn>

        <div style={{ height: SP.md }} />

        {/* Secondary actions */}
        <div style={{ display: "flex", gap: SP.sm }}>
          <GoldBtn variant="outline" onClick={handleDownloadPDF} disabled={loading} fullWidth>
            {loading ? "..." : "Download PDF"}
          </GoldBtn>
        </div>

        {/* Status message */}
        {status && (
          <div style={{
            marginTop: SP.md, padding: SP.md, borderRadius: RAD.md,
            background: status.type === "success" ? `${T.safetyGreen}15` : `${T.safetyRed}15`,
            border: `1px solid ${status.type === "success" ? T.safetyGreen : T.safetyRed}40`,
          }}>
            <div style={{ ...TYPE.bodySmall, color: status.type === "success" ? T.safetyGreen : T.safetyRed }}>
              {status.type === "success" ? "✓" : "✕"} {status.msg}
            </div>
          </div>
        )}

        {/* Profile preview based on scope */}
        <SectionLabel style={{ marginTop: SP.xl }}>What's Included</SectionLabel>
        <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: RAD.lg, border: `1px solid ${T.gold}25`, padding: 18, marginTop: SP.md, backdropFilter: "blur(8px)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: SP.md }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: T.gold, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <span style={{ color: T.ink, fontWeight: 800, fontSize: 14 }}>{profile.display_name.charAt(0)}</span>
            </div>
            <div>
              <div style={{ ...TYPE.bodySmall, fontWeight: 700, color: T.cream }}>@{profile.handle}</div>
              <div style={{ ...TYPE.overline, color: T.darkTextSec }}>
                {getScopeLabel(scope).toUpperCase()} · CONSOOME
              </div>
            </div>
          </div>

          <PreviewBlock title="DIETARY RESTRICTIONS / القيود الغذائية" color={T.safetyRed}>
            {allergens.length > 0 ? (
              <>
                <div style={{ color: T.safetyRed, fontWeight: 700, marginBottom: 6 }}>Allergies / حساسية</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {allergens.map(a => (
                    <div key={a} style={{ padding: "4px 10px", borderRadius: 6, background: `${T.safetyRed}18`, border: `1px solid ${T.safetyRed}40` }}>
                      <div style={{ fontWeight: 600, color: T.cream, fontSize: 12 }}>{a}</div>
                      {SAFETY_AR[a] && <div style={{ fontFamily: AR_FONT, direction: "rtl", textAlign: "right", color: T.cream, fontSize: 12, fontWeight: 600 }}>{SAFETY_AR[a]}</div>}
                    </div>
                  ))}
                </div>
              </>
            ) : <span>No allergens on file</span>}
            {(() => {
              const dietaryItems = DIETARY.filter(d => profile.safety?.[d.key]).map(d => d.label);
              return dietaryItems.length > 0 ? (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontWeight: 700, color: T.cream, marginBottom: 4 }}>Dietary / قيود غذائية</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {dietaryItems.map(d => (
                      <div key={d} style={{ padding: "4px 10px", borderRadius: 6, background: `${T.safetyGreen}18`, border: `1px solid ${T.safetyGreen}40` }}>
                        <div style={{ fontWeight: 600, color: T.cream, fontSize: 12 }}>{d}</div>
                        {SAFETY_AR[d] && <div style={{ fontFamily: AR_FONT, direction: "rtl", textAlign: "right", color: T.cream, fontSize: 12, fontWeight: 600 }}>{SAFETY_AR[d]}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null;
            })()}
            {profile.safety.never_eat_list.length > 0 && (
              <div style={{ marginTop: 6 }}>Never eat: {profile.safety.never_eat_list.join(", ")}</div>
            )}
            {(profile.safety.milk_intolerant || profile.safety.milk_alternatives?.length > 0) && (
              <div style={{ marginTop: 6 }}>
                🥛 {profile.safety.milk_intolerant ? "Lactose intolerant" : ""}
                {profile.safety.milk_alternatives?.length > 0 ? ` · Alt: ${profile.safety.milk_alternatives.join(", ")}` : ""}
              </div>
            )}
          </PreviewBlock>

          {(scope === "taste_summary" || scope === "full") && (
            <>
              <PreviewBlock title="TASTE PROFILE">
                <span style={{ fontWeight: 600, color: T.cream }}>{SPICE_LABELS[profile.taste?.spice_tolerance] || "N/A"}</span> spice
                {profile.taste?.richness_pref != null && <> · Richness <span style={{ fontWeight: 600, color: T.cream }}>{profile.taste.richness_pref}/5</span></>}
                {profile.taste?.umami_pref != null && <> · Umami <span style={{ fontWeight: 600, color: T.cream }}>{profile.taste.umami_pref}/5</span></>}
              </PreviewBlock>

              <PreviewBlock title="CUISINE & PROTEIN">
                {getTopCuisines(profile.cuisine?.cuisine_affinities).join(" · ") || "Not set"}
                {(profile.cuisine?.protein_ranking || []).length > 0 && <> · <span style={{ fontWeight: 600, color: T.cream }}>{profile.cuisine.protein_ranking.slice(0, 3).join(" › ")}</span></>}
              </PreviewBlock>

              <PreviewBlock title="BEVERAGE">
                {profile.beverage?.drinks_alcohol ? (
                  <>
                    {(profile.beverage?.wine_style_pref || []).length > 0 && <><span style={{ fontWeight: 600, color: T.cream }}>{profile.beverage.wine_style_pref.join(" & ")}</span> wine</>}
                    {profile.beverage?.wine_body_pref != null && <> · Body {profile.beverage.wine_body_pref}/5</>}
                    {profile.beverage?.wine_region_pref?.length > 0 && <> · {profile.beverage.wine_region_pref.join(", ")}</>}
                    {(profile.beverage?.cocktail_style_pref || []).length > 0 && <> · Cocktails: {profile.beverage.cocktail_style_pref.join(", ")}</>}
                  </>
                ) : "Non-alcoholic only"}
              </PreviewBlock>
            </>
          )}

          {scope === "full" && (
            <>
              <PreviewBlock title="DINING STYLE">
                {(profile.taste?.indulgence_default || 0) > 3 ? "Indulgent dining" : "Health-conscious"} · {profile.taste?.portion_pref === "large" ? "Large portions" : profile.taste?.portion_pref === "small" ? "Small plates" : "Standard portions"}
              </PreviewBlock>
              {(profile.context?.default_occasions || []).length > 0 && (
                <PreviewBlock title="OCCASIONS">
                  {profile.context.default_occasions.map(o => o.charAt(0).toUpperCase() + o.slice(1)).join(" · ")}
                </PreviewBlock>
              )}
            </>
          )}
        </div>

        <div style={{ ...TYPE.caption, color: T.darkTextSec, textAlign: "center", marginTop: SP.lg, lineHeight: "16px" }}>
          {scope === "safety_only"
            ? "Your Restrictions Card includes critical dietary restrictions and allergies only."
            : scope === "taste_summary"
            ? "Includes dietary restrictions plus taste, cuisine, and beverage preferences."
            : "Your complete Food ID with all preferences, context, and dining history."
          }
        </div>
      </div>
    </div>
  );
};

const PreviewBlock = ({ title, color = T.darkTextSec, children }) => (
  <div style={{ marginTop: SP.md, paddingTop: SP.sm, borderTop: `1px solid ${T.gold}15` }}>
    <div style={{ ...TYPE.overline, color, marginBottom: 4 }}>{title}</div>
    <div style={{ ...TYPE.bodySmall, color: T.darkTextSec, lineHeight: "20px" }}>{children}</div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// SCREEN: SHARED PROFILE VIEW (public, no auth)
// ─────────────────────────────────────────────────────────────
const SharedProfileScreen = ({ token, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/food-id/shared/${token}`)
      .then(r => {
        if (r.status === 410) throw new Error("This share link has expired.");
        if (r.status === 404) throw new Error("Share link not found or revoked.");
        if (!r.ok) throw new Error("Could not load profile.");
        return r.json();
      })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(friendlyError(e)); setLoading(false); });
  }, [token]);

  if (loading) return (
    <div style={{ minHeight: "100vh", background: T.darkBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center" }}>
        <img src={logoCream} alt="Consoome logo" style={{ height: 32, objectFit: "contain", marginBottom: SP.lg }} />
        <p style={{ ...TYPE.bodySmall, color: T.darkTextSec }}>Loading shared profile...</p>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: "100vh", background: T.darkBg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: SP.lg }}>
      <img src={logoCream} alt="Consoome logo" style={{ height: 32, objectFit: "contain", marginBottom: SP.xl }} />
      <div style={{ fontSize: 40, marginBottom: SP.md }}>:/</div>
      <p style={{ ...TYPE.body, color: T.cream, fontWeight: 600, marginBottom: SP.sm, textAlign: "center" }}>{error}</p>
      <GoldBtn onClick={onClose} small fullWidth={false}>Go to Consoome</GoldBtn>
    </div>
  );

  const allergens = data.safety ? getAllergensList(data.safety) : [];
  const scopeLabel = data.scope === "safety_only" ? "Restrictions Card" : data.scope === "taste_summary" ? "Taste Summary" : "Full Profile";

  return (
    <div style={{ minHeight: "100vh", background: T.darkBg, paddingBottom: 40 }}>
      <DarkBgLayers />
      <div style={{ padding: `52px ${SP.lg}px ${SP.lg}px`, position: "relative", zIndex: 1 }}>
        <div style={{ textAlign: "center", marginBottom: SP.lg }}>
          <img src={logoCream} alt="Consoome logo" style={{ height: 28, objectFit: "contain", marginBottom: SP.lg }} />
          <div style={{ width: 56, height: 56, borderRadius: "50%", background: T.gold, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto", marginBottom: SP.md }}>
            <span style={{ color: T.ink, fontWeight: 800, fontSize: 22 }}>{(data.display_name || "G").charAt(0)}</span>
          </div>
          <h1 style={{ ...TYPE.h1, color: T.cream }}>{data.display_name || "Guest"}</h1>
          <div style={{ ...TYPE.bodySmall, color: T.darkTextSec, marginTop: 4 }}>@{data.handle}</div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 14px", borderRadius: RAD.pill, background: `${T.gold}15`, border: `1px solid ${T.gold}30`, marginTop: SP.md }}>
            <span style={{ ...TYPE.caption, fontWeight: 700, color: T.gold }}>{scopeLabel}</span>
          </div>
        </div>

        {/* ── Staff summary one-liner ── */}
        {data.summary_line && (
          <div style={{ background: `${T.gold}12`, border: `1px solid ${T.gold}35`, borderRadius: RAD.md, padding: `${SP.sm}px ${SP.md}px`, marginBottom: SP.md, textAlign: "center" }}>
            <div style={{ ...TYPE.caption, color: T.darkTextSec, marginBottom: 3, fontWeight: 700, letterSpacing: "0.08em" }}>GUEST AT A GLANCE</div>
            <div style={{ ...TYPE.bodySmall, color: T.cream, fontWeight: 600, lineHeight: "20px" }}>{data.summary_line}</div>
          </div>
        )}
      </div>

      <div style={{ padding: `0 ${SP.lg}px`, position: "relative", zIndex: 1 }}>
        <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: RAD.lg, border: `1px solid ${T.gold}25`, padding: 18, backdropFilter: "blur(8px)" }}>
          <PreviewBlock title="DIETARY RESTRICTIONS / القيود الغذائية" color={T.safetyRed}>
            {allergens.length > 0 ? (
              <>
                <div style={{ color: T.safetyRed, fontWeight: 700, marginBottom: 6 }}>Allergies / حساسية</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {allergens.map(a => (
                    <div key={a} style={{ padding: "4px 10px", borderRadius: 6, background: `${T.safetyRed}18`, border: `1px solid ${T.safetyRed}40` }}>
                      <div style={{ fontWeight: 600, color: T.cream, fontSize: 12 }}>{a}</div>
                      {SAFETY_AR[a] && <div style={{ fontFamily: AR_FONT, direction: "rtl", textAlign: "right", color: T.cream, fontSize: 12, fontWeight: 600 }}>{SAFETY_AR[a]}</div>}
                    </div>
                  ))}
                </div>
              </>
            ) : <span>No allergens on file</span>}
            {(() => {
              const dietaryItems = data.safety ? DIETARY.filter(d => data.safety[d.key]).map(d => d.label) : [];
              return dietaryItems.length > 0 ? (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontWeight: 700, color: T.cream, marginBottom: 4 }}>Dietary / قيود غذائية</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {dietaryItems.map(d => (
                      <div key={d} style={{ padding: "4px 10px", borderRadius: 6, background: `${T.safetyGreen}18`, border: `1px solid ${T.safetyGreen}40` }}>
                        <div style={{ fontWeight: 600, color: T.cream, fontSize: 12 }}>{d}</div>
                        {SAFETY_AR[d] && <div style={{ fontFamily: AR_FONT, direction: "rtl", textAlign: "right", color: T.cream, fontSize: 12, fontWeight: 600 }}>{SAFETY_AR[d]}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null;
            })()}
            {data.safety?.never_eat_list?.length > 0 && (
              <div style={{ marginTop: 6 }}>Never eat: {data.safety.never_eat_list.join(", ")}</div>
            )}
          </PreviewBlock>

          {data.taste && (
            <PreviewBlock title="TASTE PROFILE">
              <span style={{ fontWeight: 600, color: T.cream }}>{data.taste.spice_label || SPICE_LABELS[data.taste.spice_tolerance] || "N/A"}</span> spice
              {data.taste.richness_label && <> · Richness: <span style={{ fontWeight: 600, color: T.cream }}>{data.taste.richness_label}</span></>}
              {data.taste.umami_label && <> · Umami: <span style={{ fontWeight: 600, color: T.cream }}>{data.taste.umami_label}</span></>}
              {data.taste.preferred_textures?.length > 0 && <> · Textures: {data.taste.preferred_textures.join(", ")}</>}
            </PreviewBlock>
          )}

          {data.cuisine && (
            <PreviewBlock title="CUISINE & PROTEIN">
              {getTopCuisines(data.cuisine.cuisine_affinities || {}).join(" · ") || "Not set"}
              {data.cuisine.protein_ranking?.length > 0 && <> · <span style={{ color: T.cream, fontWeight: 600 }}>{data.cuisine.protein_ranking.slice(0, 3).join(" › ")}</span></>}
            </PreviewBlock>
          )}

          {data.beverage && (
            <PreviewBlock title="BEVERAGE">
              {data.beverage.drinks_alcohol ? (
                <>
                  {(data.beverage.wine_style_pref || []).length > 0 && <><span style={{ fontWeight: 600, color: T.cream }}>{data.beverage.wine_style_pref.join(" & ")}</span> wine</>}
                  {data.beverage.wine_body_label && <> · Body: <span style={{ fontWeight: 600, color: T.cream }}>{data.beverage.wine_body_label}</span></>}
                  {data.beverage.wine_tannin_label && <> · Tannin: <span style={{ fontWeight: 600, color: T.cream }}>{data.beverage.wine_tannin_label}</span></>}
                  {data.beverage.wine_acidity_label && <> · Acidity: <span style={{ fontWeight: 600, color: T.cream }}>{data.beverage.wine_acidity_label}</span></>}
                  {data.beverage.wine_sweetness_label && <> · Sweetness: <span style={{ fontWeight: 600, color: T.cream }}>{data.beverage.wine_sweetness_label}</span></>}
                  {data.beverage.wine_oak_label && <> · Oak: <span style={{ fontWeight: 600, color: T.cream }}>{data.beverage.wine_oak_label}</span></>}
                  {data.beverage.wine_region_pref?.length > 0 && <> · Region: <span style={{ fontWeight: 600, color: T.cream }}>{data.beverage.wine_region_pref.join(", ")}</span></>}
                  {(data.beverage.cocktail_style_pref || []).length > 0 && <> · Cocktails: {data.beverage.cocktail_style_pref.join(", ")}</>}
                </>
              ) : "Non-alcoholic only"}
            </PreviewBlock>
          )}

          {data.occasions?.length > 0 && (
            <PreviewBlock title="DINING OCCASIONS">
              {data.occasions.map(o => o.charAt(0).toUpperCase() + o.slice(1)).join(" · ")}
            </PreviewBlock>
          )}

          {data.context && (
            <PreviewBlock title="DINING STYLE">
              {(data.context.indulgence_default || 0) > 3 ? "Indulgent dining" : "Health-conscious"} · {data.context.portion_pref === "large" ? "Large portions" : data.context.portion_pref === "small" ? "Small plates" : "Standard portions"}
              {data.context.travel_mode && " · Travelling"}
            </PreviewBlock>
          )}
        </div>

        {/* ── Metadata footer ── */}
        {data.metadata && (
          <div style={{ ...TYPE.caption, color: `${T.darkTextSec}80`, textAlign: "center", marginTop: SP.md, lineHeight: "16px" }}>
            Consoome Food ID · Verified {data.metadata.exportedAt ? new Date(data.metadata.exportedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}
            {data.metadata.expiresAt && ` · Expires ${new Date(data.metadata.expiresAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`}
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: SP.xl }}>
          <p style={{ ...TYPE.caption, color: T.darkTextSec, marginBottom: SP.md }}>Powered by Consoome</p>
          <GoldBtn onClick={onClose} small fullWidth={false}>Create Your Food ID</GoldBtn>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// SCREEN: SCAN (M1) — spec: camera frame pattern
// ─────────────────────────────────────────────────────────────
const SCAN_MAX_PAGES = 5;

const SCAN_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const ScanScreen = ({ profile, onBack, onResult }) => {
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState("");
  const [scansRemaining, setScansRemaining] = useState(null);
  const [scanResults, setScanResults] = useState(null);
  const [scanError, setScanError] = useState(null);
  const [capturedFiles, setCapturedFiles] = useState([]); // {file, preview}
  const [dishFeedback, setDishFeedback] = useState({}); // { dish_id: 'up' | 'down' }
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const abortControllerRef = useRef(null);
  const progressTimersRef = useRef([]);

  // Check quota on mount
  useEffect(() => {
    const token = localStorage.getItem("sb_token");
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const uid = payload.sub;
      if (!uid) return;
      fetch(`${API_BASE}/scan/quota/${uid}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.ok ? r.json() : null).then(data => {
        if (data) setScansRemaining(data.scans_remaining);
      }).catch(() => {});
    } catch (e) { /* ignore parse errors */ }
  }, []);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => capturedFiles.forEach(f => URL.revokeObjectURL(f.preview));
  }, [capturedFiles]);

  // Abort in-flight scan on unmount (prevents orphaned requests)
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      progressTimersRef.current.forEach(clearTimeout);
    };
  }, []);

  const addFiles = (fileList) => {
    const newFiles = Array.from(fileList);
    // Client-side file size guard
    const tooBig = newFiles.find(f => f.size > SCAN_MAX_FILE_SIZE);
    if (tooBig) {
      setScanError(`File "${tooBig.name}" is ${(tooBig.size / 1024 / 1024).toFixed(1)} MB. Maximum is 10 MB.`);
      return;
    }
    setCapturedFiles(prev => {
      const total = prev.length + newFiles.length;
      if (total > SCAN_MAX_PAGES) {
        setScanError(`Max ${SCAN_MAX_PAGES} pages. You have ${prev.length}, tried to add ${newFiles.length}.`);
        return prev;
      }
      setScanError(null);
      return [...prev, ...newFiles.map(file => ({ file, preview: URL.createObjectURL(file) }))];
    });
  };

  const removeFile = (index) => {
    setCapturedFiles(prev => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
    setScanError(null);
  };

  const handleCameraCapture = (e) => {
    if (e.target.files && e.target.files.length > 0) addFiles(e.target.files);
    e.target.value = "";
  };

  const handleGallerySelect = (e) => {
    if (e.target.files && e.target.files.length > 0) addFiles(e.target.files);
    e.target.value = "";
  };

  const handleSubmitScan = async () => {
    if (capturedFiles.length === 0) return;

    const token = localStorage.getItem("sb_token");
    const foodId = profile?._foodId;
    if (!token || !foodId) {
      setScanError("Please log in and complete onboarding first.");
      return;
    }
    if (scansRemaining != null && scansRemaining <= 0) {
      setScanError("No scans remaining. Contact support for more.");
      return;
    }

    // Abort any previous in-flight scan
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Auto-abort after 120 seconds
    const timeoutId = setTimeout(() => controller.abort(), 120000);

    setScanning(true);
    setScanError(null);
    setScanResults(null);
    setProgress("Uploading menu...");

    // Multi-stage progress text (timed, since backend is opaque)
    progressTimersRef.current.forEach(clearTimeout);
    progressTimersRef.current = [
      setTimeout(() => setProgress("Reading menu text..."), 3000),
      setTimeout(() => setProgress("Matching dishes to your profile..."), 8000),
      setTimeout(() => setProgress("Almost done..."), 20000),
    ];

    try {
      const formData = new FormData();
      formData.append("food_id", foodId);
      for (const { file } of capturedFiles) {
        formData.append("files", file);
      }

      const res = await fetch(`${API_BASE}/scan/menu`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
        signal: controller.signal,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Scan failed");
      }

      const data = await res.json();
      // ── Change 8: Filter junk dish entries ──
      const JUNK_RE = /dish names|prices|descriptions|could you|please provide|I can see|readable/i;
      if (data.recommendations) {
        data.recommendations = data.recommendations.map(d => {
          // Strip leaked markdown bold: "**DishName**" → "DishName"
          if (d.dish_name && d.dish_name.startsWith("**") && d.dish_name.endsWith("**")) {
            d.dish_name = d.dish_name.slice(2, -2);
          }
          return d;
        }).filter(d => {
          const n = d.dish_name || "";
          if (n.startsWith("- ")) return false;
          if (JUNK_RE.test(n)) return false;
          if (n.length > 80) return false;
          return true;
        });
      }
      // ── Change 7: Detect failed/junk scans ──
      const recs = data.recommendations || [];
      const AI_NOISE_RE = /I can see|Could you|please provide|readable|image of/i;
      const isBadScan = recs.length === 0 || (recs.length < 2 && recs.some(d => AI_NOISE_RE.test(d.dish_name || "")));
      if (isBadScan) {
        data._failedScan = true;
        data.recommendations = [];
      }
      setProgress("Done!");
      setScansRemaining(data.scans_remaining);
      setScanResults(data);
      setCapturedFiles([]);
      setTimeout(() => setScanning(false), 500);
      if (!isBadScan) addActivity({ type: "scan", icon: "📸", text: `Scanned menu — ${data.recommendations?.length || 0} dishes matched` });
    } catch (e) {
      if (e.name === "AbortError") {
        setScanError("Scan timed out or was cancelled. Try a clearer photo.");
      } else {
        setScanError(e.message || "Something went wrong. Please try again.");
      }
      setScanning(false);
    } finally {
      clearTimeout(timeoutId);
      progressTimersRef.current.forEach(clearTimeout);
      progressTimersRef.current = [];
      abortControllerRef.current = null;
    }
  };

  const handleCancelScan = () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
  };

  const submitDishFeedback = async (dishId, dishName, rating) => {
    setDishFeedback(prev => ({ ...prev, [dishId]: rating }));
    try {
      await fetch(`${API_BASE}/scan/dish-feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('sb_token')}`,
        },
        body: JSON.stringify({ scan_id: scanResults?.scan_session_id, dish_id: dishId, dish_name: dishName, rating }),
      });
    } catch (e) {
      // Silent fail — feedback is best-effort
    }
  };

  const pagesFull = capturedFiles.length >= SCAN_MAX_PAGES;
  const hasPages = capturedFiles.length > 0;

  return (
    <div style={{ minHeight: "100vh", background: T.darkBg, display: "flex", flexDirection: "column" }}>
      {/* Hidden file inputs — separate for camera vs gallery */}
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={handleCameraCapture} />
      <input ref={galleryInputRef} type="file" accept="image/*,.pdf" multiple style={{ display: "none" }} onChange={handleGallerySelect} />

      {/* Camera viewfinder area */}
      <div style={{ flex: 1, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {/* Top bar */}
        <div style={{ position: "absolute", top: 48, left: SP.lg, right: SP.lg, display: "flex", justifyContent: "space-between", alignItems: "center", zIndex: 2 }}>
          <button onClick={onBack} style={{ color: T.cream, background: "rgba(26,26,26,0.5)", border: "none", borderRadius: RAD.pill, padding: "8px 14px", cursor: "pointer", fontFamily: "Montserrat", ...TYPE.bodySmall, fontWeight: 600 }}>
            ✕ Close
          </button>
          {hasPages && !scanning && !scanResults && (
            <div style={{ ...TYPE.bodySmall, color: T.cream, background: "rgba(26,26,26,0.5)", borderRadius: RAD.pill, padding: "8px 14px", fontWeight: 600, fontFamily: "Montserrat" }}>
              {capturedFiles.length}/{SCAN_MAX_PAGES} pages
            </div>
          )}
        </div>

        {scanResults && scanResults._failedScan ? (
          <div style={{ padding: SP.xl, textAlign: "center", zIndex: 1, maxWidth: 340 }}>
            <div style={{ marginBottom: SP.lg, display: "flex", justifyContent: "center" }}>
              <UiIcon name="camera-off" size={56} color={T.darkTextSec} />
            </div>
            <h2 style={{ ...TYPE.h1, color: T.cream, marginBottom: SP.sm }}>No menu found in this image</h2>
            <p style={{ ...TYPE.body, color: T.darkTextSec, marginBottom: SP.xl, lineHeight: "22px" }}>
              It looks like that image doesn't contain a readable menu. Try uploading a clearer photo of the actual menu page.
            </p>
            <GoldBtn onClick={() => { setScanResults(null); setScanError(null); }}>Try Again</GoldBtn>
            <button onClick={onBack} style={{
              display: "block", width: "100%", marginTop: SP.md,
              background: "none", border: "none", cursor: "pointer",
              ...TYPE.bodySmall, color: T.gold, fontWeight: 600, fontFamily: "Montserrat",
            }}>
              Browse venues instead
            </button>
          </div>
        ) : scanResults ? (
          <div style={{ padding: SP.lg, width: "100%", maxWidth: 400, zIndex: 1 }}>
            <h2 style={{ ...TYPE.h1, color: T.cream, marginBottom: SP.md, textAlign: "center" }}>
              Found {scanResults.total_items_found} items
            </h2>
            {scanResults.language_warning && (
              <p style={{ ...TYPE.bodySmall, color: T.gold, textAlign: "center", marginBottom: SP.md }}>{scanResults.language_warning}</p>
            )}
            <div style={{ background: `${T.gold}08`, borderRadius: RAD.md, padding: "10px 12px", marginBottom: SP.md, border: `1px solid ${T.gold}15` }}>
              <p style={{ ...TYPE.caption, color: T.darkTextSec, lineHeight: "16px", fontSize: 9 }}>
                Consoome provides suggestions only. It does not verify final ingredients, allergens, traces, substitutions, cross-contamination risks, preparation methods, or real-time menu changes. Always confirm directly with venue staff before ordering.
              </p>
            </div>
            {scanResults.allergen_blocked ? (
              <div style={{ textAlign: 'center', padding: '32px 24px', color: T.darkTextSec, fontFamily: 'Montserrat' }}>
                <div style={{ fontSize: 28, marginBottom: 12 }}>🛡️</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.cream, marginBottom: 8 }}>No safe dishes found</div>
                <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                  We found {scanResults.allergen_blocked_count} dish{scanResults.allergen_blocked_count !== 1 ? 'es' : ''} but none are safe given your allergen profile. Ask staff about modifications.
                </div>
              </div>
            ) : null}
            {(scanResults.recommendations || []).map((dish, i) => (
              <div key={i} style={{
                background: "rgba(255,255,255,0.05)", borderRadius: RAD.md,
                border: `1px solid ${T.gold}20`, padding: SP.md, marginBottom: SP.sm,
                borderLeft: `3px solid ${dish.fit_percentage >= 85 ? T.gold : dish.fit_percentage >= 65 ? `${T.gold}60` : `${T.gold}25`}`,
              }}>
                {dish.staff_confirm_required && dish.allergen_warnings?.length > 0 && (
                  <div style={{
                    marginBottom: 6, padding: "5px 8px", borderRadius: RAD.sm,
                    background: `${T.safetyRed}40`, border: `1px solid ${T.safetyRed}90`,
                    borderLeft: `3px solid ${T.safetyRed}`,
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    <span style={{ fontSize: 13, flexShrink: 0 }}>⚠️</span>
                    <span style={{ ...TYPE.caption, color: T.safetyRed, fontWeight: 700 }}>
                      Confirm with staff: {dish.allergen_warnings.filter(a => ["nuts", "shellfish", "sesame"].includes(a)).join(", ") || dish.allergen_warnings.join(", ")}
                    </span>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <span style={{ ...TYPE.bodySmall, fontWeight: 700, color: T.cream }}>{dish.dish_name}</span>
                  <MatchBadge score={dish.fit_percentage} />
                </div>
                {dish.description && <p style={{ ...TYPE.caption, color: T.darkTextSec, marginBottom: 4 }}>{dish.description}</p>}
                {dish.price && <span style={{ ...TYPE.caption, color: T.gold }}>{dish.price}</span>}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                  {(dish.reasons || []).slice(0, 3).map((r, j) => (
                    <span key={j} style={{ ...TYPE.caption, fontSize: 9, padding: "2px 8px", borderRadius: RAD.pill, background: `${T.gold}15`, color: T.gold }}>{r}</span>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  {['up', 'down'].map(r => (
                    <button key={r}
                      onClick={() => submitDishFeedback(dish.id, dish.dish_name, r)}
                      style={{
                        background: dishFeedback[dish.id] === r ? T.gold : 'rgba(255,255,255,0.08)',
                        border: 'none', borderRadius: 20, padding: '4px 12px',
                        color: dishFeedback[dish.id] === r ? T.darkBg : T.darkTextSec,
                        cursor: 'pointer', fontSize: 13, fontFamily: 'Montserrat',
                      }}
                    >{r === 'up' ? '👍' : '👎'}</button>
                  ))}
                </div>
              </div>
            ))}
            <GoldBtn onClick={onBack} style={{ marginTop: SP.md }}>Done</GoldBtn>
          </div>
        ) : scanning ? (
          <div className="anim-fadein" style={{ textAlign: "center", zIndex: 1 }}>
            <div style={{
              width: 200, height: 260, borderRadius: RAD.lg,
              background: `linear-gradient(180deg, ${T.gold}10 0%, ${T.gold}25 50%, ${T.gold}10 100%)`,
              backgroundSize: "100% 200%",
              animation: "shimmer 1.5s linear infinite",
              marginBottom: SP.lg,
            }} />
            <div style={{ ...TYPE.body, color: T.cream, fontWeight: 600, marginBottom: SP.md }}>{progress}</div>
            <button onClick={handleCancelScan} style={{
              background: "transparent", border: `1px solid ${T.darkTextSec}40`, borderRadius: RAD.pill,
              color: T.darkTextSec, padding: "6px 20px", cursor: "pointer",
              fontFamily: "Montserrat", ...TYPE.bodySmall, fontWeight: 600,
            }}>Cancel</button>
          </div>
        ) : hasPages ? (
          /* Thumbnail grid of captured pages */
          <div style={{ padding: SP.lg, width: "100%", maxWidth: 400, zIndex: 1 }}>
            <h3 style={{ ...TYPE.body, color: T.cream, fontWeight: 700, textAlign: "center", marginBottom: SP.md }}>
              {capturedFiles.length} page{capturedFiles.length !== 1 ? "s" : ""} ready
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: SP.sm }}>
              {capturedFiles.map((f, i) => (
                <div key={i} style={{ position: "relative", aspectRatio: "3/4", borderRadius: RAD.md, overflow: "hidden", border: `1px solid ${T.gold}40` }}>
                  {f.file.type === "application/pdf" ? (
                    <div style={{ width: "100%", height: "100%", background: `${T.gold}10`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ fontSize: 28 }}>📄</span>
                      <span style={{ ...TYPE.caption, color: T.darkTextSec, marginTop: 4, fontSize: 9, padding: "0 4px", textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>{f.file.name}</span>
                    </div>
                  ) : (
                    <img src={f.preview} alt={`Page ${i + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  )}
                  <button onClick={() => removeFile(i)} aria-label={`Remove page ${i + 1}`} style={{
                    position: "absolute", top: 4, right: 4, width: 22, height: 22, borderRadius: "50%",
                    background: "rgba(0,0,0,0.7)", border: "none", color: T.cream, fontSize: 12,
                    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                    fontFamily: "Montserrat", fontWeight: 700, lineHeight: 1,
                  }}>✕</button>
                  <div style={{
                    position: "absolute", bottom: 4, left: 4, ...TYPE.caption, fontSize: 9,
                    background: "rgba(0,0,0,0.6)", color: T.cream, borderRadius: RAD.pill, padding: "2px 6px",
                  }}>{i + 1}</div>
                </div>
              ))}
              {/* Add more button (if not full) */}
              {!pagesFull && (
                <button type="button" onClick={() => { if (cameraInputRef.current) cameraInputRef.current.click(); }} aria-label="Add another page" style={{
                  aspectRatio: "3/4", borderRadius: RAD.md, border: `2px dashed ${T.gold}40`,
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", background: `${T.gold}05`, fontFamily: "Montserrat",
                }}>
                  <span aria-hidden="true" style={{ fontSize: 24, color: T.gold, marginBottom: 4 }}>+</span>
                  <span style={{ ...TYPE.caption, color: T.darkTextSec, fontSize: 9 }}>Add page</span>
                </button>
              )}
            </div>
            {scanError && (
              <div style={{ textAlign: "center", marginTop: SP.sm }}>
                <p style={{ ...TYPE.caption, color: T.safetyRed, marginBottom: SP.sm }}>{scanError}</p>
                <button onClick={handleSubmitScan} style={{
                  background: "transparent", border: `1px solid ${T.gold}60`, borderRadius: RAD.pill,
                  color: T.gold, padding: "6px 18px", cursor: "pointer",
                  fontFamily: "Montserrat", ...TYPE.bodySmall, fontWeight: 600,
                }}>Try again</button>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Viewfinder guide */}
            <div style={{
              width: 280, height: 360, border: `2px solid rgba(255,255,255,0.3)`,
              borderRadius: RAD.lg, position: "relative",
            }}>
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <p style={{ ...TYPE.bodySmall, color: T.darkTextSec, textAlign: "center", maxWidth: 200, lineHeight: "20px" }}>
                  {scanError ? (
                    <span style={{ color: T.safetyRed }}>{scanError}</span>
                  ) : scansRemaining != null && scansRemaining <= 0 ? (
                    "No scans remaining. Contact support for more."
                  ) : (
                    "Take a photo or upload an image/PDF (up to 5 pages). We'll match dishes to your Food ID. Scan outputs are assistive only."
                  )}
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Bottom controls */}
      {!scanResults && (
        <div style={{ padding: `${SP.lg}px ${SP.lg}px 40px` }}>
          {/* Scan button — visible when pages captured */}
          {hasPages && !scanning && (
            <div style={{ display: "flex", justifyContent: "center", marginBottom: SP.md }}>
              <GoldBtn onClick={handleSubmitScan} style={{ minWidth: 200 }}>
                Scan {capturedFiles.length} page{capturedFiles.length !== 1 ? "s" : ""}
              </GoldBtn>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: SP.xl }}>
            {/* Gallery button */}
            <button onClick={() => { if (galleryInputRef.current) galleryInputRef.current.click(); }} disabled={scanning || pagesFull || (scansRemaining != null && scansRemaining <= 0)} style={{
              width: 44, height: 44, borderRadius: RAD.sm, background: T.darkCard,
              border: `1px solid ${T.darkBorder}`, display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 18, cursor: "pointer", opacity: (pagesFull || (scansRemaining != null && scansRemaining <= 0)) ? 0.4 : 1,
            }}>
              🖼️
            </button>
            {/* Capture button */}
            <button onClick={() => { if (cameraInputRef.current) cameraInputRef.current.click(); }} disabled={scanning || pagesFull || (scansRemaining != null && scansRemaining <= 0)} style={{
              width: 68, height: 68, borderRadius: "50%",
              background: "transparent", border: `4px solid ${T.gold}`,
              cursor: scanning ? "default" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              opacity: (pagesFull || (scansRemaining != null && scansRemaining <= 0)) ? 0.4 : 1,
            }}>
              <div style={{ width: 52, height: 52, borderRadius: "50%", background: scanning ? T.goldDark : T.gold, transition: "background 0.2s" }} />
            </button>
            {/* Credits */}
            <div style={{ ...TYPE.caption, color: T.darkTextSec, textAlign: "center", width: 44 }}>
              {scansRemaining != null ? `${scansRemaining} scan${scansRemaining !== 1 ? "s" : ""} left` : "..."}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// TOGGLE (for share screen custom mode)
// ─────────────────────────────────────────────────────────────
const Toggle = ({ checked, onChange }) => (
  <div onClick={onChange} style={{
    width: 40, height: 22, borderRadius: 11, cursor: "pointer",
    background: checked ? T.gold : `${T.cream}20`,
    position: "relative", transition: "background 0.2s",
  }}>
    <div style={{
      width: 18, height: 18, borderRadius: "50%", background: T.white,
      position: "absolute", top: 2,
      left: checked ? 20 : 2,
      transition: "left 0.2s ease",
      boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
    }} />
  </div>
);

// ─────────────────────────────────────────────────────────────
// SCREEN: EMAIL CONFIRMATION — shown after registration when Supabase requires email verification
// ─────────────────────────────────────────────────────────────
const EmailConfirmScreen = ({ email, onSignIn }) => (
  <div style={{
    minHeight: "100vh", background: T.darkBg, display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center", padding: SP.xl,
    fontFamily: "'Montserrat', sans-serif", textAlign: "center", position: "relative",
  }}>
    <DarkBgLayers />
    <div style={{ position: "relative", zIndex: 2 }}>
      <div style={{ fontSize: 56, marginBottom: SP.lg }}>✉️</div>
      <h2 style={{ fontSize: 24, fontWeight: 700, color: T.cream, marginBottom: SP.sm }}>
        Check your email
      </h2>
      <p style={{ fontSize: 15, color: T.darkTextSec, lineHeight: "22px", marginBottom: SP.xxl, maxWidth: 320 }}>
        We sent a confirmation link to <strong style={{ color: T.cream }}>{email}</strong>. Tap it to activate your account.
      </p>
      <button onClick={onSignIn} style={{
        padding: "14px 32px", borderRadius: RAD.pill, border: `1px solid ${T.gold}40`,
        background: "rgba(255,255,255,0.06)", cursor: "pointer",
        fontFamily: "Montserrat", fontSize: 15, fontWeight: 600, color: T.cream,
        letterSpacing: "0.75px",
      }}>
        Back to Sign In
      </button>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// ROOT APP — spec 2.1 navigation architecture
// ─────────────────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen]     = useState("prelogin");
  const [profile, setProfile]   = useState(null);
  const [activeTab, setActiveTab] = useState("home");
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [prevScreen, setPrevScreen] = useState(null);
  const [sharedToken, setSharedToken] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [isOffline, setIsOffline] = useState(typeof navigator !== "undefined" && !navigator.onLine);
  const [showBackOnline, setShowBackOnline] = useState(false);
  const [appToast, setAppToast] = useState(null);

  const showAppToast = useCallback((msg) => {
    setAppToast(msg);
    setTimeout(() => setAppToast(null), 4000);
  }, []);

  // ── Offline detection ──
  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => {
      setIsOffline(false);
      setShowBackOnline(true);
      setTimeout(() => setShowBackOnline(false), 3000);
    };
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => { window.removeEventListener("offline", goOffline); window.removeEventListener("online", goOnline); };
  }, []);

  // ── Auth-aware fetch — detects 401/403 and triggers re-auth modal ──
  const handleSessionExpiry = useCallback(() => {
    localStorage.removeItem("sb_token");
    setSessionExpired(true);
  }, []);

  const handleReSignIn = useCallback(async () => {
    try { await signOut(); } catch (_) { /* already signed out */ }
    localStorage.removeItem("sb_token");
    setSessionExpired(false);
    setProfile(null);
    nav("login");
  }, []); // eslint-disable-line

  // ── Check for /food-id/shared/{token} URL on mount ──
  useEffect(() => {
    const m = window.location.pathname.match(/^\/food-id\/shared\/([a-f0-9-]+)$/i);
    if (m) {
      setSharedToken(m[1]);
      setScreen("shared-view");
      // Clean URL without reload
      window.history.replaceState(null, "", "/");
    }
  }, []);

  const nav = (to) => {
    setPrevScreen(screen);
    setScreen(to);
  };

  // ── GA4 page_view tracking for internal navigation ──────────────
  // index.html gtag('config') already fires the first page_view on load,
  // so we skip the initial render and only fire on subsequent screen changes.
  const _gaFirstRender = useRef(true);
  useEffect(() => {
    if (_gaFirstRender.current) { _gaFirstRender.current = false; return; }
    if (typeof window.gtag !== "function") return;
    const PAGE_TITLES = {
      "prelogin":      "Welcome",
      "why-consoome":  "Why Consoome",
      "login":         "Sign In",
      "register":      "Create Account",
      "email-confirm": "Confirm Email",
      "onboarding":    "Create Food ID",
      "main":          "Explore",
      "hotel-detail":  "Hotel",
      "venue-detail":  "Venue",
      "profile":       "My Food ID",
      "share-main":    "Share Profile",
      "scan":          "Menu Scanner",
      "more":          "More",
      "privacy":       "Privacy Policy",
      "terms":         "Terms of Service",
      "shared-view":   "Shared Food ID",
    };
    window.gtag("event", "page_view", {
      page_title:    PAGE_TITLES[screen] || screen,
      page_location: window.location.href,
      page_path:     "/" + screen,
    });
  }, [screen]);

  // ── Load Food ID profile from API after login ──────────────────
  const loadProfile = async (token) => {
    try {
      const res = await fetch(`${API_BASE}/food-id/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401 || res.status === 403) { handleSessionExpiry(); return false; }
      if (res.ok) {
        const data = await res.json();
        // Server returns "completeness" but frontend uses "completeness_score" everywhere
        if (data.completeness != null) data.completeness_score = data.completeness;
        const merged = { ...data, _foodId: data.food_id || data.id };
        setProfile(p => ({ ...p, ...data, _foodId: data.food_id || data.id }));
        cacheSafetyCard(merged);
        return true; // has food_id
      }
      return false; // no food_id yet
    } catch (e) {
      return false;
    }
  };

  // ── Handle successful login (email or OAuth callback) ──────────
  const handleLoginSuccess = async ({ token, user }) => {
    if (!token) return;
    sessionHandledRef.current = true; // prevent onAuthStateChange from double-navigating
    localStorage.setItem("sb_token", token);
    // Always extract display_name from the user object BEFORE loading profile
    const derivedName = user?.user_metadata?.full_name || user?.email?.split("@")[0] || "";
    if (derivedName) setProfile(p => ({ ...p, display_name: derivedName }));
    const hasProfile = await loadProfile(token);
    if (hasProfile) {
      nav("main"); setActiveTab("home");
    } else {
      nav("onboarding");
    }
  };

  // ── Handle successful registration ────────────────────────────
  const [confirmEmail, setConfirmEmail] = useState(null);
  const handleRegisterSuccess = async ({ token, user, name, email }) => {
    if (!token) {
      // Supabase email confirmation required — token not yet issued
      setConfirmEmail(email || user?.email || "your email");
      nav("email-confirm");
      return;
    }
    localStorage.setItem("sb_token", token);
    setProfile(p => ({ ...p, display_name: name || user?.email?.split("@")[0] }));
    nav("onboarding");
  };

  // Track whether session restore has already handled navigation
  const sessionHandledRef = useRef(false);

  // ── Restore session on app load + listen for OAuth redirects ──
  useEffect(() => {
    // Restore existing session
    const restoreSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session?.access_token) {
        const token = data.session.access_token;
        localStorage.setItem("sb_token", token);
        // Extract name from session user before loading profile
        const sessionUser = data.session.user;
        const derivedName = sessionUser?.user_metadata?.full_name || sessionUser?.email?.split("@")[0] || "";
        if (derivedName) setProfile(p => ({ ...p, display_name: derivedName }));
        const hasProfile = await loadProfile(token);
        sessionHandledRef.current = true;
        if (hasProfile) { nav("main"); setActiveTab("home"); }
        else { nav("onboarding"); }
      }
    };
    restoreSession();

    // Listen for OAuth redirects and token refreshes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if ((event === "SIGNED_IN" || event === "TOKEN_REFRESHED") && session?.access_token) {
        const token = session.access_token;
        localStorage.setItem("sb_token", token);
        // Only navigate on SIGNED_IN if restoreSession hasn't already handled it.
        // TOKEN_REFRESHED should just update the token, not re-navigate.
        if (event === "TOKEN_REFRESHED") return;
        // Skip if restoreSession already navigated (avoids double-nav race)
        if (sessionHandledRef.current) return;
        sessionHandledRef.current = true;
        const hasProfile = await loadProfile(token);
        if (hasProfile) { nav("main"); setActiveTab("home"); }
        else { nav("onboarding"); }
      }
      if (event === "SIGNED_OUT") {
        sessionHandledRef.current = false;
        localStorage.removeItem("sb_token");
        setProfile(null);
        nav("prelogin");
      }
    });
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line

  const goBack = () => {
    if (prevScreen) setScreen(prevScreen);
    else setScreen("prelogin");
  };

  // Tab bar navigation handler
  const handleTabNav = (tab) => {
    setActiveTab(tab);
    if (tab === "home") nav("main");
    if (tab === "scan") nav("scan");
    if (tab === "profile") nav("profile");
    if (tab === "share") nav("share-main");
    if (tab === "more") nav("more");
  };

  // ── Feedback handlers ──
  const handleDishFeedback = async (dish, feedbackType, venue) => {
    const token = localStorage.getItem("sb_token");
    if (!token || !profile._foodId) return;
    try {
      const res = await fetch(`${API_BASE}/food-id/${profile._foodId}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          item_id: dish.dish_id || dish.id || "00000000-0000-0000-0000-000000000000",
          item_type: "dish",
          feedback_type: feedbackType,
          venue_id: venue?.venue_id || venue?.id || null,
        }),
      });
      if (res.status === 401 || res.status === 403) { handleSessionExpiry(); return; }
      const data = await res.json().catch(() => ({}));
      if (data.toast_message) showAppToast(data.toast_message);
    } catch (e) {
      console.warn("Feedback send failed:", e);
    }
  };

  const handleVenueExperience = async (data) => {
    const token = localStorage.getItem("sb_token");
    if (!token || !profile._foodId) return;
    try {
      const res = await fetch(`${API_BASE}/food-id/${profile._foodId}/venue-experience`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(data),
      });
      if (res.status === 401 || res.status === 403) handleSessionExpiry();
    } catch (e) {
      console.warn("Venue experience send failed:", e);
    }
  };

  const showTabBar = ["main", "scan", "profile", "share-main", "more"].includes(screen);

  return (
    <ErrorBoundary>
    <div className="consoome-root" style={{
      width: "100%", minHeight: "100vh",
      fontFamily: "'Montserrat', sans-serif",
      background: T.darkBg,
      position: "relative", display: "flex", flexDirection: "column",
      transition: "background 0.8s ease",
    }}>
      {/* Offline / back-online banner */}
      {(isOffline || showBackOnline) && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 10000,
          height: 32, display: "flex", alignItems: "center", justifyContent: "center",
          background: isOffline ? T.safetyRed : T.safetyGreen,
          fontFamily: "Montserrat", fontSize: 13, fontWeight: 400, color: "#fff",
          animation: isOffline ? "offlinePulse 2s infinite" : "none",
          transition: "background 0.3s ease",
        }}>
          {isOffline ? "You're offline — Restrictions Card still available" : "Back online"}
        </div>
      )}
      <style>{`@keyframes offlinePulse { 0%,100% { opacity: 1; } 50% { opacity: 0.85; } }`}</style>

      {/* Session expired modal */}
      {sessionExpired && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center",
          backdropFilter: "blur(6px)",
        }}>
          <div style={{
            background: T.darkCard, borderRadius: RAD.xl, padding: `${SP.xl}px`,
            maxWidth: 340, width: "90%", textAlign: "center",
            border: `1px solid ${T.gold}25`, boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
          }}>
            <div style={{ fontSize: 40, marginBottom: SP.md }}>🔒</div>
            <h2 style={{ fontFamily: "Montserrat", fontSize: 20, fontWeight: 600, color: T.cream, marginBottom: SP.sm }}>
              Session expired
            </h2>
            <p style={{ fontFamily: "Montserrat", fontSize: 14, color: T.darkTextSec, lineHeight: "20px", marginBottom: SP.xl }}>
              Your session has expired. Please sign in again to continue.
            </p>
            <button onClick={handleReSignIn} style={{
              width: "100%", padding: "14px 24px", borderRadius: RAD.pill, border: "none",
              cursor: "pointer", background: T.gold, color: T.ink,
              fontFamily: "Montserrat", fontSize: 15, fontWeight: 600, letterSpacing: "0.75px",
            }}>
              Sign In
            </button>
          </div>
        </div>
      )}
      <a href="#main-content" className="skip-link">Skip to main content</a>
      {/* Persistent small logo — top-left on app screens (not on motion, login, register, why-consoome, or more — they have their own) */}
      {["main", "profile", "share-main", "venue-detail", "hotel-detail", "scan", "privacy", "terms", "onboarding"].includes(screen) && (
        <header role="banner" style={{
          position: "absolute", top: 16, left: 20, zIndex: 200,
          pointerEvents: "none",
        }}>
          <img src={logoCream} alt="Consoome logo" style={{ height: 18, objectFit: "contain", opacity: 0.85 }} />
        </header>
      )}

      <main id="main-content" role="main" style={{ flex: 1, overflowY: "auto" }}>
      <div aria-live="polite" aria-atomic="true" className="sr-only" id="screen-announcer">{screen === "main" ? "Home screen" : screen === "profile" ? "Profile screen" : screen === "scan" ? "Menu scanner" : screen === "onboarding" ? "Onboarding" : screen === "share-main" ? "Share screen" : ""}</div>
        {/* Pre-login motion — IS the entry screen; 3 choice boxes appear at end of animation */}
        {screen === "prelogin" && (
          <ConsoomePreOnboardingMotion
            onStart={() => nav("register")}
            onSkip={() => nav("register")}
            onSignIn={() => nav("login")}
            onWhyConsoome={() => nav("why-consoome")}
            onFinished={null}
          />
        )}

        {/* Why Consoome info page */}
        {screen === "why-consoome" && (
          <WhyConsoomeScreen onBack={() => nav("prelogin")} onPrivacy={() => nav("privacy")} onTerms={() => nav("terms")} />
        )}

        {/* Login — "Welcome back" → existing users skip onboarding → jump to overview */}
        {screen === "login" && (
          <LoginScreen
            onSignIn={handleLoginSuccess}
            onRegister={() => nav("register")}
            onPrivacy={() => nav("privacy")}
            onTerms={() => nav("terms")}
          />
        )}

        {/* Register — create account, then onboarding */}
        {screen === "register" && (
          <RegisterScreen
            onRegister={handleRegisterSuccess}
            onSignIn={() => nav("login")}
            onPrivacy={() => nav("privacy")}
            onTerms={() => nav("terms")}
          />
        )}

        {/* Email confirmation — shown when Supabase requires email verification */}
        {screen === "email-confirm" && (
          <EmailConfirmScreen email={confirmEmail} onSignIn={() => nav("login")} />
        )}

        {/* Onboarding — only for NEW users after registration */}
        {screen === "onboarding" && (
          <OnboardingScreen
            displayName={profile.display_name}
            onComplete={(p) => { setProfile(p); cacheSafetyCard(p); nav("main"); setActiveTab("home"); }}
          />
        )}

        {/* Hello [Name] overview — main app screen */}
        {screen === "main" && (
          <HomeScreen
            profile={profile}
            onVenue={(v) => { setSelectedVenue(v); nav("venue-detail"); }}
            onHotel={(h) => { setSelectedHotel(h); nav("hotel-detail"); }}
            onScan={() => { nav("scan"); setActiveTab("scan"); }}
            onShare={() => { nav("share-main"); setActiveTab("share"); }}
            onProfile={() => { nav("profile"); setActiveTab("profile"); }}
          />
        )}

        {/* Hotel detail — outlet listing */}
        {screen === "hotel-detail" && selectedHotel && (
          <HotelDetailScreen
            hotel={selectedHotel}
            profile={profile}
            onBack={() => nav("main")}
            onOutlet={(v) => { setSelectedVenue(v); nav("venue-detail"); }}
          />
        )}

        {screen === "venue-detail" && selectedVenue && (
          <VenueDetailScreen
            venue={selectedVenue}
            profile={profile}
            onBack={() => selectedHotel ? nav("hotel-detail") : nav("main")}
            onDishFeedback={handleDishFeedback}
            onVenueExperience={handleVenueExperience}
          />
        )}

        {screen === "profile" && (
          <ProfileScreen
            profile={profile}
            setProfile={setProfile}
            onShare={() => { nav("share-main"); setActiveTab("share"); }}
            isOffline={isOffline}
          />
        )}

        {screen === "share-main" && (
          <ShareScreen
            profile={profile}
            onBack={() => { nav("main"); setActiveTab("home"); }}
          />
        )}

        {screen === "scan" && (
          <ScanScreen
            profile={profile}
            onBack={() => { nav("main"); setActiveTab("home"); }}
            onResult={(scanResult) => {
              if (scanResult) {
                setSelectedVenue(scanResult);
                setSelectedHotel(null);
                nav("venue-detail");
              } else {
                nav("main"); setActiveTab("home");
              }
            }}
          />
        )}

        {/* More menu */}
        {screen === "more" && (
          <MoreScreen
            profile={profile}
            onPrivacy={() => nav("privacy")}
            onTerms={() => nav("terms")}
            onBack={() => { nav("main"); setActiveTab("home"); }}
            onLogout={async () => {
              await signOut();
              localStorage.removeItem("sb_token");
              setProfile(null);
              nav("login");
            }}
            onResetFoodId={async () => {
              const ok = window.confirm("Reset your Food ID?\n\nThis permanently erases your taste profile, preferences, saved dishes and learning data. You'll be taken back to onboarding to start fresh.\n\nThis cannot be undone.");
              if (!ok) return;
              try {
                const token = localStorage.getItem("sb_token");
                const res = await fetch(`${API_BASE}/food-id/me`, {
                  method: "DELETE",
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) {
                  const err = await res.text();
                  alert("Could not reset Food ID: " + err);
                  return;
                }
                ["consoome_answered_tiles", "consoome_recently_shown", "consoome_safety_card", "consoome_activity", "consoome_nudge_dismissed"].forEach(k => localStorage.removeItem(k));
                setProfile(null);
                nav("onboarding");
              } catch (e) {
                alert("Could not reset Food ID: " + (e?.message || e));
              }
            }}
          />
        )}

        {/* Legal screens */}
        {screen === "privacy" && (
          <PrivacyPolicyScreen onBack={() => nav(prevScreen || "more")} />
        )}
        {screen === "terms" && (
          <TermsOfUseScreen onBack={() => nav(prevScreen || "more")} />
        )}

        {/* Public shared profile view (no auth required) */}
        {screen === "shared-view" && sharedToken && (
          <SharedProfileScreen token={sharedToken} onClose={() => nav("prelogin")} />
        )}
      </main>

      {/* Bottom Tab Bar — spec 2.1 */}
      {showTabBar && (
        <TabBar active={activeTab} onNavigate={handleTabNav} completeness={profile?.completeness_score || 0} />
      )}

      {/* Food ID evolution toast */}
      {appToast && (
        <div style={{
          position: 'fixed', bottom: 90, left: '50%', transform: 'translateX(-50%)',
          background: T.gold, color: T.darkBg,
          padding: '12px 20px', borderRadius: 24,
          fontFamily: 'Montserrat', fontWeight: 700, fontSize: 13,
          zIndex: 9999, maxWidth: 320, textAlign: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          animation: 'toastFadeInUp 0.3s ease',
        }}>
          ✦ {appToast}
        </div>
      )}

      <style>{`
        @keyframes toastFadeInUp {
          from { opacity: 0; transform: translateX(-50%) translateY(10px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
    </ErrorBoundary>
  );
}
